import { useState, useEffect, useCallback, useRef } from "react";

// ─── MOCK DATA：每章各 2 題示範 ────────────────────────────────────────────────
const CHAPTERS = ["財務報表分析", "貨幣銀行學", "會計學", "經濟學"];

const ALL_QUESTIONS = {
  財務報表分析: [
    {
    id: "fin-01",
    q: "財務報表分析的主要功能有：",
    opts: [
      "(A) 評估企業過去的經營績效",
      "(B) 衡量企業目前的財務狀況",
      "(C) 預測企業未來之發展趨勢",
      "(D) 以上皆是",
    ],
    ans: 3,
    hint: "🔑 財務分析三大時間維度：過去、現在、未來",
    isCalc: false,
    explain:
      "財務報表分析同時涵蓋：\n(A) 評估過去績效（歷史分析）\n(B) 衡量現況財務狀況（靜態分析）\n(C) 預測未來發展趨勢（趨勢分析）\n三者皆為財務分析的核心功能，故答案為「以上皆是」。",
  },
    {
    id: "fin-02",
    q: "有關「財務報表分析」下列何者敘述錯誤：",
    opts: [
      "(A) 可作為管理當局決策分析的參考",
      "(B) 用來評估企業的獲利能力及償債能力",
      "(C) 能夠評估過去情況，但不可預測未來發展",
      "(D) 比率分析是財務分析的方法之一",
    ],
    ans: 2,
    hint: "🔑 財務分析除回顧過去，也可用於預測未來趨勢",
    isCalc: false,
    explain:
      "(C) 的說法錯誤。財務報表分析不僅能評估過去，也能作為預測未來發展趨勢的工具（例如趨勢分析、比較分析）。財務報表分析的功能橫跨過去、現在與未來三個面向。",
  },
    {
    id: "fin-03",
    q: "下列何者非為動態分析？",
    opts: [
      "(A) 流動比率分析",
      "(B) 趨勢分析",
      "(C) 現金流量分析",
      "(D) 水平分析",
    ],
    ans: 0,
    hint: "🔑 靜態分析：某一時點；動態分析：跨期間比較",
    isCalc: false,
    explain:
      "流動比率分析是靜態分析（Static Analysis），只觀察某一特定時點的數據（期末流動資產÷期末流動負債），不涉及跨期比較。\n\n動態分析（Dynamic Analysis）涉及不同時間點的比較：\n• 趨勢分析（B）：觀察多期數據變化趨勢\n• 現金流量分析（C）：反映某期間的現金流動\n• 水平分析（D）：比較兩個以上期間的增減",
  },
    {
    id: "fin-04",
    q: "趨勢分析所採用基期的方式，下列何者為非？",
    opts: [
      "(A) 固定以某一期為基期",
      "(B) 以前一期當後一期的基期",
      "(C) 以前各期的平均數",
      "(D) 未來各期的預期數",
    ],
    ans: 3,
    hint: "🔑 基期必須是已知的歷史數據，不可用未來預期",
    isCalc: false,
    explain:
      "趨勢分析（Trend Analysis）使用的基期必須是已知的歷史數據：\n• 固定基期法（A）：以某一特定年度固定為基期，各期與之相比\n• 移動基期法（B）：以前一期作為後一期的基期\n• 平均基期法（C）：以前各期平均值為基期\n\n(D) 用「未來各期的預期數」作基期，在邏輯上不成立，因為分析時未來數據尚不存在，故為錯誤方式。",
  },
    {
    id: "fin-05",
    q: "下列何者是財務報表分析者應有那些學養？",
    opts: [
      "(A) 基本經濟素養，瞭解各產業景氣循環變動",
      "(B) 熟悉各項財務會計處理流程",
      "(C) 熟知各項分析工具",
      "(D) 以上皆是",
    ],
    ans: 3,
    hint: "🔑 財務分析需跨領域知識：經濟、會計、分析工具三位一體",
    isCalc: false,
    explain:
      "優秀的財務分析師需要同時具備：\n(A) 經濟素養：了解總體經濟環境與產業景氣，才能將數字放在正確脈絡中解讀\n(B) 會計知識：熟悉財務報表的編製原則與各科目意義\n(C) 分析工具：掌握比率分析、趨勢分析、共同比分析等方法\n\n三者缺一不可，故答案為「以上皆是」。",
  },
    {
    id: "fin-06",
    q: "共同比資產負債表係以下列何者為總數？",
    opts: [
      "(A) 資產總額",
      "(B) 負債總額",
      "(C) 股東權益",
      "(D) 收益",
    ],
    ans: 0,
    hint: "🔑 共同比資產負債表：左右兩方均以「資產總額」為 100%",
    isCalc: false,
    explain:
      "共同比資產負債表（Common-size Balance Sheet）以「資產總額」為 100% 基準，計算各項目（資產、負債、股東權益各科目）占資產總額的百分比，用以分析企業的資本結構與財務狀況。\n\n共同比損益表則以「銷貨淨額」為 100% 基準，兩者的基準不同，請注意區分。",
  },
    {
    id: "fin-07",
    q: "會計資訊應具備兩項最主要的品質特性為？",
    opts: [
      "(A) 可靠性與一致性",
      "(B) 攸關性與可靠性",
      "(C) 一致性與比較性",
      "(D) 重要性與一致性",
    ],
    ans: 1,
    hint: "🔑 IASB 概念架構：兩大基本質性特徵 = 攸關性（Relevance）+ 忠實表達（Reliability）",
    isCalc: false,
    explain:
      "根據財務報告概念架構（Conceptual Framework for Financial Reporting），會計資訊的兩大基本質性特徵（Fundamental Qualitative Characteristics）為：\n\n1. 攸關性（Relevance）：資訊能影響使用者的決策\n2. 可靠性/忠實表達（Reliability / Faithful Representation）：資訊真實反映實際情況，且可驗證\n\n其他如一致性、比較性、及時性等屬於輔助性的品質特徵（Enhancing Qualitative Characteristics）。",
  },
    {
    id: "fin-08",
    q: "下列何者對共同比財務報表分析的敘述錯誤？",
    opts: [
      "(A) 有助於瞭解公司的資本結構",
      "(B) 適用於不同規模公司的比較",
      "(C) 共同比綜合損益表是以銷貨淨額為總數",
      "(D) 共同比資產負債表是以股東權益總額為總數",
    ],
    ans: 3,
    hint: "🔑 共同比資產負債表的基準是「資產總額」，非股東權益",
    isCalc: false,
    explain:
      "(D) 說法錯誤。共同比資產負債表以「資產總額」為 100%，而非股東權益總額。\n\n正確的共同比基準：\n• 共同比資產負債表 → 資產總額 = 100%\n• 共同比損益表 → 銷貨淨額 = 100%（C 正確）\n\n共同比分析的優點（A、B 均正確）：\n• 可消除企業規模差異，使不同大小的公司得以比較\n• 可清楚呈現企業的資本結構（負債比率、自有資本比率等）",
  },
    {
    id: "fin-09",
    q: "一比率欲於財務分析時發揮用途，則：",
    opts: [
      "(A) 此比率必須大於 1 年",
      "(B) 此比率必可與某些基年之比率比較",
      "(C) 用此計算比率之二數額皆必須以金額表示",
      "(D) 用此計算比率之二數額必須具備邏輯上之關係",
    ],
    ans: 3,
    hint: "🔑 比率分析前提：兩數字之間需有邏輯上的因果或關聯性",
    isCalc: false,
    explain:
      "比率分析最重要的前提是：組成比率的兩個數字必須具備「邏輯上的關係」。\n\n例如：\n• 流動比率 = 流動資產 ÷ 流動負債（兩者有邏輯關聯：均屬短期財務項目）\n• 純益率 = 淨利 ÷ 銷貨淨額（兩者有因果關係）\n\n若兩個數字沒有邏輯關係（例如：固定資產 ÷ 銷售人員薪資），計算出的比率便無意義，無法用於分析。",
  },
    {
    id: "fin-10",
    q:
      "「相同企業不同期間或不同企業相同期間的類似資訊能夠互相比較對資訊使用者才有意義」，係指會計資訊的品質特性之：",
    opts: [
      "(A) 完整性",
      "(B) 攸關性",
      "(C) 可行性",
      "(D) 比較性",
    ],
    ans: 3,
    hint: "🔑 跨期或跨企業能互比 → 比較性（Comparability）",
    isCalc: false,
    explain:
      "比較性（Comparability）：資訊使用者能比較不同時期或不同企業的財務資訊，才能作出有意義的判斷。\n\n兩個維度：\n• 縱向比較：同一企業的不同期間（前後比較）\n• 橫向比較：不同企業的相同期間（同業比較）\n\n一致性（Consistency）是比較性的前提，即企業前後期採用相同的會計方法，才能使縱向比較有效。",
  },
    {
    id: "fin-11",
    q: "財務報表分析中，對同一項目作不同時期增減的分析比較，稱為：",
    opts: [
      "(A) 橫向分析",
      "(B) 縱向分析",
      "(C) 整體分析",
      "(D) 個體分析",
    ],
    ans: 0,
    hint: "🔑 橫向分析（水平分析）：沿時間軸橫跨多期比較同一項目",
    isCalc: false,
    explain:
      "分析方向的概念：\n\n• 橫向分析（Horizontal Analysis）= 水平分析：比較同一報表項目在不同期間的增減變化，重點在「時間」維度，掌握趨勢。\n\n• 縱向分析（Vertical Analysis）= 垂直分析：比較同一期間內不同科目的比例關係（結構比），即共同比分析。\n\n本題答案為 (A) 橫向分析。",
  },
    {
    id: "fin-12",
    q:
      "比率分析是就財務報表中具有意義的兩個相關項目計算比率，並將該比率與下列何者相比較，以評估企業財務狀況及營業結果優劣？",
    opts: [
      "(A) 過去的比率",
      "(B) 事先設定的標準",
      "(C) 同業的比率",
      "(D) 以上皆是",
    ],
    ans: 3,
    hint: "🔑 比率分析三大比較基準：歷史、目標、同業",
    isCalc: false,
    explain:
      "比率分析必須與「基準」比較才有意義，三大常用基準：\n\n1. 過去的比率（A）：與企業自身歷史數據比較，觀察趨勢\n2. 事先設定的標準（B）：與企業自訂的目標或預算比較\n3. 同業的比率（C）：與同類企業的平均水準或標竿比較\n\n三種基準各有用途，實際分析時往往三者並用，故答案為「以上皆是」。",
  },
    {
    id: "fin-13",
    q: "下列哪一項目和財務報表表達內容的「一致性」有關？",
    opts: [
      "(A) 費用在發生的當期列入損益表",
      "(B) 對會計原則變動予以適當揭露處理",
      "(C) 非常利益或損失不予列入損益表",
      "(D) 假設不同年度貨幣單位價值不變",
    ],
    ans: 1,
    hint: "🔑 一致性：前後期採用相同會計方法；若有變動須揭露",
    isCalc: false,
    explain:
      "一致性原則（Consistency Principle）要求企業前後期採用相同的會計原則與方法。\n\n若會計原則有所變動（如折舊方法從直線法改為年數合計法），企業必須：\n• 在財務報表附註中「適當揭露」變動內容\n• 說明變動對當期財務數字的影響\n\n這樣使用者才能了解不同期間數字是否具有可比性，(B) 正確反映一致性的要求。",
  },
    {
    id: "fin-14",
    q: "財務報表分析對公司本身而言，其目的有：",
    opts: [
      "(A) 瞭解營運績效及評估競爭力之優劣勢",
      "(B) 預測公司未來發展",
      "(C) 發現問題謀求控制與改善",
      "(D) 以上皆是",
    ],
    ans: 3,
    hint: "🔑 財務分析對公司內部的多元功能：評估、預測、診斷",
    isCalc: false,
    explain:
      "財務報表分析對公司本身（內部使用者）的三大目的：\n\n(A) 瞭解營運績效：比較歷史數據與同業，找出競爭優劣勢\n(B) 預測未來發展：基於過去數據，建立財務模型預測未來\n(C) 發現問題並改善：透過各項比率找出財務弱點，提出改善對策\n\n三者都是財務報表分析對公司本身的重要目的，故答案為「以上皆是」。",
  },
    {
    id: "fin-15",
    q: "下列對比較分析之敘述，何者正確？",
    opts: [
      "(A) 銷貨增加表示獲利一定增加",
      "(B) 比較分析應考慮物價變動之影響",
      "(C) 負債增加為不利經營之現象",
      "(D) 擴充廠房必須發行新股籌措資金",
    ],
    ans: 1,
    hint: "🔑 通貨膨脹使數字可比性降低，比較分析須調整物價因素",
    isCalc: false,
    explain:
      "(B) 正確：比較分析需考慮物價變動（通貨膨脹）對數字的影響，否則不同期間的名目金額無法真實比較實質變化。\n\n其他選項錯誤：\n(A) 銷貨增加但成本增加更多，獲利可能下降，非「一定增加」\n(C) 負債增加不一定不利，若用於擴大投資且報酬率高於資金成本，反而有利\n(D) 擴充廠房可透過舉債、保留盈餘等多種方式籌資，非僅限發行新股",
  },
    {
    id: "fin-16",
    q:
      "資產負債表、損益表、業主權益變動表、現金流量表四種主要財務報表中，屬於靜態報表者有：",
    opts: [
      "(A) 資產負債表",
      "(B) 資產負債表、損益表",
      "(C) 資產負債表、損益表、業主權益變動表",
      "(D) 資產負債表、損益表、業主權益變動表、現金流量表",
    ],
    ans: 0,
    hint: "🔑 靜態報表：某一特定時點的快照；動態報表：某一期間的流動",
    isCalc: false,
    explain:
      "靜態報表 vs 動態報表的判斷標準：\n\n• 靜態報表（Stock Statements）：反映某一特定日期（時點）的財務狀況\n  → 資產負債表（Balance Sheet）：如「12月31日」的資產、負債、淨值 ✓\n\n• 動態報表（Flow Statements）：反映某一期間內的變動\n  → 損益表（Income Statement）：某期間的收入與費用\n  → 業主權益變動表：股東權益在某期間的變動\n  → 現金流量表：某期間的現金流入流出\n\n故只有資產負債表屬於靜態報表，答案為 (A)。",
  },
    {
    id: "fin-17",
    q:
      "財務報表分析須蒐集企業四大會計報表之外，還須蒐集那些攸關資訊？",
    opts: [
      "(A) 會計師查核報告書",
      "(B) 企業會計政策、同業平均水準",
      "(C) 未來經營景氣預測",
      "(D) 以上皆是",
    ],
    ans: 3,
    hint: "🔑 完整分析需要：會計師意見 + 政策說明 + 產業環境資料",
    isCalc: false,
    explain:
      "財務報表分析除了四大財務報表外，還需蒐集多種補充資訊：\n\n(A) 會計師查核報告書：了解查核意見類型（無保留/保留/否定等），評估財報可信度\n(B) 會計政策說明、同業資料：了解企業採用的會計方法，以及與同業比較的基準\n(C) 景氣預測：將財務數字放在產業與總體環境中解讀\n\n缺少任何一類資訊都可能使分析失準，故「以上皆是」。",
  },
    {
    id: "fin-18",
    q: "何種科目不在資產負債表中？",
    opts: [
      "(A) 資產",
      "(B) 負債",
      "(C) 業主權益",
      "(D) 本期淨利",
    ],
    ans: 3,
    hint: "🔑 資產負債表 = 靜態報表；本期淨利 = 損益表的結果",
    isCalc: false,
    explain:
      "資產負債表（Balance Sheet）的三大構成要素：\n• 資產（Assets）\n• 負債（Liabilities）\n• 業主權益（Owner's Equity）\n\n「本期淨利」是損益表（Income Statement）所計算出的結果。在結帳程序完成後，本期淨利才被轉入「保留盈餘」，成為資產負債表中股東權益的一部分，但「本期淨利」科目本身不直接列示於資產負債表中。",
  },
    {
    id: "fin-19",
    q:
      "企業經營績效好壞的衡量，勢必先選定適當的標準，再與實際經營結果比較後才能判斷：",
    opts: [
      "(A) 同業平均水準或同業目標標準",
      "(B) 企業預定目標",
      "(C) 企業歷史水準",
      "(D) 以上皆是",
    ],
    ans: 0,
    hint: "🔑 本題問最「適當」的標準，選項 A 最具外部客觀性",
    isCalc: false,
    explain:
      "評估企業績效時，選定比較標準非常重要。本題選項 (A) 同業平均水準是最客觀且具有外部比較價值的標準，因為可以衡量企業在同業中的相對競爭地位。\n\n注意：雖然 (B) 企業預定目標與 (C) 歷史水準也可作為參考，但題目問的是「適當標準」，同業比較具有最強的客觀性與代表性，故答案為 (A)。",
  },
    {
    id: "fin-20",
    q: "具有控制能力或重大影響力之長期股權投資，採用何種會計方法衡量？",
    opts: [
      "(A) 公允價值法",
      "(B) 權益法",
      "(C) 成本法",
      "(D) 攤銷後成本法",
    ],
    ans: 1,
    hint: "🔑 持股 20%~50% → 重大影響力 → 權益法；持股 > 50% → 控制 → 合併報表",
    isCalc: false,
    explain:
      "長期股權投資的會計方法選擇：\n\n• 持股比例 < 20%：通常採公允價值法（無重大影響力）\n• 持股比例 20%~50%：採「權益法」（Equity Method）— 有重大影響力\n• 持股比例 > 50%：編製合併財務報表（有控制能力）\n\n【權益法特點】\n• 投資帳面價值 = 原始成本 + 按持股比例認列的被投資公司損益\n• 收到股利時，減少投資帳面價值（非認列股利收入）\n• 能反映被投資公司淨資產的真實變動",
  },
    {
    id: "fin-21",
    q: "在比較損益表中，以實際數與同一項目的預算數相比較，稱為：",
    opts: [
      "(A) 絕對數字比較法",
      "(B) 絕對數字增減變動法",
      "(C) 增減百分比法",
      "(D) 差異分析法",
    ],
    ans: 3,
    hint: "🔑 實際 vs 預算 → 差異分析，找出差異並解釋原因",
    isCalc: false,
    explain:
      "差異分析法（Variance Analysis）：將實際發生的數字與預算（標準）數字進行比較，計算差異（有利差異 / 不利差異），並分析差異產生的原因（數量差異、價格差異等）。\n\n這是管理會計（Management Accounting）的重要工具，用於績效評估與成本控制。\n\n例如：\n• 實際銷售額 $1,000,000 vs 預算 $1,200,000\n• 銷售差異 = -$200,000（不利差異）",
  },
    {
    id: "fin-22",
    q:
      "所謂「穩健原則」是指在不確定採取何種會計處理方式較能允當表達的情況下，應選擇：",
    opts: [
      "(A) 最不可能高估企業當期損益及淨資產的方法",
      "(B) 最不可能低估企業當期損益及淨資產的方法",
      "(C) 一律以歷史成本作為衡量損益及淨資產的方法",
      "(D) 一律以成本與淨變現價值孰低法衡量損益及淨資產",
    ],
    ans: 0,
    hint: "🔑 穩健/保守原則：寧低估資產與收益，不高估；寧認損失，不遞延",
    isCalc: false,
    explain:
      "穩健原則（Conservatism / Prudence）：在不確定的情況下，應選擇最不可能「高估」資產和收益、最不可能「低估」負債和損失的方法。\n\n核心精神：\n• 損失：盡早認列（寧早不晚）\n• 利益：確定後才認列（寧晚不早）\n\n實際應用例子：\n• 存貨採成本與淨變現價值孰低法（市值跌就認損失）\n• 壞帳採備抵法（預估將來可能的損失先認列）",
  },
    {
    id: "fin-23",
    q: "下列何者較可能用來預測公司的未來營運績效？",
    opts: [
      "(A) 稅前淨利",
      "(B) 非常損益前淨利",
      "(C) 會計原則改變前淨利",
      "(D) 繼續營業部門淨利",
    ],
    ans: 3,
    hint: "🔑 預測未來 → 使用「持續性、經常性」的損益來源",
    isCalc: false,
    explain:
      "預測未來績效時，應使用最具「持續性」（Persistent）的損益數字：\n\n• 繼續營業部門淨利（D）：來自企業正常、持續的核心業務，最具代表未來的能力 ✓\n\n以下項目不適合用來預測，因為是「一次性」或「非經常性」項目：\n• 稅前淨利（A）：含稅率影響，變動因素多\n• 非常損益（B）：一次性、不可預期的事件\n• 會計原則改變（C）：政策性調整，不會持續發生",
  },
    {
    id: "fin-24",
    q: "下列何項屬於靜態分析？",
    opts: [
      "(A) 計算本期流動資產較上期增減之金額",
      "(B) 計算本期流動資產占基期流動資產金額之百分比",
      "(C) 計算本期流動資產占總資產之比率",
      "(D) 計算本期期末流動比率並與同業其他公司以往的平均流動比率比較",
    ],
    ans: 2,
    hint: "🔑 靜態分析：同一時點內部結構比，不涉及跨期或跨企業比較",
    isCalc: false,
    explain:
      "(C) 計算「本期流動資產占總資產之比率」是同一時點（本期）的結構分析，屬靜態分析（Structural Analysis）。\n\n其他選項分析：\n(A) 本期 vs 上期金額 → 跨期比較 → 動態\n(B) 本期 vs 基期百分比 → 跨期比較 → 動態\n(D) 本期比率 vs 同業歷史平均 → 跨期 + 跨企業比較 → 動態",
  },
    {
    id: "fin-25",
    q: "若純益率太低，經理人可以從那些方面檢討改善？",
    opts: [
      "(A) 提高銷售額（產品價格及銷售量）",
      "(B) 降低銷貨成本",
      "(C) 節制銷售及管理費用支出",
      "(D) 以上均應列為檢討重點",
    ],
    ans: 1,
    hint: "🔑 純益率 = 淨利 ÷ 銷貨淨額；題目問最「直接」的改善方式",
    isCalc: false,
    explain:
      "純益率（Net Profit Margin）= 稅後淨利 ÷ 銷貨淨額\n\n本題考的是最直接且關鍵的改善措施：\n(B) 降低銷貨成本：直接提升毛利率，是最核心的改善手段。\n\n雖然 (A) 提高銷售額和 (C) 降低費用也能改善純益率，但題目標準答案為 (B)，強調「降低銷貨成本」是純益率低的最根本問題所在。\n\n注意：實際上 (A)、(B)、(C) 均為有效改善途徑，但本題答案依 PDF 標記為 (B)。",
  },
    {
    id: "fin-26",
    q: "下列何者情況下對債權人有利？",
    opts: [
      "(A) 利息保障倍數增加",
      "(B) 負債比率增加",
      "(C) 流動資產減少",
      "(D) 存貨平均銷售天數提高",
    ],
    ans: 0,
    hint: "🔑 利息保障倍數 ↑ → 企業支付利息能力 ↑ → 債權人風險降低",
    isCalc: false,
    explain:
      "利息保障倍數（Interest Coverage Ratio / Times Interest Earned）：\n公式：EBIT（稅前息前淨利）÷ 利息費用\n\n• 比率越高：企業用盈餘支付利息的能力越強，債權人越有保障 ✓\n\n其他選項對債權人不利：\n(B) 負債比率增加 → 財務槓桿更高 → 還款風險增加\n(C) 流動資產減少 → 短期償債能力下降\n(D) 存貨銷售天數提高 → 資金周轉變慢 → 流動性變差",
  },
    {
    id: "fin-27",
    q: "下列何者不是財務報表使用者？",
    opts: [
      "(A) 短期債權人",
      "(B) 稽徵機關",
      "(C) 審計人員",
      "(D) 以上皆是",
    ],
    ans: 3,
    hint: "🔑 本題問「不是使用者」→ 注意選項 D 的反向邏輯",
    isCalc: false,
    explain:
      "本題是反向題，問的是「不是財務報表使用者」。\n\n財務報表的合法使用者非常廣泛：\n• 短期債權人（A）：評估短期還款能力\n• 稽徵機關（B）：評估納稅義務是否履行\n• 審計人員（C）：查核財報是否允當表達\n\n以上三者都是財務報表的使用者，因此「以上皆是（財務報表使用者）」，選 (D) 表示三者都是，沒有不是使用者的選項。",
  },
    {
    id: "fin-28",
    q: "下列何者為比較財務報表分析的限制？",
    opts: [
      "(A) 前後兩期營業性質不同無法比較",
      "(B) 前後兩期會計方法不一致無法比較",
      "(C) 前後兩期物價水準不一致無法比較",
      "(D) 以上皆是",
    ],
    ans: 3,
    hint: "🔑 比較分析的三大障礙：性質變化、方法不一、物價影響",
    isCalc: false,
    explain:
      "比較財務報表分析的三大限制：\n\n(A) 營業性質改變：企業轉型或購併後，前後期業務不同，比較失去意義\n(B) 會計方法不一致：折舊方法、存貨計價方法改變，使數字不具可比性\n(C) 物價水準差異：通貨膨脹使名目金額膨脹，實質比較需進行物價調整\n\n三者都是真實存在的比較分析限制，故答案為「以上皆是」。",
  },
    {
    id: "fin-29",
    q: "下列何者是財務報表分析的方法？",
    opts: [
      "(A) 比率分析",
      "(B) 比較分析",
      "(C) 趨勢分析",
      "(D) 以上皆是",
    ],
    ans: 3,
    hint: "🔑 財務分析方法包含：比率分析、比較分析、趨勢分析、共同比分析等",
    isCalc: false,
    explain:
      "財務報表分析的主要方法：\n\n(A) 比率分析（Ratio Analysis）：計算各財務比率並與基準比較\n(B) 比較分析（Comparative Analysis）：比較不同期間或不同企業的財務數字\n(C) 趨勢分析（Trend Analysis）：以基期為基準，觀察各期變動趨勢\n其他：共同比分析（Common-size Analysis）、現金流量分析等\n\n三種方法都是財務報表分析的核心工具，故答案為「以上皆是」。",
  },
    {
    id: "fin-30",
    q: "下列何者會影響一家公司的審計風險？",
    opts: [
      "(A) 公司所處的行業",
      "(B) 公司人事變動情形",
      "(C) 審計人員的專業素養",
      "(D) 以上皆是",
    ],
    ans: 3,
    hint: "🔑 審計風險 = 固有風險 × 控制風險 × 偵察風險，三者各有影響因子",
    isCalc: false,
    explain:
      "審計風險（Audit Risk）= 固有風險（IR）× 控制風險（CR）× 偵察風險（DR）\n\n(A) 公司所處行業 → 影響固有風險（某些行業天生風險較高，如金融業）\n(B) 公司人事變動 → 影響控制風險（人事更動影響內部控制效果）\n(C) 審計人員專業素養 → 影響偵察風險（能力越強，偵察到錯誤的機率越高）\n\n三者都是審計風險的影響因素，故答案為「以上皆是」。",
  },
    {
    id: "fin-31",
    q:
      "與決策有關，具有改變決策的能力，以及對問題有幫助，我們稱之為？",
    opts: [
      "(A) 可靠性",
      "(B) 攸關性",
      "(C) 可能比較性",
      "(D) 時效性",
    ],
    ans: 1,
    hint: "🔑 攸關性（Relevance）：能影響使用者決策的資訊特性",
    isCalc: false,
    explain:
      "攸關性（Relevance）是會計資訊的基本質性特徵之一：\n\n定義：資訊與使用者的決策有關，能改變或影響使用者的判斷和決策。\n\n攸關性的兩個組成要素：\n• 預測價值（Predictive Value）：能幫助使用者預測未來結果\n• 確認價值（Confirmatory Value）：能確認或修正先前的預測\n\n時效性（Timeliness）則是攸關性的輔助特徵，資訊必須及時提供才能保有攸關性。",
  },
    {
    id: "fin-32",
    q: "會計入帳時需要相關的憑證係為符合：",
    opts: [
      "(A) 比較性",
      "(B) 中立性",
      "(C) 一致性",
      "(D) 可驗證性",
    ],
    ans: 3,
    hint: "🔑 有憑證才能核實 → 可驗證性（Verifiability）原則",
    isCalc: false,
    explain:
      "可驗證性（Verifiability）：指有客觀、可追查的憑證（如發票、收據、合約）作為會計入帳的依據，使不同的獨立人員依據相同資訊，能得到相同的結論。\n\n憑證種類：\n• 外部憑證：發票、收據、銀行對帳單（較具公信力）\n• 內部憑證：傳票、領料單、請購單\n\n可驗證性是可靠性（忠實表達）的重要組成部分，確保會計資訊不是主觀臆測。",
  },
    {
    id: "fin-33",
    q: "下列何者非財務報告分析時之限制？",
    opts: [
      "(A) 各公司所採用會計方法未必相同",
      "(B) 沒有固定資產現值之資料",
      "(C) 無法做量化的分析",
      "(D) 會計個體前後期不一致",
    ],
    ans: 2,
    hint: "🔑 財務分析本來就是量化分析；「無法量化」是錯誤說法",
    isCalc: false,
    explain:
      "(C) 「無法做量化的分析」說法錯誤，因為財務報表分析的核心就是「量化分析」——計算各種比率、百分比、金額增減等。\n\n真正的財務報告分析限制（A、B、D 均為真實限制）：\n(A) 各公司會計方法不同：使同業比較失準\n(B) 沒有現值資料：歷史成本不反映資產真實市值\n(D) 前後期個體不一致：企業購併或轉型後，比較失去意義",
  },
    {
    id: "fin-34",
    q:
      "若您是一個公司的管理者，當面對財務報表分析時，下列敘述何者錯誤？",
    opts: [
      "(A) 應收帳款週轉率 ＝ 營業收入 ÷ 平均應收款項",
      "(B) 流動比率過高，表示公司可能發生週轉不靈",
      "(C) 總資產報酬率的比率越高，表示公司獲利能力越佳",
      "(D) 股票的本益比 ＝ 每股股價 ÷ 每股稅後盈餘",
    ],
    ans: 1,
    hint: "🔑 流動比率過高 → 資金閒置，效率差；過低才是週轉不靈的警訊",
    isCalc: false,
    explain:
      "(B) 說法錯誤：流動比率過高表示流動資產遠超流動負債，資金閒置、未能有效運用，並不表示「週轉不靈」。\n\n• 流動比率「過低」（如低於 1）→ 才可能發生週轉不靈\n• 流動比率「過高」→ 資金利用效率低，過度保守\n\n其他選項均正確：\n(A) 應收帳款週轉率公式正確\n(C) 總資產報酬率（ROA）越高確實代表獲利能力越佳\n(D) 本益比（P/E Ratio）= 股價 ÷ EPS，公式正確",
  },
    {
    id: "fin-35",
    q:
      "德昌公司上年度應收帳款週轉率為 72，存貨週轉率為 18，則該公司淨營業循環的天數為：",
    opts: [
      "(A) 5 天",
      "(B) 20 天",
      "(C) 25 天",
      "(D) 90 天",
    ],
    ans: 2,
    hint: "🔑【計算題】應收帳款週轉天數 = 365 ÷ 週轉率；淨營業循環 = 存貨天數 + 應收帳款天數",
    isCalc: true,
    explain:
      "【計算步驟】\n\n① 應收帳款週轉天數（Days Sales Outstanding）\n   = 365 ÷ 應收帳款週轉率\n   = 365 ÷ 72 ≈ 5 天\n\n② 存貨週轉天數（Days Inventory Outstanding）\n   = 365 ÷ 存貨週轉率\n   = 365 ÷ 18 ≈ 20 天\n\n③ 淨營業循環天數（Net Operating Cycle）\n   = 存貨週轉天數 + 應收帳款週轉天數\n   = 20 + 5 = 25 天\n\n→ 答案 (C)：25 天\n\n【意義】淨營業循環越短，資金周轉越快，流動性越好。",
  },
    {
    id: "fin-36",
    q:
      "計算各期某項目與基期相關項目之百分比關係，是屬於下列何種分析？",
    opts: [
      "(A) 比較分析",
      "(B) 比率分析",
      "(C) 結構分析",
      "(D) 趨勢分析",
    ],
    ans: 3,
    hint: "🔑 各期與基期百分比關係 → 跨期間變動觀察 → 趨勢分析",
    isCalc: false,
    explain:
      "趨勢分析（Trend Analysis）：\n• 選定某一期為「基期」（設為 100%）\n• 計算後續各期相對於基期的百分比\n• 觀察各期數字的增減趨勢\n\n例如：以 2018 年為基期，\n2019 年銷售額 = 基期的 110%\n2020 年銷售額 = 基期的 125%\n→ 可看出銷售持續成長的趨勢",
  },
    {
    id: "fin-37",
    q:
      "資產負債表中大部分項目都是以歷史成本作為評價基礎，主要的考量是因為歷史成本的資訊具有下列何種特性？",
    opts: [
      "(A) 決策攸關",
      "(B) 可靠性",
      "(C) 可衡量性",
      "(D) 永續經營",
    ],
    ans: 1,
    hint: "🔑 歷史成本有原始憑證可查 → 可驗證 → 可靠性高",
    isCalc: false,
    explain:
      "歷史成本（Historical Cost）被廣泛採用的主要原因是其「可靠性」（Reliability）：\n\n• 有客觀的交易憑證（發票、合約）支持\n• 可由不同人員獨立核實，結果相同（可驗證性）\n• 不受主觀判斷影響\n\n雖然歷史成本的攸關性（Relevance）較低（不反映當前市值），但其高度可靠性使其成為會計計量的主要基礎。\n\n現代 IFRS 也允許部分資產（如投資性不動產）改用公允價值，以提升攸關性。",
  },
    {
    id: "fin-38",
    q:
      "決定一財務報表資訊是否具有「重要性」的標準，通常是看該項資訊是否：",
    opts: [
      "(A) 影響企業的總資產金額",
      "(B) 影響企業的盈餘金額",
      "(C) 影響一個正常投資人的專業判斷",
      "(D) 影響企業的現金流量",
    ],
    ans: 2,
    hint: "🔑 重要性（Materiality）：以影響「理性使用者決策」為判斷標準",
    isCalc: false,
    explain:
      "重要性（Materiality）的判斷標準：若遺漏或錯誤表達某項資訊，是否可能影響「一般理性投資人（合理決策者）」的專業判斷？\n\n重要性是相對概念，不只看絕對金額大小，更看對決策的影響程度：\n• 百萬元的金額對小企業可能重要，對大型集團可能不重要\n• 金額雖小但性質特殊（如高層舞弊），也可能具有重要性\n\n會計師依據重要性決定查核程序的範圍與深度。",
  },
    {
    id: "fin-39",
    q:
      "財務報表資訊有下列哪些特質時，可以增加報表資訊的可靠性（Reliability）？ a.中立性（Neutrality） b.及時性（Timeliness） c.可驗證性（Verifiability）",
    opts: [
      "(A) a 和 b",
      "(B) a 和 c",
      "(C) b 和 c",
      "(D) a、b 和 c",
    ],
    ans: 1,
    hint: "🔑 可靠性 = 忠實表達；中立性 + 可驗證性是其組成；及時性屬攸關性的輔助",
    isCalc: false,
    explain:
      "可靠性（Reliability）/ 忠實表達（Faithful Representation）的組成要素：\n\n✓ 中立性（Neutrality）：無偏頗，不偏向任何特定使用者或結果\n✓ 可驗證性（Verifiability）：有客觀憑證，可由不同人核實\n✓ 完整性（Completeness）：揭露足夠資訊\n\n✗ 及時性（Timeliness）：屬於「攸關性」的輔助特徵，非可靠性的構成要素\n\n→ 答案為 (B)：a 和 c（中立性 + 可驗證性）",
  },
    {
    id: "fin-40",
    q: "現金流量允當比率之目的，在評估：",
    opts: [
      "(A) 評估三年間營業活動現金流量趨勢",
      "(B) 現金支付股利的均勻程度",
      "(C) 每股盈餘成長趨勢",
      "(D) 企業來自營業活動的現金流量，能負荷資本支出、存貨淨增加數和現金股利的程度",
    ],
    ans: 3,
    hint: "🔑 現金流量允當比率 = 近三年營業 CF ÷（資本支出 + 存貨增加 + 股利），評估自給能力",
    isCalc: false,
    explain:
      "現金流量允當比率（Cash Flow Adequacy Ratio）：\n\n公式：\n近三年營業活動現金流量總和 ÷（近三年資本支出 + 存貨淨增加 + 現金股利支付）\n\n目的：評估企業靠自身營業產生的現金，能否負擔以下三項主要資金需求：\n1. 資本支出（維持/擴充固定資產）\n2. 存貨淨增加（營業規模擴大）\n3. 現金股利（股東報酬）\n\n• 比率 ≥ 1：表示現金自給自足，財務健全\n• 比率 < 1：表示需借助外部融資",
  },
    {
    id: "fin-41",
    q:
      "連續多年或多期財務報表間，相同項目或科目增減變化之比較分析，稱為：",
    opts: [
      "(A) 分析比率",
      "(B) 垂直分析",
      "(C) 水平分析",
      "(D) 共同比分析",
    ],
    ans: 2,
    hint: "🔑 水平分析 = 跨期間（橫向）比較同一項目的增減",
    isCalc: false,
    explain:
      "水平分析（Horizontal Analysis）：\n• 比較連續多期相同科目的金額增減與百分比變化\n• 重點在「時間」維度的橫向延伸\n• 可揭示趨勢，但無法直接比較不同規模企業\n\n垂直分析（Vertical Analysis）= 共同比分析：\n• 以某一項目（如總資產、銷貨淨額）為 100%\n• 分析同一期間各科目的比例關係\n• 可比較不同規模企業",
  },
    {
    id: "fin-42",
    q:
      "立大公司在共同比財務分析中，若比較基礎為資產負債表者，應以何項目做為 100%？",
    opts: [
      "(A) 負債總額",
      "(B) 股東權益總額",
      "(C) 資產總額",
      "(D) 固定資產總額",
    ],
    ans: 2,
    hint: "🔑 共同比資產負債表：資產總額 = 100%",
    isCalc: false,
    explain:
      "共同比財務報表的基準：\n\n• 共同比資產負債表 → 資產總額 = 100%\n  （各資產科目、各負債科目、股東權益科目均除以資產總額）\n\n• 共同比損益表 → 銷貨淨額 = 100%\n  （各項收入、成本、費用均除以銷貨淨額）\n\n資產負債表的恆等式：資產 = 負債 + 股東權益\n以資產總額為基準，可同時分析負債比率與自有資本比率。",
  },
    {
    id: "fin-43",
    q: "在分析財務報表時，債權人的最終目的為：",
    opts: [
      "(A) 瞭解企業過去的財務狀況",
      "(B) 瞭解債務人是否有能力償還本息",
      "(C) 瞭解企業未來的獲利能力",
      "(D) 瞭解企業的資本結構",
    ],
    ans: 1,
    hint: "🔑 債權人最關心：貸出的錢能否按時拿回本金並收到利息",
    isCalc: false,
    explain:
      "不同使用者分析財務報表的最終目的不同：\n\n• 債權人（銀行、債券持有人）：最終目的是評估「還款能力」——借款人能否按時償還本金並支付利息\n\n債權人重點關注的比率：\n• 流動比率（短期償債能力）\n• 負債比率（長期財務結構）\n• 利息保障倍數（支付利息的能力）\n• 現金流量（實際可用現金）",
  },
    {
    id: "fin-44",
    q: "下列何者為虛帳戶？",
    opts: [
      "(A) 資產",
      "(B) 收益",
      "(C) 業主權益",
      "(D) 負債",
    ],
    ans: 1,
    hint: "🔑 虛帳戶（名目帳戶）= 損益類科目，每期結清歸零",
    isCalc: false,
    explain:
      "帳戶的分類：\n\n• 實帳戶（Permanent Accounts / Real Accounts）：資產、負債、業主權益\n  → 餘額跨期延續，不在結帳時清零\n\n• 虛帳戶（Temporary Accounts / Nominal Accounts）：收入、費用、股利\n  → 每期期末進行結帳，餘額歸零後轉入保留盈餘\n\n收益（Revenue）是虛帳戶，每期結帳後清零，符合期間損益計算的需要。",
  },
    {
    id: "fin-45",
    q:
      "無論現金是否已收付，只要有交易事實存在而有責任或權利的發生，就應記帳是指：",
    opts: [
      "(A) 現金收付基礎",
      "(B) 混合基礎",
      "(C) 權責發生基礎",
      "(D) 聯合基礎",
    ],
    ans: 2,
    hint: "🔑 有交易事實即記帳，不論現金流動 → 權責發生制（應計基礎）",
    isCalc: false,
    explain:
      "兩種會計基礎的比較：\n\n• 現金收付基礎（Cash Basis）：實際收到現金才認列收入，實際付現才認列費用\n  → 簡單，但不符合配合原則，也不能反映真實盈虧\n\n• 權責發生基礎（Accrual Basis）：交易事實發生，不論現金是否收付，即應認列\n  → 一般公認會計原則（GAAP）與 IFRS 採用此基礎\n  → 例如：已交貨但尚未收款，仍應認列銷貨收入",
  },
    {
    id: "fin-46",
    q: "下列何者通常並非財務報告分析人員常用的工具？",
    opts: [
      "(A) 趨勢分析",
      "(B) 隨機抽樣分析",
      "(C) 共同比分析",
      "(D) 比較分析",
    ],
    ans: 1,
    hint: "🔑 隨機抽樣屬統計學與審計工具，非財務分析的常規方法",
    isCalc: false,
    explain:
      "財務報告分析（Financial Statement Analysis）常用工具：\n✓ 趨勢分析（A）\n✓ 共同比分析（C）\n✓ 比較分析（D）\n✓ 比率分析、現金流量分析等\n\n隨機抽樣（B）是審計（Auditing）和統計研究中常用的工具，用於從大量數據中抽取樣本進行查核。\n\n財務報表分析師關注的是報表中的全部數據，而非抽樣。因此 (B) 通常不是財務分析人員的工具。",
  },
    {
    id: "fin-47",
    q: "下列何者為財務報表分析的第一個步驟？",
    opts: [
      "(A) 計算各項比率",
      "(B) 研讀財務報表的附註",
      "(C) 閱讀會計師的查核報告",
      "(D) 確立分析的目的",
    ],
    ans: 3,
    hint: "🔑 分析流程第一步：明確目的，才知道要找什麼指標",
    isCalc: false,
    explain:
      "財務報表分析的標準流程：\n\n1️⃣ 確立分析目的（D）：明確「為何分析？為誰分析？」（評估償債能力？獲利能力？投資價值？）\n2️⃣ 蒐集相關資料（財報、查核報告、附註等）\n3️⃣ 閱讀查核報告（了解審計意見）\n4️⃣ 研讀財務報表與附註\n5️⃣ 計算各項比率\n6️⃣ 比較與分析\n7️⃣ 形成結論並提出建議\n\n「確立分析目的」是一切分析的起點，不同目的會使用不同的分析指標。",
  },
    {
    id: "fin-48",
    q:
      "投資人在選擇投資計畫時應同時考慮報酬和風險，一般而言：",
    opts: [
      "(A) 應選擇風險最高的計畫",
      "(B) 報酬率最高的計畫通常風險程度最低",
      "(C) 報酬率最低的計畫通常風險程度最低",
      "(D) 風險程度和報酬率之間並無關係",
    ],
    ans: 2,
    hint: "🔑 風險與報酬正相關：高風險高報酬，低風險低報酬",
    isCalc: false,
    explain:
      "風險與報酬的基本關係（Risk-Return Tradeoff）：\n\n• 高風險 → 投資人要求高報酬率作為補償\n• 低風險 → 報酬率通常也較低（例如：定期存款風險低，報酬率也低）\n\n(C) 正確：報酬率最低的計畫（如政府公債、定存）通常風險也最低\n(B) 錯誤：報酬率最高的計畫通常風險最「高」（非最低）\n\n投資人應依自身的風險承受度，選擇適合的報酬-風險組合。",
  },
    {
    id: "fin-49",
    q:
      "上市（櫃）公司之股東常會應於每會計年度終了後幾個月內召開？",
    opts: [
      "(A) 三個月",
      "(B) 四個月",
      "(C) 五個月",
      "(D) 六個月",
    ],
    ans: 3,
    hint: "🔑 公司法：股東常會 → 會計年度終了後 6 個月內召開",
    isCalc: false,
    explain:
      "依據台灣《公司法》第 170 條規定：\n\n股東常會（Annual General Meeting, AGM）應於每會計年度終了後「6 個月內」召開。\n\n實務上，大多數上市（櫃）公司的會計年度為 1 月 1 日至 12 月 31 日，因此股東常會通常在次年的 6 月 30 日前召開（即隔年的第二季末前）。",
  },
    {
    id: "fin-50",
    q:
      "下列何種情況發生時，將無法以共同比（Common-size）方式分析二個年度的資產負債表？",
    opts: [
      "(A) 開始營業的第一個年度",
      "(B) 某一年度的存款金額為 0",
      "(C) 某一年度的股東權益為負值",
      "(D) 以上三種情況仍可使用共同比分析",
    ],
    ans: 3,
    hint: "🔑 共同比以資產總額為基準，三種情況資產仍有值，均可計算",
    isCalc: false,
    explain:
      "共同比資產負債表以「資產總額」為 100% 基準，只要資產總額不為零，就可以進行共同比分析：\n\n(A) 第一個年度：資產仍有值，可計算 ✓\n(B) 存款金額為 0：只是某一科目為零，資產總額不為零，仍可分析 ✓\n(C) 股東權益為負值（資不抵債）：資產仍大於零，仍可計算各科目占比 ✓\n\n→ 三種情況皆可使用共同比分析，答案為 (D)。",
  },
    {
    id: "fin-51",
    q:
      "上市（櫃）公司依規定，財務報表及重大訊息須在何處公告？",
    opts: [
      "(A) 國內報紙",
      "(B) 上市（櫃）公司自家網站",
      "(C) 公開資訊觀測站",
      "(D) 以上皆非",
    ],
    ans: 2,
    hint: "🔑 台灣法定公告平台：公開資訊觀測站（MOPS）",
    isCalc: false,
    explain:
      "依台灣相關法規，上市（櫃）公司的財務報表及重大訊息（如重大合約、董事異動等）必須在「公開資訊觀測站」（Market Observation Post System, MOPS）公告，網址為 mops.twse.com.tw。\n\n這個平台由台灣證券交易所管理，投資人、分析師可免費查閱所有上市（櫃）公司的財報、公告等資料，確保資訊揭露的公平性。",
  },
    {
    id: "fin-52",
    q: "下列何者不屬於財務報表的「要素」（Element）？",
    opts: [
      "(A) 資產",
      "(B) 費用",
      "(C) 業主投資",
      "(D) 來自營業活動現金流量",
    ],
    ans: 3,
    hint: "🔑 財務報表要素：資產、負債、權益、收入、費用；現金流量分類非獨立要素",
    isCalc: false,
    explain:
      "財務報表的基本要素（Elements of Financial Statements）：\n• 資產（Assets）\n• 負債（Liabilities）\n• 業主權益（Equity），包括業主投資\n• 收入（Revenue / Income）\n• 費用（Expenses）\n• 利益（Gains）\n• 損失（Losses）\n\n「來自營業活動的現金流量」是現金流量表中的分類項目，描述的是現金的流向，不是財務報表的基本「要素」。",
  },
    {
    id: "fin-53",
    q: "會計資訊與實體相符合，我們稱之為？",
    opts: [
      "(A) 一致性",
      "(B) 忠實表達",
      "(C) 成本效益原則",
      "(D) 比較性",
    ],
    ans: 1,
    hint: "🔑 忠實表達（Faithful Representation）= 資訊真實反映實際交易和事件",
    isCalc: false,
    explain:
      "忠實表達（Faithful Representation）是可靠性的現代說法，指會計資訊必須：\n\n1. 完整性（Completeness）：揭露所有必要資訊\n2. 中立性（Neutrality）：無偏頗，不為特定目的操縱\n3. 無重大錯誤（Freedom from Error）：沒有錯誤或省略\n\n「與實體相符合」即指資訊正確地描述了真實的交易和經濟事件，沒有失真。這是會計兩大基本質性特徵（攸關性 + 忠實表達）之一。",
  },
    {
    id: "fin-54",
    q:
      "投資一個報酬不完全同向變動的資產組合（投資組合 portfolio），產生整體風險低於個別資產的效果，稱為：",
    opts: [
      "(A) 風險分擔（risk share）",
      "(B) 風險分散（diversification）",
      "(C) 規模經濟（economics of scale）",
      "(D) 資產轉換（asset transformation）",
    ],
    ans: 1,
    hint: "🔑 投資組合理論：不完全正相關的資產組合 → 降低整體風險 → 分散效果",
    isCalc: false,
    explain:
      "風險分散（Diversification）：\n• 將資金投資於相關性不完全正相關（correlation < 1）的多種資產\n• 投資組合的整體風險低於各資產風險的加權平均\n\n核心精神：「不要把所有雞蛋放在同一個籃子裡」\n\n數學原理：\n投資組合風險 = √(w₁²σ₁² + w₂²σ₂² + 2w₁w₂ρ₁₂σ₁σ₂)\n當相關係數 ρ₁₂ < 1 時，組合風險 < 個別資產風險的加權平均",
  },
    {
    id: "fin-55",
    q: "兩公司同一年的折舊方式皆採用年數合計法，其符合：",
    opts: [
      "(A) 可靠性",
      "(B) 攸關性",
      "(C) 比較性",
      "(D) 重要性",
    ],
    ans: 2,
    hint: "🔑 相同方法 → 可互相比較 → 比較性（Comparability）",
    isCalc: false,
    explain:
      "比較性（Comparability）：當不同企業採用相同的會計方法（如都用年數合計法計提折舊），其財務報表得以互相比較，評估相對績效。\n\n比較性與一致性的關係：\n• 一致性（Consistency）：同一企業前後期採用相同方法\n• 比較性（Comparability）：不同企業間可以互相比較\n\n本題兩家公司採用相同折舊方法，使得財務數字具有可比性。",
  },
    {
    id: "fin-56",
    q: "收入須符合哪兩個條件，才能認列？",
    opts: [
      "(A) 已實現及已賺得",
      "(B) 可實現及可賺得",
      "(C) 已實現及可賺得",
      "(D) 以上皆可",
    ],
    ans: 0,
    hint: "🔑 收入認列兩大條件：已實現（或可實現）+ 已賺得",
    isCalc: false,
    explain:
      "收入認列（Revenue Recognition）的兩個條件：\n\n1. 已實現或可實現（Realized or Realizable）：\n   • 已實現：已收到現金\n   • 可實現：有合理把握在未來收到現金（如賒銷給信用良好的客戶）\n\n2. 已賺得（Earned）：\n   • 企業已完成或實質上已完成了賺取收入所需的工作\n   • 商品所有權已移轉，或服務已完成\n\n→ 答案 (A)「已實現及已賺得」是最嚴格的標準，也是最常見的認列條件。",
  },
    {
    id: "fin-57",
    q: "股票的本益比係指：",
    opts: [
      "(A) 每股盈餘 / 股價",
      "(B) 股價 / 每股股利",
      "(C) 股價 / 每股淨值",
      "(D) 股價 / 每股盈餘",
    ],
    ans: 3,
    hint: "🔑【公式】P/E Ratio = 股價（Price）÷ 每股盈餘（EPS）",
    isCalc: true,
    explain:
      "本益比（Price-to-Earnings Ratio, P/E Ratio）：\n\n公式：本益比 = 每股市場價格 ÷ 每股稅後盈餘（EPS）\n\n例如：\n• 股價 = $100 元\n• EPS = $5 元\n• 本益比 = $100 ÷ $5 = 20 倍\n\n意義：投資人願意為每 1 元盈餘支付 20 元的代價。\n• 本益比越高：市場對公司未來成長越樂觀（但也可能高估）\n• 本益比越低：相對便宜，但也可能反映悲觀預期\n\n→ 答案 (D)",
  },
    {
    id: "fin-58",
    q: "下列何者為動態分析？",
    opts: [
      "(A) 同一報表科目與類別的比較",
      "(B) 不同期間報表科目互相比較",
      "(C) 同年比率分析",
      "(D) 相同科目數字上的結構比較",
    ],
    ans: 1,
    hint: "🔑 動態分析 = 跨不同時間點的比較，有時間維度",
    isCalc: false,
    explain:
      "動態分析（Dynamic Analysis）涉及「時間」維度，即比較不同期間的財務數字：\n\n✓ 不同期間報表科目互相比較（B）→ 動態分析\n\n靜態分析（Static Analysis）在同一時點進行：\n✗ 同一報表科目與類別的比較（A）→ 靜態\n✗ 同年比率分析（C）→ 靜態（同一期）\n✗ 相同科目結構比較（D）→ 靜態（共同比）",
  },
    {
    id: "fin-59",
    q:
      "下列四種行業哪一行業的共同比資產負債表（Common-size Balance Sheet）中，流動資產之百分比通常較其他三個行業為高？",
    opts: [
      "(A) 汽車經銷業",
      "(B) 化學製藥業",
      "(C) 航空運輸業",
      "(D) 電腦製造業",
    ],
    ans: 0,
    hint: "🔑 流動資產高占比 → 大量存貨 + 應收帳款 → 商品流通業",
    isCalc: false,
    explain:
      "汽車經銷業的資產特性：\n• 核心資產是「汽車存貨」（大量流動資產）\n• 加上大量「應收帳款」（分期付款銷售）\n• 固定資產相對較少（展示場、辦公室）\n\n其他三個行業固定資產占比高：\n• 化學製藥業：大量實驗設備、廠房\n• 航空運輸業：昂貴的飛機（固定資產）\n• 電腦製造業：生產設備、研發設施\n\n→ 汽車經銷業流動資產占比最高",
  },
    {
    id: "fin-60",
    q:
      "所謂「收入認列原則」是指收入的認定時點應在兩項條件均滿足時。其中一項條件為「已實現」（或「可實現」），另一條件為：",
    opts: [
      "(A) 已收款",
      "(B) 已發帳單",
      "(C) 已賺得",
      "(D) 已簽訂合約",
    ],
    ans: 2,
    hint: "🔑 收入認列的第二條件：已賺得（Earned）= 服務/商品已完成移交",
    isCalc: false,
    explain:
      "收入認列的兩個條件：\n\n1. 已實現或可實現（Realized / Realizable）\n2. 已賺得（Earned）←本題考的條件\n\n「已賺得」的意義：\n• 企業已完成了履行義務（Performance Obligation）\n• 商品所有權已移轉給買方，或服務已全部/實質完成\n\n注意：\n• 「已收款」（A）不是必要條件，賒銷時未收款仍可認列\n• 「已發帳單」（B）只是開立發票，不代表已完成交付\n• 「已簽訂合約」（D）只是承諾，尚未履行",
  },
    {
    id: "fin-61",
    q:
      "將發展支出在發生當期即列為費用，主要係考量以下哪一因素？",
    opts: [
      "(A) 重要性",
      "(B) 決策攸關性",
      "(C) 保守穩健",
      "(D) 可比較性",
    ],
    ans: 2,
    hint: "🔑 研發/發展支出未來效益不確定 → 保守穩健 → 直接費用化",
    isCalc: false,
    explain:
      "研究與發展（R&D）支出的會計處理：\n\n發展支出在發生當期即費用化，主要基於「保守穩健原則」：\n• 發展支出的未來效益高度不確定（產品不一定研發成功）\n• 若資本化（認列為資產），但日後失敗，則資產虛增\n• 保守起見，直接費用化，避免高估資產和利益\n\n注意：IFRS（IAS 38）規定，符合特定條件的「發展」支出可資本化，但「研究」支出必須費用化。台灣早期規定一律費用化。",
  },
    {
    id: "fin-62",
    q:
      "期末存貨之價格採用成本與淨變現價值孰低法，是基於下列何項會計原則（操作限制原則）？",
    opts: [
      "(A) 繼續經營假設",
      "(B) 穩健原則假設",
      "(C) 收入認列原則",
      "(D) 經濟個體假設",
    ],
    ans: 1,
    hint: "🔑 孰低法 → 市值跌即認損失 → 寧低估資產 → 穩健原則",
    isCalc: false,
    explain:
      "存貨的計量：成本與淨變現價值孰低法（Lower of Cost or Net Realizable Value, LCNRV）\n\n• 若存貨成本 ≤ 淨變現價值 → 按成本入帳\n• 若存貨成本 > 淨變現價值 → 按淨變現價值入帳，認列跌價損失\n\n這體現了「穩健原則」（Conservatism）：\n• 損失（跌價）：立即認列，不遞延\n• 利益（市價上漲超過成本）：不提前認列\n→ 避免高估存貨資產，保護財報使用者",
  },
    {
    id: "fin-63",
    q:
      "依營利事業所得稅查核準則，營利事業購買固定資產，其耐用年限不及幾年，或支出金額不超過 $60,000 者，得以該成本列為當年度費用？",
    opts: [
      "(A) 一年",
      "(B) 二年",
      "(C) 三年",
      "(D) 五年",
    ],
    ans: 1,
    hint: "🔑 稅法規定：耐用年限不及 2 年，或金額不超過 $60,000 → 可費用化",
    isCalc: false,
    explain:
      "依台灣《營利事業所得稅查核準則》：\n\n固定資產可直接費用化的條件（符合其中一項即可）：\n1. 耐用年限不及「2 年」\n2. 支出金額不超過 $60,000\n\n這反映了「重要性原則」的稅務應用：金額小或使用期間短的資產，若資本化再逐期折舊，將增加會計複雜度，且對稅務影響微小，故允許直接費用化。",
  },
    {
    id: "fin-64",
    q:
      "菱生公司在共同比財務分析中，若比較基礎為損益表者，應以何項目作為 100%？",
    opts: [
      "(A) 淨利",
      "(B) 銷貨成本",
      "(C) 銷貨淨額",
      "(D) 銷貨折讓與退回",
    ],
    ans: 2,
    hint: "🔑 共同比損益表：銷貨淨額 = 100%",
    isCalc: false,
    explain:
      "共同比損益表（Common-size Income Statement）以「銷貨淨額」為 100% 基準：\n\n銷貨淨額 = 銷貨總額 - 銷貨退回與折讓 - 銷貨折扣\n\n各項目占銷貨淨額的百分比：\n• 銷貨成本 ÷ 銷貨淨額 = 銷貨成本率\n• 毛利 ÷ 銷貨淨額 = 毛利率\n• 淨利 ÷ 銷貨淨額 = 純益率（淨利率）\n\n這使得不同規模的企業可以用百分比進行比較。",
  },
    {
    id: "fin-65",
    q: "依據收入認列原則，應於何時認列收益？",
    opts: [
      "(A) 生產完畢",
      "(B) 銷售完畢",
      "(C) 收現完畢",
      "(D) 以上皆有可能",
    ],
    ans: 3,
    hint: "🔑 收入認列時點因行業而異：可以是銷售、完工比例、收現等不同時點",
    isCalc: false,
    explain:
      "收入認列的時點並非固定，因業務性質而異：\n\n(A) 生產完畢時：如農產品、採礦業（產品有現成市場且價格穩定）\n(B) 銷售完畢時：一般商品買賣（最常見，商品所有權移轉時）\n(C) 收現完畢時：分期付款、收現基礎（回收風險高時）\n其他：工程完工百分比法（長期工程合約）\n\n→ 答案 (D)「以上皆有可能」，視具體業務性質而定。",
  },
    {
    id: "fin-66",
    q:
      "近年來，上市公司須按季、半年，提供季報、半年報的財務報表，試問此乃根據：",
    opts: [
      "(A) 配合原則",
      "(B) 收入認列原則",
      "(C) 充分揭露原則",
      "(D) 繼續經營假設",
    ],
    ans: 2,
    hint: "🔑 定期公告 → 使使用者充分知悉 → 充分揭露原則",
    isCalc: false,
    explain:
      "充分揭露原則（Full Disclosure Principle）：\n• 要求企業定期且及時公布足夠的財務資訊\n• 確保報表使用者（投資人、債權人）能做出有充分資訊基礎的決策\n\n具體規範：\n• 上市公司：季報（每季公告）、半年報、年報\n• 附帶重大訊息揭露義務\n\n這是保護投資人、維護市場公平性的重要原則。",
  },
    {
    id: "fin-67",
    q: "交易事項發生應有合法憑證作為記帳的依據是合乎：",
    opts: [
      "(A) 經濟個體假設",
      "(B) 重要性原則",
      "(C) 會計期間假設",
      "(D) 客觀性原則",
    ],
    ans: 3,
    hint: "🔑 有憑證才能客觀核實 → 客觀性原則（Objectivity Principle）",
    isCalc: false,
    explain:
      "客觀性原則（Objectivity Principle）：\n• 會計記錄必須以客觀、可驗證的憑證為依據\n• 避免主觀判斷影響財務資訊的可靠性\n\n憑證種類：\n• 外部憑證（較具公信力）：供應商發票、銀行對帳單、稅單\n• 內部憑證：傳票、領料單、生產報表\n\n客觀性原則是「可驗證性」的早期說法，確保財務資訊不依賴個人主觀估計，而有客觀事實支持。",
  },
    {
    id: "fin-68",
    q: "共同比（Common-size）財務報表的分析是屬於：",
    opts: [
      "(A) 結構分析",
      "(B) 比較分析",
      "(C) 趨勢分析",
      "(D) 比率分析",
    ],
    ans: 0,
    hint: "🔑 共同比 → 計算各科目占總額比例 → 分析結構（組成）",
    isCalc: false,
    explain:
      "共同比財務報表（Common-size Statements）屬於結構分析（Structural Analysis）：\n\n• 以特定項目（資產總額 / 銷貨淨額）為 100%\n• 計算各科目占比，呈現財務「結構」\n• 例如：負債占資產的 45%（資本結構）\n• 例如：銷貨成本占銷貨淨額的 60%（成本結構）\n\n這幫助分析師快速了解企業的資本結構、成本結構與獲利結構。",
  },
    {
    id: "fin-69",
    q:
      "甲公司銷售產品附有售後服務保證，有關售後服務的支出金額目前無法確定，但可合理估計，則該公司售後服務的支出：",
    opts: [
      "(A) 應待實際發生時再認列費用",
      "(B) 現在可先認列預估的費用",
      "(C) 現在可將預估支出金額列為銷貨折讓",
      "(D) 因為尚不知提供售後服務對象，所以不需認列負債",
    ],
    ans: 1,
    hint: "🔑 可合理估計的或有負債 → 依配合原則，應及時認列預估費用",
    isCalc: false,
    explain:
      "(B) 正確：雖然金額不確定，但可合理估計，依「配合原則」與「穩健原則」，應在銷售時即認列預估的售後服務費用。\n\n會計分錄（銷售時）：\n借：售後服務費用（費用增加）\n貸：應付售後服務費用（負債增加）\n\n當實際提供售後服務時：\n借：應付售後服務費用\n貸：現金 / 人工成本等\n\n此做法確保費用在相關收入的同一期間認列，符合配合原則。",
  },
    {
    id: "fin-70",
    q:
      "共同比（Common-size）分析是屬於哪些種類的分析？a. 結構分析  b. 趨勢分析  c. 動態分析  d. 靜態分析",
    opts: [
      "(A) b 與 c",
      "(B) a 與 d",
      "(C) a 與 c",
      "(D) b 與 d",
    ],
    ans: 1,
    hint: "🔑 共同比：同一時點（靜態）的結構比（結構分析）",
    isCalc: false,
    explain:
      "共同比分析的分類：\n\n✓ a. 結構分析：計算各科目占總額的百分比，呈現財務結構\n✓ d. 靜態分析：通常以同一時點的數據進行分析，不涉及跨期比較\n\n✗ b. 趨勢分析：需要跨多期比較，共同比不是趨勢分析\n✗ c. 動態分析：動態分析涉及時間維度，共同比是靜態的\n\n→ 答案 (B)：a 與 d",
  },
    {
    id: "fin-71",
    q:
      "共同比（Common-size）財務報表中會選擇一些項目作為 100%，這些項目包括哪些？ a. 總資產  b. 股東權益  c. 銷貨總額  d. 銷貨淨額",
    opts: [
      "(A) a 與 c",
      "(B) a 與 d",
      "(C) b 與 c",
      "(D) b 與 d",
    ],
    ans: 1,
    hint: "🔑 資產負債表 → 總資產為 100%；損益表 → 銷貨淨額為 100%",
    isCalc: false,
    explain:
      "共同比財務報表的基準選擇：\n\n① 共同比資產負債表 → 「總資產（a）」= 100%\n   資產各科目 + 負債各科目 + 股東權益各科目，皆除以總資產\n\n② 共同比損益表 → 「銷貨淨額（d）」= 100%\n   （注意是銷貨「淨」額，已扣除退回與折讓，非銷貨「總」額）\n\n→ 答案 (B)：a 與 d",
  },
    {
    id: "fin-72",
    q:
      "波浪公司 90 年度之損益表中顯示淨損為 $100,000，91 年度則有淨利 $20,000。下列何種方法將無法適用於 90 及 91 年度的損益分析？",
    opts: [
      "(A) 共同比分析（Common-size）",
      "(B) 絕對金額比較",
      "(C) 百分比變動比較",
      "(D) 絕對金額增減變動比較",
    ],
    ans: 2,
    hint: "🔑 當基期（分母）為負值時，百分比變動計算無意義",
    isCalc: false,
    explain:
      "百分比變動比較（Percentage Change Analysis）公式：\n變動百分比 = (本期 - 基期) ÷ 基期 × 100%\n\n問題：\n若 90 年度（基期）淨損為 -$100,000（負值），則：\n變動百分比 = ($20,000 - (-$100,000)) ÷ (-$100,000) = -120%\n\n這個百分比沒有經濟意義（分母為負數），無法正確解讀，故「百分比變動比較」無法適用。\n\n其他方法仍可使用：\n(A) 共同比分析：各項目除以銷貨淨額，不受正負影響\n(B)(D) 絕對金額比較：直接比較金額差異，不涉及百分比",
  },
    {
    id: "fin-73",
    q:
      "財務報表係假設公司繼續經營，故應按何基礎入帳？",
    opts: [
      "(A) 變現價值",
      "(B) 淨變現價值",
      "(C) 售價",
      "(D) 取得成本",
    ],
    ans: 3,
    hint: "🔑 繼續經營假設 → 資產以取得成本（歷史成本）入帳，非清算價值",
    isCalc: false,
    explain:
      "繼續經營假設（Going Concern Assumption）：\n假設企業在可預見的未來將持續經營，而非即將清算。\n\n在此假設下：\n• 資產以「取得成本（歷史成本）」入帳，不必以清算價值或變現價值評估\n• 長期資產得以逐期折舊，而非一次性認列損失\n• 預付費用、遞延收入等跨期項目可合理存在\n\n若企業無法繼續經營（如面臨破產），則需改用清算基礎（Liquidation Basis），以變現價值評估資產。",
  },
    {
    id: "fin-74",
    q: "下列何者為靜態報表？",
    opts: [
      "(A) 資產負債表",
      "(B) 損益表",
      "(C) 股東權益變動表",
      "(D) 現金流量表",
    ],
    ans: 0,
    hint: "🔑 靜態報表反映某一特定「時點」的財務狀況快照",
    isCalc: false,
    explain:
      "四大財務報表的分類：\n\n📌 靜態報表（反映特定日期的狀況）：\n• 資產負債表（A）：「截至 OO 年 12 月 31 日」的資產、負債、股東權益 ✓\n\n📌 動態報表（反映某期間的變動）：\n• 損益表（B）：「OO 年 1 月 1 日至 12 月 31 日」的收入與費用\n• 股東權益變動表（C）：某期間股東權益的增減變動\n• 現金流量表（D）：某期間的現金流入與流出\n\n→ 答案 (A)",
  },
    {
    id: "fin-75",
    q: "下列何者不是主要財務報表的一種？",
    opts: [
      "(A) 現金流量表",
      "(B) 股東權益變動表",
      "(C) 資產變動表",
      "(D) 損益表",
    ],
    ans: 2,
    hint: "🔑 四大主要財務報表：資產負債表、損益表、股東權益變動表、現金流量表",
    isCalc: false,
    explain:
      "四大主要財務報表（依 IFRS 及 GAAP）：\n1. 資產負債表（Balance Sheet / Statement of Financial Position）\n2. 損益表（Income Statement / Statement of Comprehensive Income）\n3. 股東權益變動表（Statement of Changes in Equity）\n4. 現金流量表（Statement of Cash Flows）\n\n「資產變動表」（C）並非正式的主要財務報表，沒有這個報表的標準名稱。\n\n（另附：財務報表附註也是財務報表的重要組成部分，但不是獨立的「主要報表」）",
  }
  ],
  貨幣銀行學: [
    {
    id: "mon-01",
    q: "現行我國職司金融監督與管理之主要政府機構為：",
    opts: [
      "(A) 財政部",
      "(B) 中央銀行",
      "(C) 行政院金融監督管理委員會",
      "(D) 經濟部",
    ],
    ans: 2,
    hint: "🔑 台灣金融監管機構：金管會（FSC）於 2004 年成立，整合各項金融監理",
    isCalc: false,
    explain:
      "台灣金融監理架構：\n\n• 金融監督管理委員會（FSC）：主要金融監管機關，負責銀行、保險、證券期貨等金融業的監督管理\n\n• 中央銀行（CBC）：負責貨幣政策、外匯管理、發行通貨，不主管金融機構監理\n\n• 財政部：負責財政、稅務政策，金融監理職能已移轉至金管會\n\n→ 答案 (C)：行政院金融監督管理委員會",
  },
    {
    id: "mon-02",
    q: "費雪效果（Fisher effect）是指：",
    opts: [
      "(A) 當利率上升時，表示預期通貨膨脹增加",
      "(B) 當預期通貨膨脹增加時，利率會上升",
      "(C) 當所得增加時，利率會上升",
      "(D) 本國利率會隨國外利率上升而上升",
    ],
    ans: 1,
    hint: "🔑 費雪方程式：名目利率 ≈ 實質利率 + 預期通膨率",
    isCalc: false,
    explain:
      "費雪效果（Fisher Effect）：\n\n公式：名目利率（i）≈ 實質利率（r）+ 預期通貨膨脹率（π^e）\n\n核心概念：當預期通貨膨脹率上升時，放款人（儲蓄者）要求更高的名目利率以補償通膨對購買力的侵蝕，因此名目利率會等幅上升。\n\n例如：\n• 實質利率 = 2%，預期通膨 = 3% → 名目利率 = 5%\n• 若預期通膨升至 5% → 名目利率升至 7%\n\n→ 答案 (B)：預期通膨↑ → 利率↑",
  },
    {
    id: "mon-03",
    q: "契約型健身房突然停業，致使會員遭受損失。在不對稱訊息中，這是那種典型問題？",
    opts: [
      "(A) 逆選擇問題",
      "(B) 經濟規模問題",
      "(C) 道德風險問題",
      "(D) 交易成本問題",
    ],
    ans: 2,
    hint: "🔑 簽約後才發生的行為問題（故意倒閉收款）→ 道德風險",
    isCalc: false,
    explain:
      "資訊不對稱的兩大問題比較：\n\n• 逆向選擇（Adverse Selection）：締約「前」的資訊不對稱\n  例：借款人在申請貸款前隱瞞壞信用記錄\n\n• 道德風險（Moral Hazard）：締約「後」的行為問題\n  例：健身房收了長期會費後，趁會員不知情時突然停業跑路\n  → 會員（委託人）與健身房（代理人）之間存在資訊不對稱，健身房簽約後採取損害對方的行動\n\n→ 答案 (C)：道德風險（Post-contractual problem）",
  },
    {
    id: "mon-04",
    q: "下列何者不屬於我國銀行法所定義的專業銀行？",
    opts: [
      "(A) 交通銀行",
      "(B) 臺灣土地銀行",
      "(C) 臺灣銀行",
      "(D) 中國輸出入銀行",
    ],
    ans: 2,
    hint: "🔑 專業銀行有特定服務對象或業務範疇；臺灣銀行是一般商業銀行",
    isCalc: false,
    explain:
      "依台灣《銀行法》，專業銀行分類（各有特定服務領域）：\n• 工業銀行：如交通銀行（A），提供工業發展融資\n• 不動產信用銀行：如臺灣土地銀行（B），專注不動產融資\n• 輸出入銀行：如中國輸出入銀行（D），支援對外貿易融資\n• 農業信用銀行：如農業金庫\n\n臺灣銀行（C）雖為政府全額持股的國有銀行，但其業務性質為一般「商業銀行」，並非銀行法定義下的「專業銀行」，故答案為 (C)。",
  },
    {
    id: "mon-05",
    q: "銀行業的逾期放款是指未正常繳納本息至少逾幾個月？",
    opts: [
      "(A) 3 個月",
      "(B) 6 個月",
      "(C) 9 個月",
      "(D) 12 個月",
    ],
    ans: 0,
    hint: "🔑 台灣金管會規定：逾 3 個月（90 天）未正常還款 → 列為逾期放款（NPL）",
    isCalc: false,
    explain:
      "逾期放款（Non-Performing Loan, NPL）的定義：\n\n依台灣金融監理規定，借款人未依約履行還款義務（本金或利息）逾「3 個月（90 天）」以上，即列為逾期放款。\n\n相關影響：\n• 銀行需依比例提列備抵呆帳（提高壞帳準備）\n• 列為逾期放款後，通常停止認列利息收入\n• 逾期放款比率（NPL Ratio）= 逾期放款餘額 ÷ 總放款餘額，是衡量銀行資產品質的重要指標",
  },
    {
    id: "mon-06",
    q: "下列何者不是金融市場的主要功能？",
    opts: [
      "(A) 提供金融工具交易的場所",
      "(B) 提供交易者投機的場所",
      "(C) 促進投資活動的效率，提升經濟發展",
      "(D) 擔任資金需求者與供給者的橋樑",
    ],
    ans: 1,
    hint: "🔑 金融市場設計目的：資源配置、效率提升、資金橋樑；投機非主要功能",
    isCalc: false,
    explain:
      "金融市場的四大主要功能：\n1. 提供交易場所（A）：讓金融工具有效率地買賣\n2. 促進投資效率（C）：將資金引導到生產力最高的用途，推動經濟發展\n3. 連結供需雙方（D）：讓資金剩餘者（儲蓄者）與資金不足者（投資者/借款人）相互對接\n4. 風險分散：透過多樣化金融工具分散風險\n\n「提供投機場所」（B）雖然是市場客觀存在的現象，但並非設計金融市場的「主要目的」，投機活動對市場流動性有一定貢獻，但非核心功能。",
  },
    {
    id: "mon-07",
    q: "下列何者最能表明高通貨膨脹率與高失業率並存之現象？",
    opts: [
      "(A) 成本推動通貨膨脹",
      "(B) 需求拉動通貨膨脹",
      "(C) 資產性通貨膨脹",
      "(D) 停滯性通貨膨脹",
    ],
    ans: 3,
    hint: "🔑 Stagflation = Stagnation（停滯）+ Inflation（通膨）並存",
    isCalc: false,
    explain:
      "停滯性通貨膨脹（Stagflation）：\n• 定義：高通貨膨脹率與高失業率（經濟停滯）同時存在的異常現象\n• 歷史案例：1970 年代石油危機（OPEC 石油禁運導致油價暴漲）\n\n各類通膨比較：\n• 成本推動型（A）：供給衝擊造成的通膨（停滯性通膨的主因），但本身不等於停滯性通膨\n• 需求拉動型（B）：需求過旺拉高物價，此時就業通常較好，非停滯\n• 資產性（C）：資產價格上漲的通膨\n\n→ 最能「表明」高通膨 + 高失業並存的是「停滯性通膨（D）」",
  },
    {
    id: "mon-08",
    q: "當企業需要資金來進行投資，下列何種方式是最不好的融通管道？",
    opts: [
      "(A) 向銀行借錢",
      "(B) 發行股票",
      "(C) 向地下錢莊借貸",
      "(D) 發行債券",
    ],
    ans: 2,
    hint: "🔑 地下錢莊：無監管保護、高利貸、法律風險、人身安全風險",
    isCalc: false,
    explain:
      "正規融資管道比較：\n• 向銀行借款（A）：合法、利率受監管、程序清楚\n• 發行股票（B）：直接融資，無還款壓力，但需揭露資訊\n• 發行債券（D）：長期固定融資，成本可預測\n\n地下錢莊（C）是最差的融資方式：\n• 不受法律監管保護\n• 利率極高（常超過法定上限）\n• 逾期還款可能面臨暴力催討\n• 資金來源可能涉及洗錢或非法活動\n• 企業聲譽風險極大\n\n→ 答案 (C)：向地下錢莊借貸",
  },
    {
    id: "mon-09",
    q: "銀行的「營運風險」（operational risk）是指：",
    opts: [
      "(A) 借款人可能違約，以致無法履行債務的風險",
      "(B) 銀行變現能力或舉債能力不足，無法應付可能發生的資金需求之風險",
      "(C) 銀行內部控管不當、人員疏失或舞弊，造成銀行損失的風險",
      "(D) 契約不具法律效力或未正確記載，致使銀行資產減損或負債增加之風險",
    ],
    ans: 2,
    hint: "🔑 信用風險=違約；流動性風險=變現困難；營運風險=內部流程/人員/系統缺失",
    isCalc: false,
    explain:
      "銀行主要風險類型：\n\n• 信用風險（Credit Risk）(A)：借款人無法按時還款的風險\n\n• 流動性風險（Liquidity Risk）(B)：無法及時將資產變現或取得資金，以應付提款需求\n\n• 營運風險（Operational Risk）(C) ✓：\n  - 內部流程缺失（作業疏誤）\n  - 人員問題（疏失、舞弊、詐欺）\n  - 系統故障（IT 系統當機）\n  - 外部事件（天災、駭客攻擊）\n\n• 法律風險（Legal Risk）(D)：合約無效或法規不符造成的損失\n\n→ 答案 (C)：營運風險",
  },
    {
    id: "mon-10",
    q: "中華郵政公司吸收的郵政存簿儲金不可以運用於何種範圍？",
    opts: [
      "(A) 放款",
      "(B) 投資公債",
      "(C) 參與同業拆款市場",
      "(D) 轉存其他金融機構",
    ],
    ans: 0,
    hint: "🔑 郵政儲金依法不得直接對外放款（無銀行執照），主要轉存央行或投資公債",
    isCalc: false,
    explain:
      "中華郵政公司（前郵政儲金匯業局）的資金運用限制：\n\n依相關法規，郵政儲金的合法運用範圍：\n✓ 投資公債、國庫券等政府公債（B）\n✓ 參與同業拆款市場（C）\n✓ 轉存中央銀行或其他金融機構（D）\n\n✗ 不可直接辦理放款（A）：因中華郵政未取得銀行執照，若要放款，需透過已取得執照的銀行代辦，不得自行直接對客戶放款。",
  },
    {
    id: "mon-11",
    q: "中央銀行是「銀行的銀行」，是因：",
    opts: [
      "(A) 中央銀行獨享貨幣發行權力",
      "(B) 中央銀行辦理國庫收付、發售公債或國庫券",
      "(C) 中央銀行可對發生流動性危機的銀行提供資金融通",
      "(D) 中央銀行為維持金融穩定，負有監督銀行的責任",
    ],
    ans: 2,
    hint: "🔑 「銀行的銀行」= 最後貸款人（Lender of Last Resort）",
    isCalc: false,
    explain:
      "中央銀行的多重角色：\n• 「發鈔銀行」(A)：獨享貨幣發行權\n• 「政府的銀行」(B)：辦理國庫業務\n• 「銀行的監管者」(D)：金融監理職能（台灣現已移由金管會負責）\n\n「銀行的銀行」(C) 的核心含義：\n中央銀行扮演「最後貸款人」（Lender of Last Resort）角色——當商業銀行遭遇流動性危機（如存款擠兌），無法從市場取得資金時，央行可透過重貼現窗口緊急提供資金融通，防止個別銀行倒閉引發系統性金融危機。\n\n→ 答案 (C)",
  },
    {
    id: "mon-12",
    q: "若大眾持有現金的比率越高，則銀行可創造出來的存款：",
    opts: [
      "(A) 越多",
      "(B) 越少",
      "(C) 不變",
      "(D) 無法確定",
    ],
    ans: 1,
    hint: "🔑 現金持有率↑ → 回存銀行的資金↓ → 信用創造鏈資金基礎縮小 → 存款乘數效果↓",
    isCalc: false,
    explain:
      "貨幣乘數與現金持有率的關係：\n\n完整貨幣乘數公式（考慮民眾持有現金）：\nm = (1 + c) ÷ (rd + c)\n其中 c = 現金持有率，rd = 法定準備率\n\n當 c 上升：\n• 分子（1+c）增加，但分母（rd+c）增加更多\n• 整體貨幣乘數 m 下降\n• 銀行可創造的存款減少\n\n直觀理解：民眾把錢放口袋而非存進銀行，銀行沒有足夠資金可以放款，信用創造鏈斷裂，貨幣乘數效果縮小。\n\n→ 答案 (B)：越少",
  },
    {
    id: "mon-13",
    q: "股票在下列那個市場進行交易？",
    opts: [
      "(A) 貨幣市場",
      "(B) 資本市場",
      "(C) 期貨市場",
      "(D) 歐洲美元市場",
    ],
    ans: 1,
    hint: "🔑 資本市場：長期金融工具（股票、公司債）；貨幣市場：短期（1 年內）工具",
    isCalc: false,
    explain:
      "金融市場依期限的分類：\n\n• 貨幣市場（Money Market）：交易期限「1 年以內」的短期金融工具\n  例：國庫券、商業本票、可轉讓定存單、銀行承兌匯票\n\n• 資本市場（Capital Market）：交易期限「超過 1 年」的長期金融工具\n  例：股票（永久性）、公司債（5-30 年）、政府長期公債\n\n股票沒有到期日，屬於永久性的長期投資工具，在資本市場交易。\n\n→ 答案 (B)：資本市場",
  },
    {
    id: "mon-14",
    q: "當通貨膨脹發生時，所得重分配效果會使：",
    opts: [
      "(A) 政府稅收減少",
      "(B) 固定收入者實質工資下降",
      "(C) 債務人實質利率增加",
      "(D) 債權人實質利息所得增加",
    ],
    ans: 1,
    hint: "🔑 通膨受害者：固定薪資、固定利率債權人；受益者：債務人、持有實物資產者",
    isCalc: false,
    explain:
      "通貨膨脹的所得重分配效果：\n\n受益者（實質財富增加）：\n• 債務人（借款人）：所欠的錢「實質購買力」下降，相當於部分債務被通膨「侵蝕」\n• 持有實物資產者：房地產、黃金等隨物價上漲\n\n受害者（實質財富減少）：\n• 固定收入者 ✓ (B)：薪資固定，但物價上漲，實質工資（購買力）下降\n• 固定利率債權人：放款的名目利息固定，但實質利率（= 名目利率 - 通膨率）下降\n\n錯誤選項分析：\n(A) 通膨下，政府稅收反而「增加」（名目所得高，稅收也高）\n(C) 債務人實質利率「下降」，非增加\n(D) 債權人實質利息所得「減少」，非增加",
  },
    {
    id: "mon-15",
    q: "區分資本市場和貨幣市場的差異主要在於：",
    opts: [
      "(A) 金管會核准發行的數量",
      "(B) 證券發行期限的長短（貨幣市場是交易一年內到期證券）",
      "(C) 證券收益率",
      "(D) 以上皆是",
    ],
    ans: 1,
    hint: "🔑 市場分類的唯一標準：到期期限！1 年以內 = 貨幣市場；超過 1 年 = 資本市場",
    isCalc: false,
    explain:
      "貨幣市場 vs 資本市場的核心區別：\n\n關鍵分類標準：「發行（到期）期限」\n• 貨幣市場：到期期限 ≤ 1 年（短期）\n• 資本市場：到期期限 > 1 年（長期）\n\n收益率不是分類標準（C 錯誤）：長短期工具的收益率受市場供需影響，不是固定差異。\n金管會核准數量也非分類標準（A 錯誤）。\n\n→ 答案 (B)：發行期限長短是唯一的分類標準",
  },
    {
    id: "mon-16",
    q: "下列那些交易不屬於間接金融？",
    opts: [
      "(A) 民眾向商業銀行借款",
      "(B) 人壽保險公司購買股票",
      "(C) 公司藉發行公司債取得資金",
      "(D) 信託投資公司投資票券",
    ],
    ans: 2,
    hint: "🔑 直接金融：企業直接向市場發行有價證券融資，無金融中介機構參與",
    isCalc: false,
    explain:
      "直接金融 vs 間接金融：\n\n• 直接金融（Direct Finance）：資金需求者直接向市場發行有價證券（股票、債券）取得資金，資金供給者直接持有這些證券\n  → (C) 公司「直接」發行公司債取得資金 ✓ → 直接金融\n\n• 間接金融（Indirect Finance）：透過金融中介機構（銀行、保險公司等）連結資金供需雙方\n  → (A) 民眾→銀行→放款：透過銀行中介\n  → (B) 人壽保險公司購買股票：保費→保險公司→市場投資（中介機構代為投資）\n  → (D) 信託公司投資票券：委託人→信託公司→市場\n\n→ 答案 (C)：發行公司債屬直接金融，不屬於間接金融",
  },
    {
    id: "mon-17",
    q: "下列何者不屬於資本市場的金融工具？",
    opts: [
      "(A) 股票",
      "(B) 商業本票",
      "(C) 公司債",
      "(D) 不動產擔保債券",
    ],
    ans: 1,
    hint: "🔑 商業本票：期限通常在 90 天~1 年以內 → 貨幣市場工具",
    isCalc: false,
    explain:
      "各金融工具的市場歸屬：\n\n資本市場工具（期限 > 1 年）：\n✓ 股票（A）：永久性，無到期日\n✓ 公司債（C）：通常 3~30 年\n✓ 不動產擔保債券（D）：長期抵押債券\n\n貨幣市場工具（期限 ≤ 1 年）：\n✗ 商業本票（B）：企業發行的短期融資票券，通常 90 天~270 天，屬貨幣市場工具\n\n→ 答案 (B)：商業本票屬貨幣市場，不屬資本市場",
  },
    {
    id: "mon-18",
    q: "有關歐洲美元（Euro-dollar），下列敘述何者正確？",
    opts: [
      "(A) 歐洲美元是以歐元計值的",
      "(B) 是指流通在美國境外的美元",
      "(C) 其流通範圍僅以歐洲為限",
      "(D) 歐洲美元是存放在美國境外銀行的國內分行之存款",
    ],
    ans: 1,
    hint: "🔑 歐洲美元 ≠ 歐元；是存放在美國境外任何地方銀行的美元存款",
    isCalc: false,
    explain:
      "歐洲美元（Euro-dollar）的正確定義：\n\n• 「歐洲」指「境外（Offshore）」，非指歐洲大陸\n• 存放在「美國境外」銀行的美元存款，包括全球各地（倫敦、香港、新加坡等），不限歐洲\n• 與歐元（Euro, €）完全無關\n• 不受美國聯邦準備法規（如存款準備率等）的直接監管\n\n錯誤選項分析：\n(A) 歐洲美元是美元計值，不是歐元計值\n(C) 不僅限歐洲，全球境外均算\n(D) 是存放在美國境外的「外國」銀行，不是「國內分行」\n\n→ 答案 (B)",
  },
    {
    id: "mon-19",
    q: "何謂銀行業核定信用額度所使用的信用基礎 3C？",
    opts: [
      "(A) 品格、能力、資本",
      "(B) 借款戶聲譽、資金用途、還款財源",
      "(C) 品格、資金用途、資本",
      "(D) 借款戶聲譽、能力、資本",
    ],
    ans: 0,
    hint: "🔑 信用分析 3C：Character（品格）、Capacity（能力）、Capital（資本）",
    isCalc: false,
    explain:
      "銀行信用審核的 3C 原則（有時延伸為 5C）：\n\n① Character（品格）：借款人的誠信、還款意願、信用歷史\n② Capacity（能力）：借款人的還款能力（收入、現金流量）\n③ Capital（資本）：借款人的淨資產、自有資本\n\n延伸的 5C：\n④ Collateral（擔保品）：抵押品的價值\n⑤ Conditions（市場條件）：整體經濟環境\n\n→ 答案 (A)：品格（Character）、能力（Capacity）、資本（Capital）",
  },
    {
    id: "mon-20",
    q: "下列何種金融市場可以提供企業長期的資金融通？",
    opts: [
      "(A) 資本市場",
      "(B) 貨幣市場",
      "(C) 次級市場",
      "(D) 外匯市場",
    ],
    ans: 0,
    hint: "🔑 長期資金來源 = 資本市場（股票發行、長期債券）",
    isCalc: false,
    explain:
      "不同市場的資金融通期限：\n\n• 資本市場（A）✓：提供超過 1 年的「長期」資金，企業可透過：\n  - 發行股票（永久性股權資金）\n  - 發行長期公司債（5~30 年）\n\n• 貨幣市場（B）：只能提供 1 年以內的「短期」資金\n\n• 次級市場（C）：是已發行證券的「流通」市場，讓投資人轉手買賣，不直接提供企業新資金\n\n• 外匯市場（D）：從事不同貨幣兌換，主要用於匯率管理，不是企業融資的管道\n\n→ 答案 (A)：資本市場",
  },
    {
    id: "mon-21",
    q: "下列何者非屬台灣貨幣市場投資工具？",
    opts: [
      "(A) 國庫券",
      "(B) 銀行承兌匯票",
      "(C) 歐洲美元借款",
      "(D) 商業本票",
    ],
    ans: 2,
    hint: "🔑 歐洲美元是境外市場工具，非台灣國內貨幣市場的標準工具",
    isCalc: false,
    explain:
      "台灣貨幣市場（短期資金市場）的主要投資工具：\n✓ 國庫券（A）：政府發行的短期債務工具\n✓ 銀行承兌匯票（B）：銀行承兌的貿易票據\n✓ 商業本票（D）：企業發行的短期票券\n✓ 可轉讓定期存單（NCD）\n✓ 附買回協議（RP）\n\n歐洲美元借款（C）：\n• 是在美國境外（國際市場）取得的美元資金\n• 屬於「國際金融市場」的工具\n• 不屬於台灣國內貨幣市場的標準工具\n\n→ 答案 (C)",
  },
    {
    id: "mon-22",
    q: "根據我國中央銀行公布的貨幣定義，下列何者不屬於準貨幣的內容？",
    opts: [
      "(A) 貨幣市場共同基金",
      "(B) 郵政儲金",
      "(C) 支票存款",
      "(D) 外匯存款",
    ],
    ans: 2,
    hint: "🔑 台灣央行貨幣層次：M1A 含支票存款；準貨幣 = M2 - M1B",
    isCalc: false,
    explain:
      "台灣央行貨幣定義層次：\n\nM1A = 通貨淨額 + 支票存款 + 活期存款\nM1B = M1A + 活期儲蓄存款\nM2 = M1B + 準貨幣\n\n準貨幣（Quasi-money）= M2 - M1B，包含：\n✓ 定期存款及定期儲蓄存款\n✓ 郵政儲金（B）\n✓ 外匯存款（D）\n✓ 外國人持有之新台幣存款\n✓ 貨幣市場共同基金（A）\n\n支票存款（C）屬於 M1A（狹義貨幣），是「即期可用的交易貨幣」，非「準貨幣」。\n\n→ 答案 (C)：支票存款不屬於準貨幣",
  },
    {
    id: "mon-23",
    q: "資金剩餘者直接購買資金不足者所發行之有價證券（如：股票、政府公債、公司債、商業本票等），謂之：",
    opts: [
      "(A) 直接金融",
      "(B) 間接金融",
      "(C) 財務金融",
      "(D) 以上皆非",
    ],
    ans: 0,
    hint: "🔑 直接購買有價證券，無金融機構仲介 → 直接金融",
    isCalc: false,
    explain:
      "直接金融（Direct Finance）的定義：\n\n資金剩餘者（最終儲蓄者/投資人）直接購買資金不足者（企業、政府）所發行的有價證券，資金直接流通，不透過銀行等金融中介機構。\n\n特色：\n• 發行人可取得較低成本的資金（無中間成本）\n• 投資人直接承擔發行人的信用風險\n• 需要資本市場或貨幣市場作為交易平台\n\n對比間接金融：儲蓄者→銀行（收存款）→借款人（放款），銀行承擔信用風險並賺取利差。\n\n→ 答案 (A)：直接金融",
  },
    {
    id: "mon-24",
    q: "當名目利率下降而預期通貨膨脹率上升時，則實質利率：",
    opts: [
      "(A) 上升",
      "(B) 下降",
      "(C) 不變",
      "(D) 無法確定",
    ],
    ans: 1,
    hint: "🔑【計算題】費雪方程式：實質利率 ≈ 名目利率 − 預期通膨率，兩個方向都使實質利率下降",
    isCalc: true,
    explain:
      "【計算驗證—費雪方程式】\n\n實質利率（r）≈ 名目利率（i）− 預期通貨膨脹率（π^e）\n\n情境分析：\n• 名目利率 i 下降（例：從 5% 降到 4%）→ 分子減少\n• 預期通膨率 π^e 上升（例：從 1% 升到 3%）→ 要減去的數增加\n\n計算示範：\n• 變動前：r = 5% − 1% = 4%\n• 變動後：r = 4% − 3% = 1%\n\n兩個因素方向都使實質利率「下降」：\n① 名目利率↓ → 實質利率↓\n② 預期通膨↑ → 實質利率↓\n\n→ 答案 (B)：實質利率下降",
  },
    {
    id: "mon-25",
    q: "現行每一存款人在同一家金融機構的最高保額為存款本金是多少？",
    opts: [
      "(A) 新台幣 100 萬元",
      "(B) 新台幣 200 萬元",
      "(C) 新台幣 300 萬元",
      "(D) 新台幣 500 萬元",
    ],
    ans: 2,
    hint: "🔑 台灣存款保險：每人每機構最高保障新台幣 300 萬元（本金）",
    isCalc: false,
    explain:
      "台灣存款保險制度：\n\n主管機關：中央存款保險公司（CDIC）\n\n保障上限：每一存款人在同一金融機構的存款本金，最高保障「新台幣 300 萬元」。\n\n重要說明：\n• 超過 300 萬元的部分不在保障範圍\n• 保障對象：一般存款人（活期、定期、儲蓄存款等）\n• 「每人每機構」獨立計算（跨機構各自有 300 萬保障）\n• 2011 年前曾全額保障，後調整為現行定額保障\n\n→ 答案 (C)：新台幣 300 萬元",
  },
    {
    id: "mon-26",
    q: "假設銀行存款準備率維持 10%，不保留任何超額準備，且所有現金均回存至銀行體系。當中央銀行向某銀行買入公債 $100 萬元，則貨幣供給最多可增加：",
    opts: [
      "(A) $100 萬元",
      "(B) $500 萬元",
      "(C) $1,000 萬元",
      "(D) $2,000 萬元",
    ],
    ans: 2,
    hint: "🔑【計算題】貨幣乘數 = 1 ÷ 法定準備率；公開市場操作買入 $100 萬 → 注入基礎貨幣",
    isCalc: true,
    explain:
      "【計算步驟—信用乘數】\n\n① 貨幣乘數（Money Multiplier）\n   = 1 ÷ 法定準備率（rd）\n   = 1 ÷ 10%\n   = 10\n\n② 央行公開市場操作：\n   買入公債 $100 萬 → 向銀行注入 $100 萬基礎貨幣\n\n③ 信用創造過程（簡化）：\n   第 1 輪：存入 $100 萬，準備 $10 萬，放款 $90 萬\n   第 2 輪：存入 $90 萬，準備 $9 萬，放款 $81 萬\n   ……以此類推（等比級數）\n\n④ 貨幣供給最大增加量：\n   = $100 萬 × 貨幣乘數\n   = $100 萬 × 10\n   = $1,000 萬\n\n→ 答案 (C)：$1,000 萬元",
  },
    {
    id: "mon-27",
    q: "下列何者屬於貨幣市場？",
    opts: [
      "(A) 股票市場",
      "(B) 公債市場",
      "(C) 商業本票市場",
      "(D) 公司債市場",
    ],
    ans: 2,
    hint: "🔑 貨幣市場：1 年以內到期的短期工具；商業本票通常 90~270 天",
    isCalc: false,
    explain:
      "各市場工具的期限分類：\n\n貨幣市場（≤ 1 年）：\n✓ 商業本票（C）：90~270 天，屬短期票券\n✓ 國庫券、銀行承兌匯票、可轉讓定存單\n\n資本市場（> 1 年）：\n✗ 股票（A）：無到期日，長期股權\n✗ 公債市場（B）：政府長期公債，期限通常 2~30 年\n✗ 公司債市場（D）：企業長期債券，通常 3~30 年\n\n→ 答案 (C)：商業本票市場屬貨幣市場",
  },
    {
    id: "mon-28",
    q: "高通貨膨脹率與高失業率並存的現象，稱為：",
    opts: [
      "(A) 停滯性通貨膨脹",
      "(B) 惡性通貨膨脹",
      "(C) 出口型通貨膨脹",
      "(D) 開放性通貨膨脹",
    ],
    ans: 0,
    hint: "🔑 Stagflation = Stagnation（經濟停滯/高失業）+ Inflation（高通膨）並存",
    isCalc: false,
    explain:
      "停滯性通貨膨脹（Stagflation）：\n\n同時出現：\n• 高通貨膨脹率（物價持續上漲）\n• 高失業率（經濟成長停滯）\n\n歷史案例：1970 年代兩次石油危機\n• 石油輸出國組織（OPEC）限制石油出口 → 能源成本大幅上升\n• 企業生產成本增加 → 物價上漲（通膨）+ 裁員減產（失業率上升）\n• 形成「成本推動型」停滯性通膨\n\n其他選項：\n(B) 惡性通膨（Hyperinflation）：物價極速飆升（如德國 1920 年代）\n(C)(D) 非標準術語\n\n→ 答案 (A)",
  },
    {
    id: "mon-29",
    q: "下列何者不屬於我國貨幣總計數 M1A 中？",
    opts: [
      "(A) 通貨淨額",
      "(B) 支票存款",
      "(C) 活期存款",
      "(D) 活期儲蓄存款",
    ],
    ans: 3,
    hint: "🔑 M1A = 通貨 + 支票存款 + 活期存款；活期儲蓄存款屬 M1B",
    isCalc: false,
    explain:
      "台灣央行貨幣層次定義：\n\nM1A（最狹義貨幣）：\n✓ 通貨淨額（A）：流通在外的鈔票和硬幣\n✓ 支票存款（B）：可開支票的活期帳戶\n✓ 活期存款（C）：可隨時提款的一般活期帳戶\n\nM1B = M1A + 活期儲蓄存款（D）\n• 活期儲蓄存款通常有利息，且提款頻率限制較多\n• 不屬於 M1A，屬於 M1B 的增項\n\nM2 = M1B + 準貨幣（定期存款、郵儲、外匯存款等）\n\n→ 答案 (D)：活期儲蓄存款不屬於 M1A",
  },
    {
    id: "mon-30",
    q: "銀行的存款準備率中，為中央銀行所規定一定要保有的比率稱為：",
    opts: [
      "(A) 必要準備率",
      "(B) 臨界準備率",
      "(C) 資本適足率",
      "(D) 法定準備率",
    ],
    ans: 3,
    hint: "🔑 由央行以法令規定的存款準備比率 = 法定準備率（Required Reserve Ratio）",
    isCalc: false,
    explain:
      "存款準備金相關概念：\n\n• 法定準備率（Required Reserve Ratio）(D) ✓：\n  中央銀行依法規定，銀行對每單位存款必須保留的最低準備比率\n\n• 實際準備 = 法定準備 + 超額準備\n• 超額準備（Excess Reserves）：銀行自願保有超過法定要求的準備金\n\n• 資本適足率（C）：是銀行資本與風險加權資產的比率（巴塞爾協定要求），與準備率不同\n\n法定準備率是央行貨幣政策工具之一：\n• 提高法定準備率 → 銀行可放款資金減少 → 貨幣供給緊縮\n• 降低法定準備率 → 銀行可放款資金增加 → 貨幣供給擴張\n\n→ 答案 (D)",
  },
    {
    id: "mon-31",
    q: "近幾年委內瑞拉處在惡性通貨膨脹狀態：",
    opts: [
      "(A) 其貨幣價值維持不變",
      "(B) 其貨幣不再具有價值",
      "(C) 貨幣價值快速上升",
      "(D) 貨幣價值先上升後下降",
    ],
    ans: 1,
    hint: "🔑 惡性通貨膨脹（Hyperinflation）：物價暴漲 → 貨幣購買力急速喪失 → 貨幣幾乎無價值",
    isCalc: false,
    explain:
      "惡性通貨膨脹（Hyperinflation）的定義：\n通常指每月通膨率超過 50% 以上的極端通膨狀況。\n\n委內瑞拉案例（2016~2019 年）：\n• 通膨率一度高達 1,000,000%（百萬%）以上\n• 政府大量印鈔以支付財政赤字 → 貨幣供給爆增 → 物價暴漲\n• 一袋雜貨的價格可能在一天之內翻倍\n• 玻利瓦（Bolívar）貨幣幾近廢紙，民眾以美元或以物易物替代\n\n結果：貨幣幾乎「不再具有價值」（B），民眾喪失對貨幣的信任。\n\n→ 答案 (B)",
  },
    {
    id: "mon-32",
    q: "當銀行體系遭逢存款擠兌時，貨幣乘數會：",
    opts: [
      "(A) 變大",
      "(B) 不受影響",
      "(C) 變小",
      "(D) 無法確定",
    ],
    ans: 2,
    hint: "🔑 存款擠兌 → 民眾大量提領現金 → 現金持有率上升 → 乘數縮小",
    isCalc: false,
    explain:
      "【貨幣乘數與擠兌的關係】\n\n貨幣乘數公式：m = (1+c) ÷ (rd + c)\n其中 c = 現金持有率，rd = 法定準備率\n\n存款擠兌（Bank Run）發生時：\n1. 民眾大量從銀行提領現金（現金持有率 c 急劇上升）\n2. c 上升 → 分母增加幅度 > 分子增加幅度\n3. 貨幣乘數 m 縮小\n4. 貨幣供給收縮\n\n同時，銀行為應付提款需求，也會增持超額準備金（re 上升），進一步壓縮乘數。\n\n歷史教訓：1930 年代美國銀行大規模擠兌，貨幣乘數急劇下降，貨幣供給萎縮，加劇大蕭條。\n\n→ 答案 (C)：貨幣乘數變小",
  },
    {
    id: "mon-33",
    q: "下列何者不是貨幣市場的信用工具？",
    opts: [
      "(A) 商業本票",
      "(B) 公司股票",
      "(C) 銀行承兌匯票",
      "(D) 可轉讓定期存單",
    ],
    ans: 1,
    hint: "🔑 股票是長期股權工具，屬資本市場；貨幣市場只有短期信用工具",
    isCalc: false,
    explain:
      "貨幣市場信用工具（短期，≤ 1 年）：\n✓ 商業本票（A）：企業短期融資票券\n✓ 銀行承兌匯票（C）：銀行承兌的貿易信用票據\n✓ 可轉讓定期存單（D，NCD）：銀行發行可在市場轉讓的定存憑證\n✓ 國庫券：政府短期債務工具\n\n公司股票（B）：\n• 代表公司所有權（股權），非債務性信用工具\n• 無到期日（永久性）\n• 屬資本市場工具，在股票交易所交易\n• 不在貨幣市場流通\n\n→ 答案 (B)：公司股票不是貨幣市場信用工具",
  },
    {
    id: "mon-34",
    q: "金融市場包括下列何者？",
    opts: [
      "(A) 銀行市場、票券市場",
      "(B) 證券市場、期貨及金融衍生商品市場",
      "(C) 保險市場",
      "(D) 以上皆是",
    ],
    ans: 3,
    hint: "🔑 廣義金融市場包含：銀行、票券、證券、期貨衍生品、保險等各類市場",
    isCalc: false,
    explain:
      "廣義金融市場的組成：\n\n(A) 銀行市場：商業銀行、儲蓄銀行等的存放款業務\n    票券市場：商業本票、銀行承兌匯票等短期票券交易\n\n(B) 證券市場：股票、債券的初級市場（發行）和次級市場（流通）\n    期貨及衍生商品市場：期貨、選擇權、遠期契約、交換等\n\n(C) 保險市場：壽險、產險等保障型金融服務\n\n金融市場涵蓋以上所有類型，形成完整的金融體系。\n\n→ 答案 (D)：以上皆是",
  },
    {
    id: "mon-35",
    q: "下列何者屬於選擇性信用管制措施？",
    opts: [
      "(A) 道義說服",
      "(B) 保證金比率",
      "(C) 存款準備率政策",
      "(D) 公開市場操作",
    ],
    ans: 1,
    hint: "🔑 選擇性信用管制：針對「特定」用途或市場（非全面性）的信用管控手段",
    isCalc: false,
    explain:
      "貨幣政策工具的分類：\n\n一般性（全面性）工具—影響全體信用：\n• 公開市場操作（D）\n• 法定存款準備率（C）\n• 重貼現率\n\n選擇性（特定性）工具—針對特定部門或用途：\n✓ 保證金比率（B）：規定購買有價證券的融資比例上限（如：融資金額不得超過股票市值的某%），直接管控股市信用\n✓ 不動產放款規定：限制房地產貸款的成數（LTV）\n✓ 消費者信用管制：規定分期付款的頭期款比例\n\n道義說服（A）：屬於「非正式」溝通工具，非「選擇性信用管制」的正式分類。\n\n→ 答案 (B)：保證金比率",
  },
    {
    id: "mon-36",
    q: "若中央銀行決定調降國內利率 1 碼，請問利率的 1 碼是指多少？",
    opts: [
      "(A) 0.25",
      "(B) 0.25%",
      "(C) 0.10",
      "(D) 0.10%",
    ],
    ans: 1,
    hint: "🔑 台灣央行利率調整的基本單位：1 碼 = 0.25 個百分點（basis points × 25）",
    isCalc: false,
    explain:
      "台灣利率調整的計量單位：\n\n• 1 碼（一碼）= 0.25%（即 25 個基點，25 bps）\n• 1 基點（basis point）= 0.01%\n• 4 碼 = 1%\n\n使用「碼」的情境：\n• 央行宣布升息「半碼」= 升息 0.125%\n• 央行宣布降息「2 碼」= 降息 0.5%\n\n注意：(A) 0.25 是數字但沒有「%」單位，表達不完整；正確應為 (B) 0.25%。\n\n→ 答案 (B)：0.25%",
  },
    {
    id: "mon-37",
    q: "下列何者是商業銀行資產負債表中之資產？",
    opts: [
      "(A) 個人在商業銀行之定期存款",
      "(B) 企業在商業銀行之支票存款",
      "(C) 政府在商業銀行之存款",
      "(D) 商業銀行對個人之放款",
    ],
    ans: 3,
    hint: "🔑 銀行觀點：存款 = 負債（銀行欠存款人）；放款 = 資產（存款人欠銀行）",
    isCalc: false,
    explain:
      "從銀行的角度看資產負債表（銀行觀點很關鍵！）：\n\n銀行負債（銀行欠別人的錢）：\n✗ 個人定期存款（A）：銀行欠存款人的錢 → 負債\n✗ 企業支票存款（B）：銀行欠企業的錢 → 負債\n✗ 政府存款（C）：銀行欠政府的錢 → 負債\n\n銀行資產（別人欠銀行的錢）：\n✓ 商業銀行對個人之放款（D）：客戶欠銀行的貸款 → 資產 ✓\n其他銀行資產：準備金存款、投資的政府公債等\n\n→ 答案 (D)：放款是銀行的資產",
  },
    {
    id: "mon-38",
    q: "下列各項貨幣政策工具中，最重要也是先進國家最常使用的是何者？",
    opts: [
      "(A) 貼現政策",
      "(B) 存款準備率政策",
      "(C) 公開市場操作",
      "(D) 放款準備政策",
    ],
    ans: 2,
    hint: "🔑 公開市場操作：靈活、精確、可隨時逆向操作，是現代央行最核心的工具",
    isCalc: false,
    explain:
      "三大傳統貨幣政策工具的比較：\n\n① 公開市場操作（C）✓ 最重要：\n• 央行買賣政府債券，調整銀行準備金\n• 優點：靈活（隨時可操作）、精確（可控制金額）、可逆向（買/賣皆可）\n• 美聯準、歐央行均以此為主要工具\n\n② 法定準備率（B）：\n• 調整幅度有限（影響巨大），不常頻繁使用\n• 對小型銀行影響不均等\n\n③ 貼現（重貼現）政策（A）：\n• 調整銀行向央行借款的利率\n• 被動式工具（需等銀行主動來借），效果難以精確控制\n\n→ 答案 (C)：公開市場操作",
  },
    {
    id: "mon-39",
    q: "在總合供需模型中，當中央銀行提高存款準備率時，則：",
    opts: [
      "(A) 總合需求曲線右移",
      "(B) 總合需求曲線左移",
      "(C) 總合供給曲線右移",
      "(D) 總合供給曲線左移",
    ],
    ans: 1,
    hint: "🔑 提高準備率 → 貨幣供給↓ → 利率↑ → 投資↓ → 總需求↓ → AD 曲線左移",
    isCalc: false,
    explain:
      "【政策傳導機制】\n\n提高法定存款準備率（緊縮性貨幣政策）：\n\n① 銀行法定準備↑ → 可放款資金↓\n② 貨幣供給（M）減少\n③ 利率（i）上升\n④ 企業投資（I）減少、消費借貸（C）減少\n⑤ 總合需求（AD = C+I+G+NX）下降\n⑥ 總合需求曲線（AD Curve）向左移動\n\n結果：在物價水準不變下，均衡產出減少，有助於抑制通膨。\n\n注意：此政策影響「需求面」，不影響「供給曲線」（C、D 錯）。\n\n→ 答案 (B)：AD 曲線左移",
  },
    {
    id: "mon-40",
    q: "其他條件不變，當中央銀行提高法定準備率時，則：",
    opts: [
      "(A) 銀行必須增加所持有的準備金",
      "(B) 銀行必須減少所持有的準備金",
      "(C) 銀行必須持有的準備金不變",
      "(D) 以上情況都有可能",
    ],
    ans: 0,
    hint: "🔑 法定準備率↑ → 每單位存款必須保留的準備金比例提高 → 準備金需求增加",
    isCalc: false,
    explain:
      "法定準備率調升的直接效果：\n\n法定準備金 = 存款餘額 × 法定準備率\n\n若法定準備率從 10% 調升至 15%：\n• 假設存款 $1,000 萬\n• 原準備金需求：$1,000 萬 × 10% = $100 萬\n• 新準備金需求：$1,000 萬 × 15% = $150 萬\n• 銀行必須「增加」持有 $50 萬準備金\n\n對銀行的影響：\n• 增持準備金 → 可放款資金減少 $50 萬\n• 信用創造能力下降\n• 市場資金緊縮，利率有上升壓力\n\n→ 答案 (A)：銀行必須增加所持有的準備金",
  },
    {
    id: "mon-41",
    q: "銀行資產負債表中負債的部分並不包括下列那一項？",
    opts: [
      "(A) 企業及個人存款",
      "(B) 政府存款",
      "(C) 政府債券",
      "(D) 進口結匯保證金",
    ],
    ans: 2,
    hint: "🔑 政府債券是銀行「買入」的投資 → 銀行資產；存款是銀行欠的錢 → 負債",
    isCalc: false,
    explain:
      "銀行資產負債表的結構：\n\n銀行負債（銀行欠別人的錢）：\n✓ 企業及個人存款（A）：銀行欠存款人\n✓ 政府存款（B）：銀行代政府保管\n✓ 進口結匯保證金（D）：客戶向銀行繳交的保證金，銀行有退還義務\n✓ 發行的金融債券、同業借款等\n\n銀行資產（銀行擁有的，別人欠銀行的）：\n✗ 政府債券（C）：銀行「買入」政府公債，是銀行的投資（資產）\n\n關鍵邏輯：銀行「持有」政府債券，代表銀行是債權人，政府欠銀行錢 → 銀行資產，非負債。\n\n→ 答案 (C)：政府債券",
  },
    {
    id: "mon-42",
    q: "外匯市場外資大量撤離，本國貨幣大幅貶值的現象，是：",
    opts: [
      "(A) 銀行危機",
      "(B) 貨幣危機",
      "(C) 債務危機",
      "(D) 以上皆非",
    ],
    ans: 1,
    hint: "🔑 外資撤離 → 本幣貶值、外匯存底大幅下降 → 貨幣危機（Currency Crisis）",
    isCalc: false,
    explain:
      "金融危機類型分類：\n\n• 貨幣危機（Currency Crisis）(B) ✓：\n  外資大量撤離 → 本幣拋售 → 匯率急貶 → 外匯存底耗盡\n  歷史案例：1997 年亞洲金融風暴（泰銖、韓元、印尼盾大幅貶值）\n\n• 銀行危機（A）：銀行大規模倒閉或不良資產激增\n\n• 債務危機（C）：政府或企業無法償還外債\n  例：2010 年歐洲主權債務危機（希臘、西班牙等）\n\n• 系統性金融危機：多種危機同時發生（如 2008 年全球金融海嘯）\n\n→ 答案 (B)：貨幣危機",
  },
    {
    id: "mon-43",
    q: "在各種存款中，何者之存款準備率最高？",
    opts: [
      "(A) 儲蓄存款",
      "(B) 定期存款",
      "(C) 支票存款及活期存款",
      "(D) 以上皆非",
    ],
    ans: 2,
    hint: "🔑 流動性越高的存款，準備率越高；支票與活期存款流動性最高",
    isCalc: false,
    explain:
      "各類存款的法定準備率高低（從高到低）：\n\n1. 支票存款 + 活期存款（C）✓ 最高：\n   • 流動性最高，存款人隨時可提領\n   • 銀行需保留較高準備金以應付隨時提款\n\n2. 活期儲蓄存款：\n   • 流動性次高，準備率稍低\n\n3. 定期存款（B）：\n   • 有固定到期日，提款受限制，流動性較低\n   • 法定準備率最低\n\n4. 儲蓄存款（A）：\n   • 介於定期與活期之間\n\n原則：流動性越高（越易提款）→ 準備率越高（銀行需保留更多以應付提款）\n\n→ 答案 (C)",
  },
    {
    id: "mon-44",
    q: "依據購買力平價說主張，若一國的物價水準相對於另一國上升時，則：",
    opts: [
      "(A) 該國的貨幣會升值，而另一國的貨幣也升值",
      "(B) 該國的貨幣會升值，而另一國的貨幣會貶值",
      "(C) 該國的貨幣會貶值，而另一國的貨幣也貶值",
      "(D) 該國的貨幣會貶值，而另一國的貨幣會升值",
    ],
    ans: 3,
    hint: "🔑 PPP：物價↑ → 出口競爭力↓ → 本幣貶值；另一國相對便宜 → 他國幣升值",
    isCalc: false,
    explain:
      "購買力平價說（Purchasing Power Parity, PPP）：\n\n核心邏輯：各國相同商品的價格，以同一貨幣衡量，長期應趨於相等。\n\n情境分析：\n若甲國物價相對於乙國上升：\n① 甲國商品變貴 → 出口競爭力下降\n② 進口增加、出口減少 → 外匯需求增加、供給減少\n③ 甲國貨幣「貶值」\n④ 相對地，乙國商品更便宜 → 乙國出口增加\n⑤ 乙國貨幣「升值」\n\n實際應用：大麥克指數（The Economist）即基於 PPP 概念，比較各國貨幣的相對購買力。\n\n→ 答案 (D)：甲國貶值，另一國升值",
  },
    {
    id: "mon-45",
    q: "商業銀行信用創造的最大可能為：",
    opts: [
      "(A) 貨幣供給量",
      "(B) 活期存款的總額",
      "(C) 金融體系內的存款總額",
      "(D) 存款準備率的倒數乘以原始存款",
    ],
    ans: 3,
    hint: "🔑【計算題】最大信用創造 = 原始存款 × 貨幣乘數 = 原始存款 ÷ 法定準備率",
    isCalc: true,
    explain:
      "【信用創造最大值公式】\n\n假設：\n• 銀行不保留超額準備\n• 所有提領的現金均回存銀行\n\n貨幣乘數 = 1 ÷ 法定準備率（rd）\n\n信用創造的最大值 = 原始存款 × (1 ÷ rd)\n= 原始存款 ÷ 法定準備率\n\n計算示範：\n若原始存款 = $100 萬，rd = 10%\n最大信用創造 = $100 萬 ÷ 10% = $1,000 萬\n\n即存款準備率的倒數（10 倍）乘以原始存款（$100 萬）= $1,000 萬。\n\n→ 答案 (D)：存款準備率的倒數乘以原始存款",
  },
    {
    id: "mon-46",
    q: "下列何種公司是證券市場中「融資」與「融券」業務之主要經營者？",
    opts: [
      "(A) 證券投資信託公司",
      "(B) 證券投資顧問公司",
      "(C) 票券金融公司",
      "(D) 證券金融公司",
    ],
    ans: 3,
    hint: "🔑 融資（借錢買股票）+ 融券（借股票放空）= 證券金融公司（俗稱「票金」）",
    isCalc: false,
    explain:
      "台灣證券市場主要機構：\n\n• 證券投資信託公司（A）：發行共同基金，代為管理投資組合（如台灣大型基金公司）\n\n• 證券投資顧問公司（B）：提供投資建議、研究報告，不直接操作資金\n\n• 票券金融公司（C）：從事短期票券（商業本票等）的發行、交易、承銷\n\n• 證券金融公司（D）✓：\n  - 融資（Margin Loan）：提供資金給投資人購買股票（投資人先付 60%，金融公司貸 40%）\n  - 融券（Short Selling）：出借股票給投資人進行放空操作\n  - 台灣唯一的綜合性「融資融券」機構為台灣證券金融公司（簡稱台灣證金）\n\n→ 答案 (D)",
  },
    {
    id: "mon-47",
    q: "成本推動型的停滯性通貨膨脹（Cost-pushed Stagnation）是根源於：",
    opts: [
      "(A) 名目貨幣供給的減少",
      "(B) 名目貨幣供給的增加",
      "(C) 生產力的降低",
      "(D) 生產力的提高",
    ],
    ans: 2,
    hint: "🔑 成本推動型：供給面衝擊 → 生產力↓ or 成本↑ → 廠商縮減產量並提高售價",
    isCalc: false,
    explain:
      "成本推動型停滯性通膨（Cost-pushed Stagflation）：\n\n根源：供給面的負面衝擊\n✓ 生產力降低（C）：每單位投入產出減少 → 單位成本上升\n✓ 原物料成本上漲（如石油危機）\n✓ 工資暴漲超過生產力增長\n\n傳導機制：\n生產力↓ → 生產成本↑ → 短期總供給（SRAS）曲線左移 → 物價↑ + 產量↓（失業率↑）→ 停滯性通膨\n\n貨幣供給（A、B）的變化影響「需求面」：\n• 貨幣供給增加 → 需求拉動型通膨（物價↑ + 產量↑）\n• 貨幣供給減少 → 通縮，非停滯性通膨的原因\n\n→ 答案 (C)：生產力降低",
  },
    {
    id: "mon-48",
    q: "中央銀行透過下列何政策工具扮演「最後貸款者」的角色？",
    opts: [
      "(A) 重貼現政策",
      "(B) 準備金政策",
      "(C) 公開市場買進政策",
      "(D) 公開市場賣出政策",
    ],
    ans: 0,
    hint: "🔑 最後貸款者 = 銀行危機時的緊急資金窗口 = 重貼現窗口",
    isCalc: false,
    explain:
      "最後貸款者（Lender of Last Resort）機制：\n\n重貼現政策（Discount Policy）(A) ✓：\n• 央行提供「重貼現窗口」（Discount Window）\n• 當商業銀行面臨流動性危機（資金短缺）時，可以合格票據或擔保品向央行申請緊急融通\n• 央行以「重貼現率」收取利息，提供最後資金援助\n• 防止個別銀行流動性危機演變成系統性金融崩潰\n\n其他工具的角色：\n• 公開市場操作（C、D）：主動調控市場整體流動性，非針對個別危機銀行\n• 法定準備率（B）：預防性工具，不能立即解決危機\n\n→ 答案 (A)：重貼現政策",
  },
    {
    id: "mon-49",
    q: "下列何者不是投資銀行公開承銷（Initial Public Offering）的方式？",
    opts: [
      "(A) 包銷（Firm Commitment）",
      "(B) 代銷（Best Offer）",
      "(C) 足額發行（All or None）",
      "(D) 私下募集（Private Placement）",
    ],
    ans: 3,
    hint: "🔑 IPO 是公開承銷，私下募集（Private Placement）是非公開方式，性質相反",
    isCalc: false,
    explain:
      "IPO（Initial Public Offering）公開承銷方式：\n\n✓ 包銷（Firm Commitment）(A)：\n  承銷商以固定價格買下全部股票，承擔所有賣不出去的風險\n\n✓ 代銷（Best Effort）(B)：\n  承銷商盡力銷售，賣不完部分退還發行人，風險由發行人承擔\n\n✓ 足額發行（All or None）(C)：\n  若認購未達一定數量，則取消本次發行，全額退款\n\n✗ 私下募集（Private Placement）(D)：\n  向少數特定投資人（機構法人等）非公開募資，不屬於 IPO（公開承銷）的方式，性質完全不同\n\n→ 答案 (D)：私下募集不是 IPO 的方式",
  },
    {
    id: "mon-50",
    q: "下列何項變化會讓總需求曲線往右移？（其他條件不變）",
    opts: [
      "(A) 物價水準上升",
      "(B) 政府採購增加",
      "(C) 貨幣供給減少",
      "(D) 政府稅收增加",
    ],
    ans: 1,
    hint: "🔑 AD 右移 = 總需求增加；政府採購（G）↑ 直接增加 AD = C+I+G+NX",
    isCalc: false,
    explain:
      "總合需求曲線（AD Curve）移動的因素：\n\nAD 右移（總需求增加）的非價格因素：\n✓ 政府採購（G）增加（B）：直接增加 AD 的 G 部分 ✓\n✓ 消費增加（稅收減少、消費信心提升）\n✓ 投資增加（利率下降、預期獲利↑）\n✓ 淨出口增加（匯率貶值）\n✓ 貨幣供給增加 → 利率↓ → 投資↑ → AD↑\n\n不移動 AD 曲線（是 AD 曲線「上面」的點移動）：\n✗ 物價水準上升（A）：這是沿著 AD 曲線的移動，不是曲線「移動」\n\nAD 左移的因素：\n✗ 貨幣供給減少（C）：M↓ → 利率↑ → 投資↓ → AD 左移\n✗ 政府稅收增加（D）：民間可支配所得↓ → 消費↓ → AD 左移\n\n→ 答案 (B)：政府採購增加",
  },
    {
    id: "mon-51",
    q: "貨幣數量學說（quantity theory of money）認為：",
    opts: [
      "(A) 貨幣流通速度會緩慢增加",
      "(B) 實質所得是完全由貨幣數量多寡所決定",
      "(C) 貨幣需求量會受到利率的影響",
      "(D) 物價水準完全是由貨幣供給量多寡所決定",
    ],
    ans: 3,
    hint: "🔑 費雪交換方程式 MV = PQ；V、Q 固定時，M↑ → P 等比例↑",
    isCalc: false,
    explain:
      "貨幣數量學說（Quantity Theory of Money）：\n\n費雪交換方程式：M × V = P × Q\n• M：貨幣供給量\n• V：貨幣流通速度（假設固定）\n• P：物價水準\n• Q：實質產出（假設固定，處於充分就業）\n\n推論：在 V 和 Q 固定的假設下，\n→ M 增加一倍 → P 也增加一倍\n→ 「物價水準完全由貨幣供給量決定」（D）✓\n\n錯誤選項分析：\n(A) 古典學派假設 V 固定不變，非緩慢增加\n(B) 實質所得由生產力、資本等供給面因素決定，不由貨幣量決定\n(C) 凱因斯認為貨幣需求受利率影響，但古典貨幣數量學說不強調此點\n\n→ 答案 (D)",
  },
    {
    id: "mon-52",
    q: "當中央銀行進行公開市場買入操作時（各種有價證券），下列何種變動正確？",
    opts: [
      "(A) 準備金減少",
      "(B) 貨幣供給額減少",
      "(C) 貨幣基數增加（貨幣供給增加）",
      "(D) 市場利率上漲",
    ],
    ans: 2,
    hint: "🔑 央行買入公債 → 支付現金給賣方 → 銀行準備金增加 → 貨幣基數增加",
    isCalc: false,
    explain:
      "公開市場「買入」操作（Expansionary OMO）：\n\n操作流程：\n① 央行在公開市場買入政府公債\n② 央行支付現金給賣出公債的銀行或投資人\n③ 銀行準備金增加（貨幣基數 MB 增加）\n④ 透過信用乘數，貨幣供給（M1B、M2）增加\n⑤ 市場資金充裕 → 利率下降（非上漲）\n\n各選項分析：\n(A) 準備金減少 ✗（應為增加）\n(B) 貨幣供給減少 ✗（應為增加）\n(C) 貨幣基數增加 ✓\n(D) 市場利率上漲 ✗（應為下降）\n\n→ 答案 (C)",
  },
    {
    id: "mon-53",
    q: "下列何項交易會讓一國之經常帳（current account）餘額增加？",
    opts: [
      "(A) 本國人赴外國觀光",
      "(B) 本國人赴外國留學",
      "(C) 外國人授權技術給本國人，本國人支付權利金",
      "(D) 本國出口產品增加",
    ],
    ans: 3,
    hint: "🔑 經常帳順差：出口 > 進口；出口增加 → 外匯流入 → 經常帳餘額增加",
    isCalc: false,
    explain:
      "國際收支帳（Balance of Payments）— 經常帳（Current Account）：\n\n經常帳組成：\n• 商品貿易帳：出口 - 進口\n• 服務帳：旅遊、教育、技術授權費等\n• 移轉收支：匯款等\n\n各選項分析（對經常帳的影響）：\n(A) 本國人出國觀光：支付外匯（服務進口）→ 經常帳「減少」\n(B) 本國人出國留學：支付外匯（服務進口）→ 經常帳「減少」\n(C) 支付技術授權金給外國：外匯流出 → 服務進口 → 經常帳「減少」\n(D) 本國出口增加 ✓：外匯流入 → 貿易順差增加 → 經常帳餘額「增加」\n\n→ 答案 (D)",
  },
    {
    id: "mon-54",
    q: "下列何項不是金融危機？",
    opts: [
      "(A) 銀行擠兌危機",
      "(B) 石油危機",
      "(C) 政府債務危機",
      "(D) 資本市場危機",
    ],
    ans: 1,
    hint: "🔑 金融危機 = 金融體系的危機；石油危機是能源/實體經濟危機，非金融體系危機",
    isCalc: false,
    explain:
      "金融危機（Financial Crisis）的定義：\n金融體系（銀行、資本市場、貨幣）因大規模損失或信心崩潰而無法正常運作的狀況。\n\n各選項分析：\n✓ 銀行擠兌危機（A）：存款人大量提款，銀行流動性崩潰 → 金融危機\n✓ 政府債務危機（C）：政府無力償還公債（如希臘債務危機）→ 金融危機\n✓ 資本市場危機（D）：股市、債市崩盤（如 1929 年股市崩盤）→ 金融危機\n\n✗ 石油危機（B）：\n• 1973 年、1979 年 OPEC 的石油禁運/減產\n• 影響的是「實體經濟」（能源成本、通膨、衰退）\n• 屬於「供給衝擊（Supply Shock）」，不是金融體系本身的危機\n\n→ 答案 (B)：石油危機不是金融危機",
  },
    {
    id: "mon-55",
    q: "下列何項金融機構會影響一國之貨幣供給數量？",
    opts: [
      "(A) 人壽保險公司",
      "(B) 產物保險公司",
      "(C) 銀行",
      "(D) 共同基金",
    ],
    ans: 2,
    hint: "🔑 能創造存款貨幣（信用創造）的金融機構 = 銀行體系",
    isCalc: false,
    explain:
      "哪些機構影響貨幣供給：\n\n✓ 銀行（C）：\n• 透過「存款→放款→再存款」的信用創造機制，直接創造（增加）或銷毀（減少）存款貨幣\n• 銀行是 M1B、M2 的核心創造主體\n\n✗ 人壽保險公司（A）、產物保險公司（B）：\n• 收取保費後主要投資股票、債券等，資金在金融體系間移轉，但不創造「新的」貨幣\n\n✗ 共同基金（D）：\n• 集中資金投資，但購買股票或債券不增加貨幣供給（只是貨幣的移轉）\n• 貨幣市場基金可能被納入 M2，但本身不創造貨幣\n\n→ 答案 (C)：銀行",
  },
    {
    id: "mon-56",
    q: "貨幣為一種，而所得為一種？",
    opts: [
      "(A) 存量；流量",
      "(B) 流量；存量",
      "(C) 存量；固定值",
      "(D) 固定值；存量",
    ],
    ans: 0,
    hint: "🔑 存量（Stock）：某時點的量；流量（Flow）：某期間的量",
    isCalc: false,
    explain:
      "存量（Stock）vs 流量（Flow）：\n\n• 存量（Stock）：在「某一特定時點」衡量的數量\n  → 貨幣供給（M）：某一天的貨幣總量，如「2024 年 12 月 31 日的 M2 = 55 兆元」\n  → 其他例子：資本存量、財富、人口\n\n• 流量（Flow）：在「某一段期間」內累積的數量\n  → 所得（Y）：某一年賺到的收入，如「2024 年 GDP = 24 兆元」\n  → 其他例子：投資、消費、儲蓄、折舊\n\n→ 答案 (A)：貨幣是存量；所得是流量",
  },
    {
    id: "mon-57",
    q: "下列何者是占商業銀行最大比例之資產？",
    opts: [
      "(A) 準備",
      "(B) 應收現金",
      "(C) 政府公債",
      "(D) 放款",
    ],
    ans: 3,
    hint: "🔑 銀行最主要的收益來源 = 放款利息；放款自然是最大資產",
    isCalc: false,
    explain:
      "商業銀行資產結構（以台灣一般商業銀行為例，依比例高至低）：\n\n1. 放款（Loans）(D) ✓ — 比例最大：\n   企業貸款、房屋貸款、消費貸款等，是銀行最主要的收益來源（利息收入）\n\n2. 投資（政府公債等）：次大資產，屬次要流動性儲備\n\n3. 準備金（A）：\n   法定準備金 + 超額準備，僅占很小比例（通常僅幾%）\n\n4. 應收現金（B）：\n   在途票據等，金額較小\n\n銀行的核心業務模式：「吸收存款→發放放款→賺取利差」，放款占資產的比例通常超過 60%。\n\n→ 答案 (D)：放款",
  },
    {
    id: "mon-58",
    q: "下列何項金融工具是在集中市場交易？",
    opts: [
      "(A) 外匯",
      "(B) 政府公債",
      "(C) 商業本票",
      "(D) 上市公司股票",
    ],
    ans: 3,
    hint: "🔑 集中市場（Exchange）= 有組織的交易所，規格化交易；OTC = 分散議價交易",
    isCalc: false,
    explain:
      "集中市場（Exchange） vs 店頭市場（OTC / Over-the-Counter）：\n\n集中市場特點：\n• 有固定場所（交易所）\n• 規格化合約\n• 公開競價\n• 有清算機構保障\n\n✓ 上市公司股票（D）：在台灣證券交易所（TWSE）或興櫃市場集中交易 ✓\n✓ 期貨：在台灣期貨交易所交易\n\n店頭市場（OTC）交易（議價）：\n✗ 外匯（A）：全球外匯市場是分散的電子議價市場，無集中交易所\n✗ 政府公債（B）：主要在 OTC 市場（債券交易商間議價）\n✗ 商業本票（C）：在票券市場 OTC 交易\n\n→ 答案 (D)：上市公司股票",
  },
    {
    id: "mon-59",
    q: "16 世紀的英國鑄幣局長格萊欣（Gresham）觀察到，消費者會保留儲存成色高的貨幣，將成色較低的貨幣進入市場交易，使得在民間流通的多數為劣幣，良幣則較少見於世。這種現象稱為：",
    opts: [
      "(A) 貨幣數量學說",
      "(B) 貨幣中立性說",
      "(C) 劣幣驅逐良幣",
      "(D) 金幣本位制",
    ],
    ans: 2,
    hint: "🔑 格萊欣法則（Gresham's Law）：「Bad money drives out good」",
    isCalc: false,
    explain:
      "格萊欣法則（Gresham's Law）：劣幣驅逐良幣\n\n核心概念：\n• 當市場上同時流通「名義價值相同、但實質含量不同」的貨幣時\n• 人們傾向保存（囤積）成色高（實質價值高）的良幣\n• 把成色低（實質價值低）的劣幣流通出去\n• 結果：市場上流通的以劣幣為主，良幣消失\n\n現代應用：\n• 通貨膨脹嚴重時，民眾用本國貨幣（劣幣）消費，保留美元（良幣）→ 美元化現象\n• 委內瑞拉、辛巴威等惡性通膨國家均出現此現象\n\n→ 答案 (C)：劣幣驅逐良幣",
  },
    {
    id: "mon-60",
    q: "中央銀行最常使用的貨幣政策工具是：",
    opts: [
      "(A) 公開市場操作",
      "(B) 貼放利率的調整",
      "(C) 存款準備率的調整",
      "(D) 道德說服",
    ],
    ans: 0,
    hint: "🔑 靈活、精確、可頻繁使用 → 公開市場操作是現代央行最主要工具",
    isCalc: false,
    explain:
      "各貨幣政策工具的使用頻率比較：\n\n✓ 公開市場操作（A）— 最常使用：\n• 可以每日進行，精確控制注入/抽走的資金量\n• 效果快速（T+1 即生效）\n• 方向靈活（可買可賣，可擴張可緊縮）\n• 美聯準、歐央行、台灣央行均以此為主\n\n✗ 貼放利率（B）：被動性（等銀行來申請），不能主動控制量\n\n✗ 存款準備率（C）：調整幅度大、影響深遠，通常只在特殊情況才調整，非頻繁工具\n\n✗ 道德說服（D）：非正式工具，效果不確定，不是常規工具\n\n→ 答案 (A)：公開市場操作",
  },
    {
    id: "mon-61",
    q: "下列何者不屬於存款貨幣機構？",
    opts: [
      "(A) 商業銀行",
      "(B) 中小企業銀行",
      "(C) 信用合作社",
      "(D) 郵政儲金匯業局",
    ],
    ans: 3,
    hint: "🔑 存款貨幣機構：可吸收存款並創造貨幣的金融機構；郵政儲金局非銀行機構",
    isCalc: false,
    explain:
      "存款貨幣機構（Depository Institutions）：\n以吸收存款為主要資金來源、具有信用創造功能的金融機構。\n\n屬於存款貨幣機構：\n✓ 商業銀行（A）：最典型的存款貨幣機構\n✓ 中小企業銀行（B）：專業銀行，仍可吸收存款並放款\n✓ 信用合作社（C）：地區性合作金融機構，可吸收存款\n\n不屬於存款貨幣機構：\n✗ 郵政儲金匯業局（D）（現中華郵政）：\n• 雖然吸收大量郵政儲金\n• 但不具銀行執照，不能自行放款創造貨幣\n• 吸收的儲金需轉存央行或其他銀行，非直接參與信用創造\n\n→ 答案 (D)",
  },
    {
    id: "mon-62",
    q: "現階段負責基層金融機構之金融檢查業務者是：",
    opts: [
      "(A) 中央銀行",
      "(B) 財政部",
      "(C) 金融監督管理委員會檢查局",
      "(D) 中央存款保險公司",
    ],
    ans: 2,
    hint: "🔑 台灣金融檢查主管機關：金管會（下設銀行局、檢查局等）",
    isCalc: false,
    explain:
      "台灣金融監理與檢查機構分工：\n\n• 金融監督管理委員會（FSC）：主管全體金融業監督管理\n  - 銀行局：銀行業的法規制定與監理\n  - 金融監督管理委員會檢查局（C）✓：\n    負責對各類金融機構（包括基層金融機構：信用合作社、農漁會信用部等）進行實地金融檢查\n\n• 中央銀行（A）：貨幣政策，過去曾有部分金融監理功能，已移交金管會\n• 財政部（B）：財政稅務，金融監理職能已移轉\n• 中央存款保險公司（D）：存款保障，不負責金融檢查\n\n→ 答案 (C)",
  },
    {
    id: "mon-63",
    q: "由於資訊不對稱，銀行決定是否融資給借款人，往往會面臨「逆向選擇」（adverse selection）問題。下列何者是銀行解決逆向選擇問題的方法？",
    opts: [
      "(A) 監控借款人的財務狀況",
      "(B) 限定放款的用途",
      "(C) 委託專業機構對借款人進行信用評估",
      "(D) 要求借款人維持其所提供擔保品之價值",
    ],
    ans: 2,
    hint: "🔑 逆選擇（締約前）的解決：透過篩選機制獲取更多貸款前的資訊",
    isCalc: false,
    explain:
      "逆向選擇（Adverse Selection）= 締約「前」的資訊不對稱問題：\n高風險借款人比銀行更清楚自己的風險，更積極申請貸款。\n\n解決方法比較：\n\n✓ 委託專業機構信用評估（C）：\n  • 如聯合徵信中心（JCIC）、信用評等機構\n  • 在「放款前」取得客觀的第三方信用資訊\n  • 直接解決締約前的資訊不對稱 ✓\n\n✗ 監控借款人財務（A）：是締約「後」的道德風險監控，非逆選擇的解決方法\n✗ 限定放款用途（B）：減少道德風險（締約後），非逆選擇\n✗ 要求維持擔保品價值（D）：締約後的保護措施，非逆選擇\n\n→ 答案 (C)",
  },
    {
    id: "mon-64",
    q: "央行提高法定準備率，銀行準備金需求增加，金融市場利率最可能：",
    opts: [
      "(A) 拆借款利率不變",
      "(B) 拆借款利率上升",
      "(C) 拆借款利率下降",
      "(D) 以上皆非",
    ],
    ans: 1,
    hint: "🔑 準備率↑ → 銀行需保留更多準備金 → 同業拆款需求增加 → 拆款利率↑",
    isCalc: false,
    explain:
      "傳導機制分析：\n\n① 央行提高法定準備率\n② 每家銀行需持有更多準備金（增加準備金需求）\n③ 可用於對外放款和同業拆借的資金減少\n④ 銀行向同業借款的需求增加（供給減少、需求增加）\n⑤ 同業拆款利率（Interbank Rate）上升\n   如：台灣銀行間隔夜拆款利率（TAIBOR）上揚\n⑥ 整體市場資金緊縮，各種市場利率隨之上升\n\n→ 答案 (B)：拆借款利率上升",
  },
    {
    id: "mon-65",
    q: "下列何者不屬於中央銀行的負債項？",
    opts: [
      "(A) 郵政儲金轉存款",
      "(B) 對政府機關放款",
      "(C) 銀行存款準備金",
      "(D) 政府存款",
    ],
    ans: 1,
    hint: "🔑 央行對政府放款 = 央行借錢給政府 = 政府欠央行 = 央行「資產」",
    isCalc: false,
    explain:
      "中央銀行資產負債表結構：\n\n央行「資產」（別人欠央行或央行持有的）：\n✓ 對政府機關放款（B）✓ 答案：央行「貸款給政府」→ 政府欠央行 → 央行資產\n✓ 外匯存底（黃金、外幣）\n✓ 政府公債（買入）\n✓ 對商業銀行的貼現貸款\n\n央行「負債」（央行欠別人的）：\n✓ 郵政儲金轉存款（A）：中華郵政轉存的儲金，央行需付息\n✓ 銀行存款準備金（C）：商業銀行存放在央行的準備金\n✓ 政府存款（D）：政府在央行的帳戶餘額\n✓ 流通中的通貨（發行的鈔票）\n\n→ 答案 (B)：對政府放款是央行資產，非負債",
  },
    {
    id: "mon-66",
    q: "下列貨幣供給創造過程中的參與者，何者扮演的角色最重要？",
    opts: [
      "(A) 中央銀行",
      "(B) 存款貨幣機構",
      "(C) 存款者",
      "(D) 借款者",
    ],
    ans: 2,
    hint: "🔑 信用創造的起點：存款者將現金存入銀行，才啟動整個乘數效應鏈",
    isCalc: false,
    explain:
      "貨幣供給創造的三大參與者分析：\n\n• 中央銀行（A）：控制貨幣基數（MB）和法定準備率，決定乘數大小\n• 存款貨幣機構（B）：執行放款，將存款轉化為貸款，進行信用創造\n• 借款者（D）：借入資金並使用，使資金再流入銀行體系\n\n✓ 存款者（C）— 最重要角色：\n• 將現金「存入」銀行，提供銀行信用創造的「原始資金」\n• 沒有存款者存款，銀行沒有資金可以放款，整個信用創造鏈根本無法啟動\n• 也決定貨幣乘數（c = 現金持有率），持有現金比例影響乘數大小\n\n→ 答案 (C)：存款者角色最重要",
  },
    {
    id: "mon-67",
    q: "下列何者是貨幣政策的最終目的之一？",
    opts: [
      "(A) 低通貨膨脹率",
      "(B) 造成經濟大幅波動",
      "(C) 減緩經濟成長",
      "(D) 貨幣供給成長率呈現不規則的變化",
    ],
    ans: 0,
    hint: "🔑 各國央行的核心目標：物價穩定（低通膨）、充分就業、經濟穩定成長",
    isCalc: false,
    explain:
      "中央銀行貨幣政策的最終目標（Final Targets）：\n\n1. 物價穩定（Price Stability）→ 低且穩定的通貨膨脹率（A）✓\n   例：台灣央行目標約為 2% 左右通膨率\n   例：美聯準設定 2% 通膨目標\n\n2. 充分就業（Full Employment）：失業率維持在自然失業率附近\n\n3. 經濟穩定成長（Economic Growth）：避免景氣大幅波動\n\n4. 金融體系穩定（Financial Stability）\n\n錯誤選項皆與「維護經濟穩定」的目標相反：\n(B) 大幅波動 ✗\n(C) 減緩成長 ✗\n(D) 貨幣供給不規則 ✗\n\n→ 答案 (A)：低通貨膨脹率",
  },
    {
    id: "mon-68",
    q: "下列何項金融工具不屬於衍生性金融商品？",
    opts: [
      "(A) 股票選擇權",
      "(B) 商業本票",
      "(C) 利率遠期契約",
      "(D) 股票指數期貨",
    ],
    ans: 1,
    hint: "🔑 衍生性商品：從基礎資產「衍生」出來的合約（期貨、選擇權、遠期、互換）",
    isCalc: false,
    explain:
      "衍生性金融商品（Financial Derivatives）：\n從基礎資產（股票、利率、匯率、商品）「衍生」出來的金融契約，價值取決於基礎資產價格。\n\n四大類衍生性商品：\n✓ 期貨（Futures）：如股票指數期貨（D）\n✓ 選擇權（Options）：如股票選擇權（A）\n✓ 遠期契約（Forwards）：如利率遠期契約（C）\n✓ 互換（Swaps）：如利率交換\n\n非衍生性商品：\n✗ 商業本票（B）：企業直接發行的短期信用票券，是基本（本源）金融工具，不從其他資產衍生\n\n→ 答案 (B)：商業本票",
  },
    {
    id: "mon-69",
    q: "下列何項金融機構是屬於契約型的儲蓄機構？",
    opts: [
      "(A) 共同基金",
      "(B) 商業銀行",
      "(C) 人壽保險公司",
      "(D) 農會",
    ],
    ans: 2,
    hint: "🔑 契約型儲蓄機構：以長期合約（保險契約、退休金）定期吸收資金",
    isCalc: false,
    explain:
      "金融機構的分類：\n\n契約型儲蓄機構（Contractual Savings Institutions）：\n透過「長期合約」定期向大眾吸收資金，並用於長期投資\n✓ 人壽保險公司（C）：以壽險保單（長期合約）收取保費\n✓ 退休基金（年金基金）：以退休計畫合約定期提撥\n\n存款機構（Depository Institutions）：\n✗ 商業銀行（B）：以存款方式吸收資金\n✗ 農會信用部（D）：農會下設的信用合作機構\n\n投資型機構（Investment Intermediaries）：\n✗ 共同基金（A）：彙集投資人資金投入市場，以受益憑證為媒介\n\n→ 答案 (C)：人壽保險公司",
  },
    {
    id: "mon-70",
    q: "下列何項不是中央銀行的資產？",
    opts: [
      "(A) 政府公債",
      "(B) 貼現貸款",
      "(C) 財政部在央行存款",
      "(D) 黃金",
    ],
    ans: 2,
    hint: "🔑 財政部存款在央行 → 政府欠央行保管責任 → 央行「負債」",
    isCalc: false,
    explain:
      "中央銀行資產（Assets）：\n✓ 政府公債（A）：央行買入政府公債，持有債權 → 資產\n✓ 貼現貸款（B）：央行貸款給商業銀行，銀行欠央行 → 資產\n✓ 黃金（D）：央行持有的黃金準備 → 資產\n✓ 外匯存底（美元、歐元等）\n\n中央銀行負債（Liabilities）：\n✗ 財政部在央行存款（C）：\n  • 政府（財政部）將稅收、公債款項等存放在央行\n  • 央行有義務在政府需要時付款 → 央行「負債」\n\n→ 答案 (C)：財政部在央行的存款是央行的負債，非資產",
  },
    {
    id: "mon-71",
    q: "下列何項業務不會改變銀行之資產負債表？",
    opts: [
      "(A) 購買私人企業之股票",
      "(B) 購買政府公債",
      "(C) 替客戶進行大額外匯交易收取服務費",
      "(D) 放款給家庭購買房子",
    ],
    ans: 2,
    hint: "🔑 代客戶進行外匯買賣 = 表外業務（Off-Balance Sheet）：資產負債均不變，只收手續費",
    isCalc: false,
    explain:
      "各業務對銀行資產負債表的影響：\n\n✗ (A) 購買企業股票：\n  借：有價證券投資（資產↑）貸：現金（資產↓）→ 資產內部調整，表有變動\n\n✗ (B) 購買政府公債：\n  借：政府公債（資產↑）貸：現金（資產↓）→ 同上，表有變動\n\n✗ (D) 房屋放款：\n  借：放款（資產↑）貸：存款或現金（資產↓或負債↑）→ 表有重大變動\n\n✓ (C) 代客戶外匯交易（銀行不承接外匯部位，純代理）：\n  • 銀行只是居間撮合，自身的資產和負債不增減\n  • 只認列「手續費收入」（損益變動，非資產負債）\n  • 屬「表外業務（Off-Balance Sheet）」\n\n→ 答案 (C)",
  },
    {
    id: "mon-72",
    q: "王董在債券市場以 10 萬元買了一張票面價值為 12 萬元的公司債，持有一年，領了 6 千元利息後，再以 10.4 萬元賣出，則該公司債的相關報酬率之敘述，下列何者正確？",
    opts: [
      "(A) 持有期間收益率 10%",
      "(B) 名目收益率（nominal yield）6%",
      "(C) 當期收益率（current yield）5%",
      "(D) 持有期間收益率（yield to holding period）8.3%",
    ],
    ans: 0,
    hint: "🔑【計算題】持有期間收益率 = (利息收入 + 資本利得) ÷ 期初投入成本",
    isCalc: true,
    explain:
      "【各報酬率計算步驟】\n\n基本資料：\n• 買入價格（期初投入）：$100,000\n• 票面價值：$120,000\n• 利息收入：$6,000\n• 賣出價格：$104,000\n• 資本利得 = $104,000 - $100,000 = $4,000\n\n① 持有期間收益率（Holding Period Yield）(A) ✓\n= (利息收入 + 資本利得) ÷ 期初買入成本\n= ($6,000 + $4,000) ÷ $100,000\n= $10,000 ÷ $100,000\n= 10% ← 正確答案\n\n② 名目收益率（Nominal Yield）：\n= 票面利息 ÷ 票面價值 = $6,000 ÷ $120,000\n= 5%（非 6%，(B) 錯誤）\n\n③ 當期收益率（Current Yield）：\n= 票面利息 ÷ 市場買入價格 = $6,000 ÷ $100,000\n= 6%（非 5%，(C) 錯誤）\n\n→ 答案 (A)：持有期間收益率 10%",
  },
    {
    id: "mon-73",
    q: "下列何項因素會使名目貨幣需求增加？",
    opts: [
      "(A) 實質所得上升",
      "(B) 物價水準下跌",
      "(C) 非貨幣資產利率上升",
      "(D) 貨幣的報酬下跌",
    ],
    ans: 0,
    hint: "🔑 貨幣需求（名目）= 物價 × 實質需求；實質所得↑ → 交易需求↑ → 貨幣需求↑",
    isCalc: false,
    explain:
      "名目貨幣需求（Md）的影響因素：\n\nMd = P × L(Y, i)\n• P：物價水準\n• Y：實質所得（交易動機）\n• i：名目利率（持有貨幣的機會成本）\n\n各選項分析：\n✓ 實質所得上升（A）：\n  購買更多商品 → 需要更多貨幣進行交易 → 名目貨幣需求增加 ✓\n\n✗ 物價下跌（B）：\n  購買相同商品所需的名目金額減少 → 名目貨幣需求「減少」\n\n✗ 非貨幣資產利率上升（C）：\n  持有貨幣的機會成本↑ → 人們轉換到高收益資產 → 貨幣需求「減少」\n\n✗ 貨幣報酬下跌（D）：\n  持有貨幣相對不划算 → 貨幣需求「減少」\n\n→ 答案 (A)：實質所得上升",
  },
    {
    id: "mon-74",
    q: "就短期總供給與總需求模型而言，當勞動與原物料成本皆上升時，短期均衡達成後之結果為：",
    opts: [
      "(A) 產量增加與物價水準上漲",
      "(B) 產量增加與物價水準下跌",
      "(C) 產量減少與物價水準上漲",
      "(D) 產量減少與物價水準下跌",
    ],
    ans: 2,
    hint: "🔑 成本上升 → 廠商生產意願降低 → SRAS 曲線左移 → 物價↑ + 產量↓",
    isCalc: false,
    explain:
      "【總供需模型（AS-AD Model）分析】\n\n情境：勞動成本 + 原物料成本皆上升\n\n①供給面衝擊（負面）：\n  生產成本上升 → 廠商在每個物價水準都減少供給\n  → 短期總供給曲線（SRAS）向左移動\n\n② 在總需求（AD）不變的情況下：\n  SRAS 左移 → 新均衡在 AD 曲線上的左上方\n  → 均衡物價水準「上漲」\n  → 均衡實質產量「減少」\n\n③ 結果：\n  物價↑ + 產量↓ = 成本推動型通膨\n  若持續，可能演變為停滯性通貨膨脹（Stagflation）\n\n→ 答案 (C)：產量減少與物價水準上漲",
  },
    {
    id: "mon-75",
    q: "所謂「價量不相容原則」是指中央銀行若選擇控制貨幣供給為其中間目標，就會失去對何者的控制？",
    opts: [
      "(A) 失業率",
      "(B) 匯率水準",
      "(C) 通貨膨脹率",
      "(D) 利率水準",
    ],
    ans: 3,
    hint: "🔑 貨幣供給（量）固定時，利率（價）由市場供需決定，央行無法同時控制兩者",
    isCalc: false,
    explain:
      "價量不相容原則（Price-Quantity Incompatibility）：\n\n在貨幣市場中：\n• 「量」= 貨幣供給（M）\n• 「價」= 利率（i）\n\n兩者關係如同一條向右下方傾斜的貨幣需求曲線：\n\n情況 1：央行固定貨幣供給（控制量）：\n→ 貨幣需求變動時，利率自由浮動，央行「失去利率控制」\n\n情況 2：央行釘住特定利率目標（控制價）：\n→ 利率固定時，貨幣供給必須隨需求被動調整，央行「失去貨幣供給控制」\n\n結論：央行不能同時精確控制「貨幣供給量」和「利率」，選擇控制其中一個，就必須放棄另一個。\n\n→ 答案 (D)：選擇控制貨幣供給，就失去利率水準的控制",
  }
  ],
  會計學: [
    {
    id: "acc-01",
    q: "甲公司為汽車經銷商，則在其正常營業活動中，準備出售之汽車應列為：",
    opts: [
      "(A) 運輸設備",
      "(B) 不動產、廠房及設備",
      "(C) 存貨",
      "(D) 其他資產",
    ],
    ans: 2,
    hint: "🔑 關鍵判斷：以銷售為目的 → 存貨；以自用為目的 → 固定資產",
    isCalc: false,
    explain:
      "判斷會計科目的關鍵在於「持有目的」：\n\n• 汽車經銷商的核心業務是「買賣汽車」，準備出售的汽車是其商品\n  → 屬於「存貨（Inventory）」\n\n• 若汽車經銷商買一台汽車供業務員跑外務使用\n  → 屬於「不動產、廠房及設備（PPE）」\n\n相同的實物因持有目的不同，會計科目完全不同。\n\n→ 答案 (C)：存貨",
  },
    {
    id: "acc-02",
    q: "某公司一年賣出 8,000 產品，售價每單位 $20，變動成本每單位 $10，固定成本每年 $40,000，該公司賺得稅前淨利是多少？",
    opts: [
      "(A) $120,000",
      "(B) $80,000",
      "(C) $40,000",
      "(D) $20,000",
    ],
    ans: 2,
    hint: "🔑【計算題】淨利 = 銷貨收入 − 變動成本 − 固定成本",
    isCalc: true,
    explain:
      "【計算步驟—邊際貢獻法】\n\n① 銷貨收入 = 8,000 × $20 = $160,000\n\n② 變動成本總額 = 8,000 × $10 = $80,000\n\n③ 貢獻邊際（Contribution Margin）\n   = 銷貨收入 − 變動成本\n   = $160,000 − $80,000 = $80,000\n\n④ 稅前淨利\n   = 貢獻邊際 − 固定成本\n   = $80,000 − $40,000\n   = $40,000\n\n→ 答案 (C)：稅前淨利 $40,000",
  },
    {
    id: "acc-03",
    q: "若銷貨成本為 $350,000，銷貨毛利率為 30%，則銷貨收入為：",
    opts: [
      "(A) $150,000",
      "(B) $500,000",
      "(C) $250,000",
      "(D) $100,000",
    ],
    ans: 1,
    hint: "🔑【計算題】毛利率 = 毛利 ÷ 銷貨收入；銷貨成本率 = 1 − 毛利率 = 70%",
    isCalc: true,
    explain:
      "【計算步驟—逆推法】\n\n已知：\n• 銷貨成本 = $350,000\n• 銷貨毛利率 = 30%\n• ∴ 銷貨成本率 = 1 − 30% = 70%\n\n設銷貨收入 = S\n銷貨成本 = S × 70% = $350,000\nS = $350,000 ÷ 70%\nS = $500,000\n\n驗證：\n• 毛利 = $500,000 × 30% = $150,000\n• 銷貨成本 = $500,000 − $150,000 = $350,000 ✓\n\n→ 答案 (B)：銷貨收入 $500,000",
  },
    {
    id: "acc-04",
    q: "公司於 1 月 1 日以現金 $105,000 購入機器一台，預計使用年限 5 年，殘值 $5,000，年底依直線法提列折舊，一年折舊費用是多少？",
    opts: [
      "(A) $5,000",
      "(B) $10,000",
      "(C) $20,000",
      "(D) $50,000",
    ],
    ans: 2,
    hint: "🔑【計算題】直線法折舊 = (成本 − 殘值) ÷ 耐用年限",
    isCalc: true,
    explain:
      "【直線法（Straight-Line Method）折舊公式】\n\n年折舊費用 = (原始成本 − 估計殘值) ÷ 耐用年限\n\n代入數值：\n= ($105,000 − $5,000) ÷ 5 年\n= $100,000 ÷ 5\n= $20,000 / 年\n\n特點：每年折舊費用相同，帳面價值線性遞減。\n5 年後帳面價值 = $105,000 − ($20,000 × 5) = $5,000（殘值）\n\n→ 答案 (C)：$20,000",
  },
    {
    id: "acc-05",
    q: "會計師查核報告的意見種類不包括：",
    opts: [
      "(A) 無保留意見",
      "(B) 保留意見",
      "(C) 無法表示意見",
      "(D) 同意意見",
    ],
    ans: 3,
    hint: "🔑 審計意見四種類型：無保留、保留、否定、無法表示意見",
    isCalc: false,
    explain:
      "會計師查核報告的四種意見類型：\n\n① 無保留意見（Unqualified Opinion）：財報允當表達，最佳意見\n② 保留意見（Qualified Opinion）：有部分保留事項，但整體尚可\n③ 否定意見（Adverse Opinion）：財報嚴重不實，整體無法允當表達\n④ 無法表示意見（Disclaimer of Opinion）：查核範圍受限，無法形成意見\n\n「同意意見（D）」並不是正式的審計意見種類，是本題的干擾選項。\n\n→ 答案 (D)：同意意見不是查核報告的意見種類",
  },
    {
    id: "acc-06",
    q: "會計的記載，應符合下列何者之規定，才可充分表達企業之會計所得？",
    opts: [
      "(A) 所得稅法",
      "(B) 公司法",
      "(C) 公平交易法",
      "(D) 一般公認會計原則",
    ],
    ans: 3,
    hint: "🔑 財務會計 vs 稅務會計：財務報表遵循 GAAP/IFRS，非稅法",
    isCalc: false,
    explain:
      "財務會計與稅務會計的區分：\n\n• 財務會計（Financial Accounting）：\n  依照「一般公認會計原則（GAAP）」或「國際財務報導準則（IFRS）」編製\n  目的：對外報告，供投資人、債權人等決策\n\n• 稅務會計（Tax Accounting）：\n  依照「所得稅法」計算應納稅額\n  目的：計算稅務所得，申報繳稅\n\n兩者常有差異（如：折舊方法、收入認列時點），產生「暫時性差異」和「遞延所得稅」。\n\n→ 答案 (D)：一般公認會計原則",
  },
    {
    id: "acc-07",
    q: "當公司宣告將發放現金股利時，在宣告當日對該公司財務報表會產生何種影響？",
    opts: [
      "(A) 資產減少，權益減少",
      "(B) 負債減少，權益增加",
      "(C) 負債增加，權益減少",
      "(D) 普通股股本增加",
    ],
    ans: 2,
    hint: "🔑 宣告日（非發放日）：確認「應付股利」負債，同時減少保留盈餘",
    isCalc: false,
    explain:
      "現金股利的會計處理（分兩個時點）：\n\n【宣告日（Declaration Date）】\n借：保留盈餘（股東權益↓）\n貸：應付現金股利（負債↑）\n→ 負債增加，股東權益減少 → 答案 (C) ✓\n\n【發放日（Payment Date）】\n借：應付現金股利（負債↓）\n貸：現金（資產↓）\n→ 資產減少，負債減少（等額）\n\n注意：宣告日並不影響資產（現金尚未流出），只影響負債和股東權益。",
  },
    {
    id: "acc-08",
    q: "在分期攤還本金的情況下，各期利息有何變化？",
    opts: [
      "(A) 各期利息費用逐年遞增",
      "(B) 各期利息費用逐年遞減",
      "(C) 各期利息費用相同",
      "(D) 受到各期攤還本金金額大小的影響",
    ],
    ans: 1,
    hint: "🔑【計算題】利息費用 = 期初未還本金餘額 × 利率；本金逐期減少 → 利息遞減",
    isCalc: true,
    explain:
      "【計算驗證—等額本金還款】\n\n假設：借款 $100,000，年利率 10%，每年還本 $25,000\n\n第 1 年：\n  利息 = $100,000 × 10% = $10,000\n  還本後餘額 = $75,000\n\n第 2 年：\n  利息 = $75,000 × 10% = $7,500\n  還本後餘額 = $50,000\n\n第 3 年：\n  利息 = $50,000 × 10% = $5,000\n  還本後餘額 = $25,000\n\n第 4 年：\n  利息 = $25,000 × 10% = $2,500\n  還本後餘額 = $0\n\n→ 利息費用逐年遞減（$10,000 → $7,500 → $5,000 → $2,500）\n→ 答案 (B)",
  },
    {
    id: "acc-09",
    q: "下列何項非為於企業資產負債表之固定資產項下表達之必要條件？",
    opts: [
      "(A) 具有未來經濟效益",
      "(B) 剩餘耐用年限必須超過 1 年或 1 個營業週期",
      "(C) 供營業使用",
      "(D) 具有實體存在",
    ],
    ans: 1,
    hint: "🔑 固定資產認列條件：未來效益、供營業用、有形實體；「剩餘」年限非條件",
    isCalc: false,
    explain:
      "不動產、廠房及設備（PPE）的認列條件：\n✓ (A) 具有未來經濟效益：能在未來帶來收益\n✓ (C) 供營業使用：不是為了轉售（轉售為存貨），而是供企業自用\n✓ (D) 具有實體存在：有形資產（無形資產不屬 PPE）\n\n✗ (B) 剩餘耐用年限超過 1 年：這不是必要條件。\n重要說明：「耐用年限」是在「取得時」評估的，而非「剩餘年限」。一台已使用 4 年、原耐用年限 5 年的機器，雖剩餘不足 1 年，仍可列為固定資產直到完全折舊。\n\n→ 答案 (B)：非必要條件",
  },
    {
    id: "acc-10",
    q: "上市公司資產負債表上的會計科目均為：",
    opts: [
      "(A) 實帳戶",
      "(B) 混合帳戶",
      "(C) 虛帳戶",
      "(D) 實帳戶、虛帳戶皆有",
    ],
    ans: 0,
    hint: "🔑 資產負債表 = 靜態報表；所有科目均為實帳戶（永久帳戶）",
    isCalc: false,
    explain:
      "帳戶類型與財務報表的對應：\n\n• 實帳戶（Permanent / Real Accounts）：\n  資產、負債、業主權益科目\n  → 餘額跨期延續，不在結帳時清零\n  → 全部出現在「資產負債表」上 ✓\n\n• 虛帳戶（Temporary / Nominal Accounts）：\n  收入、費用、股利\n  → 每期結帳後餘額清零，轉入保留盈餘\n  → 出現在「損益表」和「股東權益變動表」\n\n資產負債表的科目（資產、負債、股東權益）全都是「實帳戶」。\n\n→ 答案 (A)：實帳戶",
  },
    {
    id: "acc-11",
    q: "甲公司於年初發行面額 $3,000,000 之公司債，票面利率及市場有效利率皆為 3%，每年年底付息，則下列敘述何者錯誤？",
    opts: [
      "(A) 此公司債之發行價格 $3,000,000",
      "(B) 此公司債每年應認列之利息費用為 $90,000",
      "(C) 此公司債之到期值 $3,180,000",
      "(D) 此公司債到期時的帳面價值為 $3,000,000",
    ],
    ans: 2,
    hint: "🔑【計算題】平價發行時，「到期值」= 面額（應還本金），非含總利息",
    isCalc: true,
    explain:
      "【分析各選項】\n\n票面利率 = 市場利率 = 3% → 平價發行\n\n(A) 發行價格 = $3,000,000（面額）✓\n   平價發行：票面利率 = 市場利率，發行價格等於面額\n\n(B) 每年利息費用 = $3,000,000 × 3% = $90,000 ✓\n   平價發行下，利息費用 = 帳面價值 × 市場利率 = 面額 × 3%\n\n(C) ★錯誤★：「到期值（Maturity Value / Face Value）」\n   = 到期時應償還的本金 = 面額 = $3,000,000\n   （非面額 + 歷年利息總額）\n   若誤算：$3,000,000 + $90,000 × 6年 = $3,540,000 → 此為錯誤概念\n   注意：到期值≠面額+利息，利息逐年支付，不計入到期值\n\n(D) 到期時帳面價值 = 面額 = $3,000,000 ✓\n   平價發行無折溢價攤銷，帳面價值始終 = 面額\n\n→ 答案 (C)：到期值 $3,180,000 的說法錯誤",
  },
    {
    id: "acc-12",
    q: "下列項目何者在現金流量表中應列為融資活動之現金流量？",
    opts: [
      "(A) 出售土地",
      "(B) 購買固定資產",
      "(C) 發行公司債",
      "(D) 發放股票股利",
    ],
    ans: 2,
    hint: "🔑 融資活動：與長期負債和股東權益相關的現金交易",
    isCalc: false,
    explain:
      "現金流量表三大活動分類：\n\n• 營業活動（Operating）：日常業務的現金流入流出\n\n• 投資活動（Investing）：長期資產的購買或出售\n  → 出售土地（A）：出售長期資產 → 投資活動\n  → 購買固定資產（B）：購買長期資產 → 投資活動\n\n• 融資活動（Financing）：與長期負債和股東權益相關\n  → 發行公司債（C）✓：舉借長期負債，取得資金 → 融資活動\n  → 償還長期借款、發行股票、支付現金股利 → 融資活動\n\n注意：發放「股票股利（D）」不涉及現金，通常不出現在現金流量表中（或列為附記）\n\n→ 答案 (C)：發行公司債",
  },
    {
    id: "acc-13",
    q: "資產成本為 $40,000，殘值為 $4,000，估計可使用 5 年，按年數合計法計提折舊，第 3 年底之累計折舊為：",
    opts: [
      "(A) $21,600",
      "(B) $24,000",
      "(C) $28,800",
      "(D) $32,000",
    ],
    ans: 2,
    hint: "🔑【計算題】年數合計法：各年折舊率 = 剩餘年數 ÷ 年數總和（1+2+3+4+5=15）",
    isCalc: true,
    explain:
      "【年數合計法（Sum-of-Years-Digits, SYD）計算步驟】\n\n基本資料：\n• 成本 $40,000，殘值 $4,000\n• 應折舊金額 = $40,000 − $4,000 = $36,000\n• 耐用年限 = 5 年\n• 年數合計 = 5+4+3+2+1 = 15\n\n各年折舊費用（前高後低）：\n第 1 年：$36,000 × 5/15 = $12,000\n第 2 年：$36,000 × 4/15 = $9,600\n第 3 年：$36,000 × 3/15 = $7,200\n第 4 年：$36,000 × 2/15 = $4,800\n第 5 年：$36,000 × 1/15 = $2,400\n\n第 3 年底累計折舊：\n= $12,000 + $9,600 + $7,200\n= $28,800\n\n→ 答案 (C)：$28,800",
  },
    {
    id: "acc-14",
    q: "下列那一個關於定期盤存制之描述最為適切：",
    opts: [
      "(A) 可以在每筆銷貨發生後，立即算出銷貨成本",
      "(B) 通常適用於低單價的商品",
      "(C) 需要保存詳細的存貨記錄",
      "(D) 需要保存銷貨成本帳簿以供隨時記載",
    ],
    ans: 1,
    hint: "🔑 定期盤存制：不連續記錄，期末盤點；適合低單價、大量商品",
    isCalc: false,
    explain:
      "定期盤存制（Periodic Inventory System）vs 永續盤存制（Perpetual）：\n\n定期盤存制特點：\n• 平時不記錄存貨進出，只記購貨\n• 期末實地盤點，計算期末存貨\n• 銷貨成本 = 期初存貨 + 本期購貨 − 期末存貨\n• 無法隨時掌握存貨數量\n• 成本較低、作業簡單\n✓ 適合「低單價、大量」商品（B）：如超市、量販店商品\n\n永續盤存制（A、C、D 的特點）：\n• 每筆交易即時記錄存貨增減\n• 可隨時知道存貨數量和成本\n• 需要保存詳細記錄\n• 適合高單價商品（如珠寶、汽車）\n\n→ 答案 (B)",
  },
    {
    id: "acc-15",
    q: "上市公司在選擇會計方法時，會受到下列哪些因素之影響？",
    opts: [
      "(A) 股票市場的反應",
      "(B) 公司的債務契約",
      "(C) 公司獎酬的計畫",
      "(D) 以上皆是",
    ],
    ans: 3,
    hint: "🔑 會計政策選擇受多方利害關係人（Stakeholders）影響",
    isCalc: false,
    explain:
      "企業選擇會計方法的動機（Positive Accounting Theory）：\n\n(A) 股票市場反應：\n  選擇能提高報告盈餘的方法 → 吸引投資人、維持股價\n\n(B) 公司債務契約：\n  債務契約常設有「財務比率限制條款」（如最低流動比率）\n  企業可能選擇能維持比率達標的會計方法，避免違約\n\n(C) 公司獎酬計畫：\n  若高管薪酬與盈餘掛鉤（如盈餘紅利），管理層有動機選擇能提高盈餘的方法\n\n三種動機均真實存在，均影響會計方法的選擇。\n\n→ 答案 (D)：以上皆是",
  },
    {
    id: "acc-16",
    q: "有關公司宣告發放股票股利之敘述，何者錯誤：",
    opts: [
      "(A) 宣告後公司股東權益總額不變",
      "(B) 宣告後公司保留盈餘總額減少",
      "(C) 宣告後公司投入資本總額不變",
      "(D) 宣告後公司每股面值不變",
    ],
    ans: 2,
    hint: "🔑 股票股利：保留盈餘轉入股本 → 股本（投入資本）增加，非不變",
    isCalc: false,
    explain:
      "宣告股票股利的會計分錄：\n借：保留盈餘（減少）\n貸：普通股股本（增加）\n     資本公積（如按市價，差額入資本公積）\n\n各選項分析：\n(A) 股東權益總額不變 ✓：只是股東權益內部重新分配（保留盈餘→股本），總額相同\n(B) 保留盈餘減少 ✓：借方沖減保留盈餘\n(C) ★錯誤★：投入資本（= 股本 + 資本公積）「增加」\n   因為原來是保留盈餘，發放後轉入股本/資本公積（屬投入資本），投入資本增加\n(D) 每股面值不變 ✓：股票股利是增加流通股數，面值固定不變（股票分割才改面值）\n\n→ 答案 (C)：投入資本總額「增加」，非「不變」",
  },
    {
    id: "acc-17",
    q: "或有負債在下列何種情況下應予入帳：",
    opts: [
      "(A) 極有可能發生且金額可合理估計時",
      "(B) 極有可能發生時",
      "(C) 有可能發生且金額可以合理估計時",
      "(D) 有可能發生時",
    ],
    ans: 0,
    hint: "🔑 或有負債入帳需同時滿足兩條件：機率「極高」+ 金額「可估計」",
    isCalc: false,
    explain:
      "或有負債（Contingent Liability）的處理原則：\n\n認列條件（兩者缺一不可）：\n① 極有可能（Probable）發生：機率通常 > 75%\n② 金額可合理估計（Reasonably Estimable）\n\n不同情境的處理：\n• 極可能 + 可估計 → 入帳認列負債（A）✓\n• 極可能 + 無法估計 → 只在附註揭露\n• 有可能（Possible）→ 只在附註揭露\n• 幾乎不可能（Remote）→ 不需處理\n\n→ 答案 (A)：極有可能發生且金額可合理估計時才入帳",
  },
    {
    id: "acc-18",
    q: "賒購某商品標價 $24,000，商業折扣 15%，現金折扣 5%，若採總額法，則在折扣期限內付款時應：",
    opts: [
      "(A) 借記：應付帳款 $20,400",
      "(B) 貸記：現金 $20,400",
      "(C) 借記：應付帳款 $24,000",
      "(D) 貸記：現金 $19,200",
    ],
    ans: 0,
    hint: "🔑【計算題】總額法：先扣商業折扣後入帳；現金折扣在付款時認列",
    isCalc: true,
    explain:
      "【計算步驟—總額法】\n\n① 扣除商業折扣（Trade Discount）後的入帳金額：\n   $24,000 × (1 − 15%) = $24,000 × 85% = $20,400\n   → 以 $20,400 借記進貨，貸記應付帳款 $20,400\n\n② 在折扣期限內付款（享有 5% 現金折扣）：\n   應付帳款清償：借記應付帳款 $20,400（A）✓\n   實際付款：$20,400 × (1 − 5%) = $19,380\n   現金折扣利得：$20,400 × 5% = $1,020\n   \n   完整分錄：\n   借：應付帳款 $20,400\n   貸：現金 $19,380\n   貸：進貨折扣（或進貨折讓）$1,020\n\n注意：(B) 貸記現金應為 $19,380（非 $20,400）；(D) $19,200 錯誤。\n\n→ 答案 (A)：借記應付帳款 $20,400",
  },
    {
    id: "acc-19",
    q: "所謂「配合原則」是指：",
    opts: [
      "(A) 費用應與收入配合",
      "(B) 負債應與資產配合",
      "(C) 資產應與收入配合",
      "(D) 收入應與現金配合",
    ],
    ans: 0,
    hint: "🔑 配合原則（Matching Principle）：認列收入的同期，同步認列相關費用",
    isCalc: false,
    explain:
      "配合原則（Matching Principle）：\n\n核心概念：費用應在帶來相關收入的同一期間認列，使淨利能正確反映當期經營成果。\n\n實際應用例子：\n• 商品銷售時認列銷貨收入 → 同時認列銷貨成本（進貨費用）\n• 年底認列折舊費用 → 對應固定資產帶來的全年收益\n• 計提預估的壞帳費用 → 對應本期的賒銷收入\n• 計提保固費用 → 對應本期的銷售收入\n\n配合原則是損益表最重要的編製原則，確保損益計算的正確性。\n\n→ 答案 (A)：費用應與收入配合",
  },
    {
    id: "acc-20",
    q: "將某一金額以下的支出一律作為收益支出，是合乎：",
    opts: [
      "(A) 客觀原則",
      "(B) 一致性原則",
      "(C) 重要性原則",
      "(D) 成本原則",
    ],
    ans: 2,
    hint: "🔑 金額小、影響微不足道 → 直接費用化 → 重要性原則（Materiality）",
    isCalc: false,
    explain:
      "重要性原則（Materiality Principle）：\n\n金額小的支出，即使理論上應資本化再逐期折舊，但若對財務報表的影響微不足道，可直接費用化（收益支出），不必嚴格適用資本化原則。\n\n實務應用：\n• 設定金額門檻（如 $10,000 以下）\n• 低於門檻的支出一律費用化\n• 高於門檻才需評估是否資本化\n\n好處：節省會計作業成本，且對財務報表影響不重大，不影響使用者決策。\n\n→ 答案 (C)：重要性原則",
  },
    {
    id: "acc-21",
    q: "會計恆等式，係指：",
    opts: [
      "(A) 毛利 ＝ 期初存貨 ＋ 進貨成本 − 期末存貨",
      "(B) 淨利 ＝ 收入 − 毛利 − 營業費用",
      "(C) 淨利 ＝ 收入 − 費用",
      "(D) 資產 ＝ 負債 ＋ 業主權益",
    ],
    ans: 3,
    hint: "🔑 會計恆等式（基本方程式）：資產 = 負債 + 業主權益，複式簿記的基礎",
    isCalc: false,
    explain:
      "會計恆等式（Accounting Equation）：\n\n資產（Assets）= 負債（Liabilities）+ 業主權益（Owner's Equity）\n\n這是複式簿記（Double-Entry Bookkeeping）的核心基礎：\n• 每筆交易借貸必須平衡\n• 等式永遠成立，無論發生什麼交易\n\n說明其他選項的問題：\n(A) 描述的是「銷貨成本」的計算公式，非會計恆等式\n(B) 淨利計算公式不正確（毛利已扣除銷貨成本）\n(C) 淨利 = 收入 − 費用 是正確的損益計算，但不是「會計恆等式」\n\n→ 答案 (D)：資產 = 負債 + 業主權益",
  },
    {
    id: "acc-22",
    q: "X1 年初購入設備 $7,000，估計可用三年，殘值 $1,000，按直線法提列折舊，X3 年 10 月 1 日售得 $2,000 時應：",
    opts: [
      "(A) 借記出售資產損失 $500",
      "(B) 貸記出售資產利益 $500",
      "(C) 借記累計折舊 $5,000",
      "(D) 借記折舊 $500",
    ],
    ans: 1,
    hint: "🔑【計算題】出售前先計算帳面價值，再比較售價決定是利益或損失",
    isCalc: true,
    explain:
      "【計算步驟—直線法出售資產】\n\n① 年折舊額 = ($7,000 − $1,000) ÷ 3年 = $2,000/年\n\n② 累計折舊計算（至出售日）：\n   X1年：$2,000\n   X2年：$2,000（累計 $4,000）\n   X3年 1~9月：$2,000 × 9/12 = $1,500（累計 $5,500）\n\n③ 出售日帳面價值：\n   = 成本 − 累計折舊\n   = $7,000 − $5,500 = $1,500\n\n④ 出售損益：\n   售價 $2,000 > 帳面價值 $1,500\n   → 出售資產利益 = $2,000 − $1,500 = $500\n\n⑤ 出售分錄：\n   借：現金 $2,000\n   借：累計折舊 $5,500\n   貸：設備 $7,000\n   貸：出售資產利益 $500 ← 貸記\n\n→ 答案 (B)：貸記出售資產利益 $500",
  },
    {
    id: "acc-23",
    q: "預收收入中，已實現部分應轉入下列哪一個帳戶？",
    opts: [
      "(A) 資產",
      "(B) 負債",
      "(C) 費用",
      "(D) 收入",
    ],
    ans: 3,
    hint: "🔑 預收收入（負債）→ 服務完成後 → 已實現 → 轉為「收入」",
    isCalc: false,
    explain:
      "預收收入（Unearned Revenue）的會計處理：\n\n初始收款時：\n借：現金（資產↑）\n貸：預收收入（負債↑）—尚未提供服務，是欠客戶的義務\n\n服務完成時（已賺得）：\n借：預收收入（負債↓）\n貸：收入（Revenue↑）—服務已提供，轉為已賺得的收入\n\n例如：\n• 收取雜誌 12 個月訂閱費 → 預收收入（負債）\n• 每月服務交付 → 1/12 轉入訂閱收入\n\n→ 答案 (D)：已實現的預收收入轉入「收入」帳戶",
  },
    {
    id: "acc-24",
    q: "依據我國財務會計準則，若折舊方法由年數合計法改為直線法，稱之為：",
    opts: [
      "(A) 會計估計變動",
      "(B) 會計原則變動",
      "(C) 會計政策變動",
      "(D) 會計主體變動",
    ],
    ans: 0,
    hint: "🔑 依 IFRS：折舊方法改變 = 會計估計變動（前瞻法，非追溯調整）",
    isCalc: false,
    explain:
      "依照 IFRS（IAS 8）的分類：\n\n折舊方法的改變屬於「會計估計變動（Change in Accounting Estimate）」：\n• 因為折舊方法的選擇反映了管理層對資產消耗模式的「估計」\n• 採用「前瞻法（Prospective Approach）」：只影響當期及未來期間，不追溯調整\n\n會計政策變動（Change in Accounting Policy）：\n• 如：存貨計價從先進先出法改為加權平均法\n• 通常需追溯調整（Retrospective Approach）\n\n台灣早期 GAAP 可能有不同規定，但考試以現行 IFRS 為準。\n\n→ 答案 (A)：會計估計變動",
  },
    {
    id: "acc-25",
    q: "企業發生虧損時，其會計方程式：",
    opts: [
      "(A) 依然平衡",
      "(B) 左方較大",
      "(C) 右方較大",
      "(D) 以上皆是",
    ],
    ans: 0,
    hint: "🔑 無論盈虧，會計恆等式「資產 = 負債 + 業主權益」永遠成立",
    isCalc: false,
    explain:
      "會計恆等式的絕對性：\n\n資產（A）= 負債（L）+ 業主權益（OE）\n\n發生虧損時：\n• 淨損使業主權益（保留盈餘）減少\n• 同時，資產（如現金、應收帳款）也會減少\n• 或者負債增加（如累積的應付帳款）\n\n無論如何，等式兩邊同幅變動，等式「依然平衡」。\n\n具體例子：\n• 以現金支付費用 $10,000（發生損失）\n  → 現金↓$10,000（資產↓），費用↑→保留盈餘↓$10,000（OE↓）\n  → 兩邊都減少 $10,000，恆等式維持\n\n→ 答案 (A)：依然平衡",
  },
    {
    id: "acc-26",
    q: "下列何情形被視為「投資公司對被投資公司有控制能力」之可能性最小？",
    opts: [
      "(A) 與其他投資人約定下，具有半數之有表決權股份的能力",
      "(B) 投資公司持有被投資公司有表決權之股份表決權最高者",
      "(C) 依法令或契約約定，可操控公司之財務、營運及人事方針",
      "(D) 有權主導董事會超過半數之投票權，且公司之控制權操控於該董事會",
    ],
    ans: 1,
    hint: "🔑 表決權「最高」但未必過半，控制力最不確定；其他選項均有明確控制依據",
    isCalc: false,
    explain:
      "控制能力（Control）的判斷：\n\n(A) 透過協議握有過半表決權能力 → 有明確控制安排 ✓\n(C) 依法令或契約可主導財務、營運、人事 → 實質控制 ✓\n(D) 主導董事會過半投票權 → 正式控制機制 ✓\n\n(B) 持有「最高比例」表決權（但未必過半）：\n• 例如：持有 30%，其他人各持 15%\n• 30% 雖然最高，但三個 15% 股東聯合（45%）就能否決\n• 沒有任何正式控制機制，控制可能性最小\n\n→ 答案 (B)：可能性最小",
  },
    {
    id: "acc-27",
    q: "會計所稱結帳，是結清：",
    opts: [
      "(A) 資產類帳戶",
      "(B) 負債類帳戶",
      "(C) 負債、資產帳戶",
      "(D) 收入與費用帳戶",
    ],
    ans: 3,
    hint: "🔑 結帳：清零「虛帳戶」（收入、費用）；實帳戶（資產、負債）餘額延續",
    isCalc: false,
    explain:
      "結帳程序（Closing Process）：\n\n目的：將當期損益計算清楚，並將損益結果轉入永久帳戶（保留盈餘）\n\n結帳的對象 = 虛帳戶（臨時帳戶）：\n✓ 收入帳戶：借記各收入科目，貸記損益彙總\n✓ 費用帳戶：貸記各費用科目，借記損益彙總\n✓ 損益彙總：最終結轉至保留盈餘\n\n不結清的帳戶 = 實帳戶（永久帳戶）：\n✗ 資產、負債、業主權益：期末餘額繼續帶入下一期\n\n結帳後，收入和費用帳戶餘額歸零，準備新的一期記帳。\n\n→ 答案 (D)：收入與費用帳戶",
  },
    {
    id: "acc-28",
    q: "下列何者不是存貨：",
    opts: [
      "(A) 房地產商待售之房地",
      "(B) 機械公司自用之機器",
      "(C) 文具店中供銷售之文具盤存",
      "(D) 製造業中之在製品盤存",
    ],
    ans: 1,
    hint: "🔑 存貨定義：以銷售為目的持有的商品；自用固定資產非存貨",
    isCalc: false,
    explain:
      "存貨（Inventory）的定義：\n企業在正常營業過程中持有以供出售，或正在生產用於出售，或生產/提供服務時用於消耗的資產。\n\n各選項分析：\n✓ (A) 房地產商待售房地：以銷售為目的 → 存貨\n✗ (B) 機械公司自用之機器：供自身業務使用 → 不動產、廠房及設備（固定資產）\n✓ (C) 文具店供銷售文具：商品庫存 → 存貨\n✓ (D) 製造業在製品：生產中的半成品，最終用於出售 → 存貨\n\n判斷關鍵：持有目的是「出售」還是「自用」\n\n→ 答案 (B)：機械公司自用之機器不是存貨",
  },
    {
    id: "acc-29",
    q: "甲公司購買檯燈 1 只，成本 $1,800，根據產品說明書，該品牌檯燈之平均耐用年限約為 5 年。甲公司之會計人員在購買日將 $1,800 全數認列為費用，期末亦未做相關調整分錄。試問下列敘述何者正確？",
    opts: [
      "(A) 甲公司會計人員此種做法違反了一般公認會計原則",
      "(B) 甲公司會計人員此種做法係反映審慎性，未違反一般公認會計原則",
      "(C) 甲公司會計人員此種做法係反映重要性，未違反一般公認會計原則",
      "(D) 甲公司會計人員此種做法會降低財務報表之攸關性",
    ],
    ans: 2,
    hint: "🔑 $1,800 金額極小，直接費用化符合重要性原則，不違反 GAAP",
    isCalc: false,
    explain:
      "重要性原則的應用：\n\n理論上，耐用 5 年的檯燈應資本化，每年提列折舊 $360（$1,800÷5）。\n\n但實際上，$1,800 對一般企業財務報表的影響微乎其微：\n• 若對決策者的判斷無實質影響 → 屬不重要（Immaterial）\n• 直接費用化，節省會計作業\n• 符合「重要性原則（Materiality Principle）」\n• 不違反 GAAP\n\n注意：\n• (B) 審慎性（穩健原則）是在不確定情況下保守處理，與本題情境不符\n• (D) 不影響攸關性，$1,800 差異不重要\n\n→ 答案 (C)：反映重要性原則，未違反 GAAP",
  },
    {
    id: "acc-30",
    q: "長期股權投資採權益法處理者，當被投資公司於投資當年度發放現金股利時，投資公司應貸記：",
    opts: [
      "(A) 股利收入",
      "(B) 投資損益",
      "(C) 長期投資－股票",
      "(D) 應收股利",
    ],
    ans: 2,
    hint: "🔑 權益法下收現金股利 = 已認列利益的「回收」→ 減少投資帳面，非認列股利收入",
    isCalc: false,
    explain:
      "權益法（Equity Method）下的股利處理：\n\n邏輯：\n• 被投資公司賺得利益時，投資公司已按持股比例認列「投資損益」（增加投資帳面）\n• 被投資公司發放現金股利，是將已認列的利益「轉回」給投資公司\n• 因此，收到股利時應減少投資帳面價值（非再認列收入，避免重複認列）\n\n會計分錄：\n借：現金（收到的現金股利）\n貸：長期投資－股票（C）✓ ← 減少投資帳面價值\n\n若誤貸記「股利收入（A）」，會導致重複認列利益（已在認列損益時認列一次）。\n\n→ 答案 (C)：長期投資－股票",
  },
    {
    id: "acc-31",
    q: "下列那一個產業最有可能使用「個別認定法」去計算銷貨成本？",
    opts: [
      "(A) 珠寶業",
      "(B) 藥妝業",
      "(C) 輪胎業",
      "(D) 食品業",
    ],
    ans: 0,
    hint: "🔑 個別認定法適用於：高單價、異質性（每件不同）、低周轉的商品",
    isCalc: false,
    explain:
      "個別認定法（Specific Identification Method）：\n• 針對每件特定商品追蹤其實際成本\n• 銷出哪件，就記錄該件的實際取得成本\n\n適用條件：\n✓ 商品單價高\n✓ 每件商品有獨特性（異質，非同質可互換）\n✓ 每次銷售數量少、金額大\n\n各行業分析：\n✓ 珠寶業（A）：每件珠寶獨一無二、單價高 → 最適合個別認定法\n✗ 藥妝業（B）：大量同質商品，難以個別追蹤\n✗ 輪胎業（C）：標準化量產商品\n✗ 食品業（D）：量大、同質，通常用先進先出法或加權平均法\n\n→ 答案 (A)：珠寶業",
  },
    {
    id: "acc-32",
    q: "期末盤點存貨時，若遺漏計算某批商品，致期末存貨低估，則會導致該公司當年度的：",
    opts: [
      "(A) 銷貨毛利高估",
      "(B) 淨利低估",
      "(C) 保留盈餘不變",
      "(D) 營運資金高估",
    ],
    ans: 1,
    hint: "🔑【計算題】期末存貨低估 → 銷貨成本高估 → 毛利低估 → 淨利低估",
    isCalc: true,
    explain:
      "【連鎖影響分析】\n\n公式：銷貨成本 = 期初存貨 + 本期進貨 − 期末存貨\n\n期末存貨「低估」時：\n銷貨成本 = 期初存貨 + 進貨 − (期末存貨低估值)\n→ 銷貨成本「高估」\n\n銷貨毛利 = 銷貨收入 − 銷貨成本(高估)\n→ 銷貨毛利「低估」（非高估，A 錯）\n\n淨利 = 毛利 − 營業費用\n→ 淨利「低估」（B 正確）✓\n\n保留盈餘 = 期初保留盈餘 + 淨利(低估)\n→ 保留盈餘「低估」（非不變，C 錯）\n\n營運資金 = 流動資產(含存貨低估) − 流動負債\n→ 流動資產低估 → 營運資金「低估」（非高估，D 錯）\n\n→ 答案 (B)：淨利低估",
  },
    {
    id: "acc-33",
    q: "採備抵法認列應收帳款之壞帳費用，當實際發生壞帳時，對財務報表之影響為：",
    opts: [
      "(A) 應收帳款淨變現價值減少",
      "(B) 應收帳款淨變現價值不變",
      "(C) 流動資產減少",
      "(D) 營運資金減少",
    ],
    ans: 1,
    hint: "🔑 備抵法沖銷實際壞帳：應收帳款↓ 和 備抵壞帳↓ 等額，淨額不變",
    isCalc: false,
    explain:
      "備抵法（Allowance Method）的兩個步驟：\n\n步驟一（期末計提）：\n借：壞帳費用（費用↑，淨利↓）\n貸：備抵壞帳（資產抵減↑）\n→ 應收帳款帳面額不變，但「淨額」減少\n\n步驟二（實際沖銷壞帳時）：\n借：備抵壞帳（資產抵減↓）\n貸：應收帳款（資產↓）\n→ 應收帳款總額↓，備抵壞帳↓，兩者等額\n→ 應收帳款「淨變現價值」= 總額 − 備抵 = 不變 ✓\n→ 流動資產總額不變（淨額未變）\n→ 淨利不影響（費用已在計提時認列）\n\n→ 答案 (B)：淨變現價值不變",
  },
    {
    id: "acc-34",
    q: "期末調整前備抵壞帳有借餘 $100，期末備抵壞帳應為應收帳款餘額 $10,000 的 3%，則本期應提壞帳費用：",
    opts: [
      "(A) $400",
      "(B) $300",
      "(C) $200",
      "(D) $100",
    ],
    ans: 0,
    hint: "🔑【計算題】目標備抵壞帳 = $10,000 × 3% = $300；現有借餘 $100 → 需提 $400",
    isCalc: true,
    explain:
      "【計算步驟—備抵壞帳調整】\n\n① 目標備抵壞帳餘額：\n   = 應收帳款 × 壞帳率\n   = $10,000 × 3% = $300（貸方餘額）\n\n② 調整前備抵壞帳狀況：\n   借餘 $100（異常）→ 表示過去沖銷壞帳金額超過已提備抵\n   本期需要調整至目標貸餘 $300\n\n③ 本期應提壞帳費用：\n   = 目標貸餘 $300 − 現在借餘 (-$100)\n   = $300 + $100 = $400\n\n理解方式：\n備抵壞帳本來有借餘 $100（「虧空」），\n要讓它變成貸餘 $300，\n需要貸記備抵壞帳 $400（$100 補虧空 + $300 達目標）\n\n調整分錄：\n借：壞帳費用 $400\n貸：備抵壞帳 $400\n\n→ 答案 (A)：$400",
  },
    {
    id: "acc-35",
    q: "在公司設有定額零用金制度下，若以零用金支付計程車費時，應作何種會計處理？",
    opts: [
      "(A) 貸記零用金",
      "(B) 借記交通費",
      "(C) 借記零用金",
      "(D) 不作分錄，作備忘錄即可",
    ],
    ans: 3,
    hint: "🔑 定額零用金制：每次動用零用金只作備忘，等到撥補時才作費用分錄",
    isCalc: false,
    explain:
      "定額零用金制度（Petty Cash Fund）的操作：\n\n設立零用金時：\n借：零用金 $XX\n貸：現金 $XX\n\n支用零用金時（B 是錯誤的做法）：\n→ 只作「備忘記錄」（保存收據/憑條），不作正式會計分錄 ✓ (D)\n\n撥補零用金時（零用金快用完）：\n借：交通費 $XX（以收據為憑）\n借：其他費用 $XX\n貸：現金 $XX（補回零用金至定額）\n\n零用金帳戶只在設立、增減金額時才變動，平時支用不作分錄。\n\n→ 答案 (D)：不作分錄，作備忘錄即可",
  },
    {
    id: "acc-36",
    q: "甲公司在編製完成銀行往來調節表後，下列何者需於公司帳上作調整分錄？",
    opts: [
      "(A) 銀行手續費",
      "(B) 銀行誤將兌付他公司支票誤記為該公司帳戶",
      "(C) 在途存款",
      "(D) 未兌現支票",
    ],
    ans: 0,
    hint: "🔑 需公司調整的項目：銀行已知但公司帳尚未記錄的事項（如手續費、利息）",
    isCalc: false,
    explain:
      "銀行往來調節表的調整項目分類：\n\n需要「公司帳」調整的項目：\n✓ 銀行手續費（A）：銀行已從帳戶扣除，公司帳尚未記錄 → 公司需補作費用分錄\n✓ 銀行利息收入：銀行已入帳，公司未記\n✓ 代收款項：銀行代收但公司未知\n\n需要「銀行帳」調整的項目（通知銀行更正）：\n✗ 銀行誤記（B）：銀行記錯，請銀行更正，公司帳不用動\n✗ 在途存款（C）：公司已記，銀行尚未收到 → 等銀行入帳即可\n✗ 未兌現支票（D）：公司已開出，銀行尚未兌付 → 等銀行兌付即可\n\n→ 答案 (A)：銀行手續費",
  },
    {
    id: "acc-37",
    q: "下列何項不屬營業資產？",
    opts: [
      "(A) 商譽",
      "(B) 償債基金",
      "(C) 專利權",
      "(D) 土地",
    ],
    ans: 1,
    hint: "🔑 營業資產：用於日常營運；償債基金是為特定目的提撥的專款投資",
    isCalc: false,
    explain:
      "營業資產（Operating Assets）vs 非營業資產：\n\n✓ 商譽（A）：來自企業購併，代表超額獲利能力，用於日常經營 → 營業資產\n✓ 專利權（C）：用於生產或提供服務，帶來日常收益 → 營業資產\n✓ 土地（D）：廠房所在地，供日常生產使用 → 營業資產\n\n✗ 償債基金（B）：\n• 公司提撥專款，用於日後償還長期債務\n• 不用於日常營業，是財務性長期投資\n• 屬「其他資產（長期投資）」，非營業資產\n\n→ 答案 (B)：償債基金不屬營業資產",
  },
    {
    id: "acc-38",
    q: "可供銷售商品成本包括兩項：期初存貨和：",
    opts: [
      "(A) 毛利",
      "(B) 進貨成本",
      "(C) 期初存貨",
      "(D) 期末存貨",
    ],
    ans: 1,
    hint: "🔑 可供銷售商品成本 = 期初存貨 + 本期進貨成本",
    isCalc: false,
    explain:
      "存貨計算公式（定期盤存制）：\n\n可供銷售商品成本（Cost of Goods Available for Sale）：\n= 期初存貨 + 本期進貨成本\n\n再扣除期末存貨，得到：\n銷貨成本 = 可供銷售商品成本 − 期末存貨\n= 期初存貨 + 進貨成本 − 期末存貨\n\n記憶方法：\n「期初 + 進貨 = 可以賣的」\n「可以賣的 − 沒賣完（期末）= 賣出的成本」\n\n→ 答案 (B)：進貨成本",
  },
    {
    id: "acc-39",
    q: "若期末存貨高估，則：",
    opts: [
      "(A) 本期銷貨成本低估，淨利高估",
      "(B) 本期之銷貨成本及淨利均低估",
      "(C) 本期之銷貨成本及淨利均高估",
      "(D) 本期銷貨成本高估，淨利低估",
    ],
    ans: 0,
    hint: "🔑【計算題】期末存貨↑ → 銷貨成本↓（因扣除的較多）→ 毛利↑ → 淨利↑",
    isCalc: true,
    explain:
      "【連鎖影響分析—期末存貨高估】\n\n銷貨成本公式：\n= 期初存貨 + 進貨 − 期末存貨\n\n期末存貨高估（虛增）→ 扣除金額增大\n→ 銷貨成本「低估」\n\n毛利 = 銷貨收入 − 銷貨成本(低估)\n→ 毛利「高估」\n\n淨利 = 毛利(高估) − 費用\n→ 淨利「高估」\n\n下一期的影響：\n• 本期期末高估 = 下期期初高估\n• 下期銷貨成本高估（+），淨利低估（-）\n• 兩期相加，錯誤自動抵消（自動更正）\n\n→ 答案 (A)：銷貨成本低估，淨利高估",
  },
    {
    id: "acc-40",
    q: "或有負債是：",
    opts: [
      "(A) 流動負債",
      "(B) 長期負債",
      "(C) 估計負債",
      "(D) 一種可能發生而未必發生之負債",
    ],
    ans: 3,
    hint: "🔑 或有負債的本質：條件式負債，取決於未來不確定事件是否發生",
    isCalc: false,
    explain:
      "或有負債（Contingent Liability）的本質定義：\n\n「一種可能發生，但未必發生的負債」（D）✓\n\n或有負債的特性：\n• 其發生與否，取決於未來某一不確定事件\n• 如：未決訴訟（輸官司才需賠款）、產品保固（產品壞才要維修）\n\n與其他選項的區別：\n(A) 流動負債：確定性的短期負債（如應付帳款）\n(B) 長期負債：確定性的長期負債（如公司債）\n(C) 估計負債（Estimated Liability）：金額不確定但發生很確定（如壞帳準備、折舊）\n\n或有負債是「發生本身」不確定，而非只是金額不確定。\n\n→ 答案 (D)",
  },
    {
    id: "acc-41",
    q: "「X2 年 2 月 8 日本公司光華廠發生大火，廠房及設備受到嚴重焚燬，此項財產雖均可得到保險理賠，惟修復期間之營業損失，未在理賠範圍之內，其金額亦無法估計。」X1 年財務報表中之附註揭露，最有可能描述：",
    opts: [
      "(A) 很有可能發生之或有事項",
      "(B) 有可能發生之或有事項",
      "(C) 估計負債",
      "(D) 期後事項",
    ],
    ans: 3,
    hint: "🔑 火災發生在 X1 年「報表日後」→ 期後事項；在 X1 年附註揭露",
    isCalc: false,
    explain:
      "判斷關鍵：事件發生的時點\n\n• X1 年財務報表（通常為 12/31）\n• 火災發生：X2 年 2 月 8 日（報表日「之後」）\n\n→ 這是「期後事項（Subsequent Event）」\n• 發生在 X1 年資產負債表日後\n• 但在財務報表核准發布前\n• 不調整 X1 報表數字，但需在附註揭露\n\n期後事項類型：\n• 調整型：提供 X1 年已存在狀況的進一步證據（需調整報表）\n• 非調整型：X1 年後才發生的新事件（只需附註揭露）→ 本題屬此類\n\n→ 答案 (D)：期後事項",
  },
    {
    id: "acc-42",
    q: "下列何種投資於其公平價值發生變化時，應認列損益並表達於損益表中？",
    opts: [
      "(A) 備供出售股權投資",
      "(B) 交易目的之股權投資",
      "(C) 持有至到期日之投資",
      "(D) 權益法股權投資",
    ],
    ans: 1,
    hint: "🔑 交易目的（短線買賣）→ 公允價值變動直接入損益表",
    isCalc: false,
    explain:
      "金融資產計量與損益表的關係：\n\n① 交易目的（FVTPL）(B) ✓：\n   公允價值變動 → 直接認列「當期損益（損益表）」\n   目的：短期獲利，頻繁買賣\n\n② 備供出售（FVOCI）(A)：\n   公允價值變動 → 認列「其他綜合損益（OCI）」\n   在股東權益中顯示，不影響當期損益表\n   （IFRS 9 後改為 FVOCI）\n\n③ 持有至到期日（HTM）(C)：\n   以攤銷後成本衡量，不認列公允價值變動（僅認列利息收入）\n\n④ 權益法（D）：\n   按持股比例認列被投資公司損益，非按公允價值變動\n\n→ 答案 (B)：交易目的股權投資",
  },
    {
    id: "acc-43",
    q: "採用零用金制度時，下列何種情況下不須作分錄？",
    opts: [
      "(A) 零用金設立時",
      "(B) 零用金支用時",
      "(C) 帳戶餘額增減時",
      "(D) 以上皆非",
    ],
    ans: 1,
    hint: "🔑 定額零用金：設立時作分錄，支用時只留備忘，撥補時才作費用分錄",
    isCalc: false,
    explain:
      "定額零用金制度各環節的會計處理：\n\n(A) 設立時 → 須作分錄：\n   借：零用金 / 貸：現金\n\n(B) 支用時 → 「不須作分錄」✓，只保留收據作備忘記錄\n   這是定額零用金制度的核心特色\n\n(C) 帳戶餘額增減時 → 須作分錄：\n   增加：借：零用金 / 貸：現金\n   減少：借：現金 / 貸：零用金\n\n撥補時（非選項但重要）→ 須作分錄：\n   借：各費用科目（依收據）/ 貸：現金\n\n→ 答案 (B)：零用金支用時不須作分錄",
  },
    {
    id: "acc-44",
    q: "股票股利發放日，企業股東權益受到之影響為：",
    opts: [
      "(A) 股東權益總額減少，每股帳面價值減少",
      "(B) 股東權益總額減少，每股帳面價值不變",
      "(C) 股東權益總額不變，每股帳面價值減少",
      "(D) 股東權益總額不變，每股帳面價值不變",
    ],
    ans: 2,
    hint: "🔑 股票股利：總額不變（內部重分配）；股數增加 → 每股帳面價值下降",
    isCalc: false,
    explain:
      "股票股利對股東權益的影響（以發放日為例）：\n\n發放分錄：\n借：應發放股票股利（原宣告時設的科目）\n貸：普通股股本（股數增加）\n\n分析：\n✓ 股東權益總額不變：只是在股東權益內部將「應發放股票股利」轉入「股本」，總額不變\n✓ 每股帳面價值減少：\n   發行新股增加流通股數，但總股東權益不變\n   每股帳面價值 = 股東權益總額 ÷ 流通股數\n   股數增加（分母增大）→ 每股帳面價值下降\n\n→ 答案 (C)：總額不變，每股帳面價值減少",
  },
    {
    id: "acc-45",
    q: "若發行面額 $10 之股票 50,000 股，取得現金 $600,000，則下列股票發行之相關會計處理，何者錯誤？",
    opts: [
      "(A) 借記「現金」$600,000",
      "(B) 貸記「普通股股本」$500,000",
      "(C) 貸記「資本公積─普通股發行溢價」$100,000",
      "(D) 資產負債表股東權益中列示投入資本 $500,000",
    ],
    ans: 3,
    hint: "🔑【計算題】投入資本 = 股本 + 資本公積；$500,000 + $100,000 = $600,000",
    isCalc: true,
    explain:
      "【計算分析—股票發行溢價】\n\n發行資料：\n• 面額 $10 × 50,000 股 = 股本 $500,000\n• 實收現金 $600,000\n• 溢價 = $600,000 − $500,000 = $100,000\n\n會計分錄：\n借：現金 $600,000 (A) ✓\n貸：普通股股本 $500,000 (B) ✓\n貸：資本公積－發行溢價 $100,000 (C) ✓\n\n(D) 的錯誤：\n投入資本（Paid-in Capital）= 股本 + 資本公積\n= $500,000 + $100,000 = $600,000（非 $500,000）\n\n資本公積（溢價）是股東「額外」投入的資本，應計入投入資本，故投入資本 = $600,000。\n\n→ 答案 (D)：投入資本應為 $600,000，非 $500,000",
  },
    {
    id: "acc-46",
    q: "下列何項不會出現在企業的損益表？",
    opts: [
      "(A) 會計原則變動的累積影響數",
      "(B) 庫藏股票",
      "(C) 存貨跌價損失",
      "(D) 外幣兌換損失",
    ],
    ans: 1,
    hint: "🔑 庫藏股票是股東權益的「抵減項」，出現在資產負債表，不在損益表",
    isCalc: false,
    explain:
      "各項目所在財務報表：\n\n損益表的項目：\n✓ 會計原則變動累積影響數（A）：列示於損益表中\n✓ 存貨跌價損失（C）：屬費用，列損益表\n✓ 外幣兌換損失（D）：屬費用，列損益表\n\n資產負債表的項目：\n✗ 庫藏股票（B）：\n   公司買回自己的股票，是股東權益的「抵減」項目\n   資產負債表：\n   普通股股本 $XXX\n   資本公積   $XXX\n   保留盈餘   $XXX\n   「減：庫藏股票 ($XXX)」\n   → 出現在資產負債表股東權益區塊，不在損益表\n\n→ 答案 (B)：庫藏股票",
  },
    {
    id: "acc-47",
    q: "沖銷陳廢過時的存貨，對公司的流動性有何影響？",
    opts: [
      "(A) 增加速動比率",
      "(B) 減少速動比率",
      "(C) 增加營運資金",
      "(D) 減少流動比率",
    ],
    ans: 3,
    hint: "🔑 沖銷存貨 → 存貨（流動資產）↓ → 流動比率↓；速動資產不含存貨，速動比率不受影響",
    isCalc: false,
    explain:
      "沖銷存貨（Write-off Obsolete Inventory）的影響：\n\n分錄：\n借：存貨跌價損失（費用）\n貸：存貨（流動資產↓）\n\n對各比率的影響：\n\n流動比率 = 流動資產 ÷ 流動負債\n→ 存貨↓（流動資產↓）→ 分子減少 → 流動比率「減少」(D) ✓\n\n速動比率 = (流動資產 − 存貨 − 預付費用) ÷ 流動負債\n→ 存貨本來就不計入速動資產\n→ 沖銷存貨不影響速動資產 → 速動比率「不變」（A、B 均錯）\n\n營運資金 = 流動資產 − 流動負債\n→ 流動資產↓ → 營運資金「減少」（C 說增加，錯誤）\n\n→ 答案 (D)：流動比率減少",
  },
    {
    id: "acc-48",
    q: "內部控制主要在提高會計記錄的正確性與可靠性，並保護資產的安全，良好現金管理通則包含：",
    opts: [
      "(A) 現金保管與會計記錄工作應由不同人負責擔任",
      "(B) 任何重大交易應避免由一人或一部門負責完成，以利相互核對勾稽",
      "(C) 現金作業集中收付，且立即記入帳冊，銀行調節表應定期由出納以外人員編製或覆核",
      "(D) 以上皆是",
    ],
    ans: 3,
    hint: "🔑 良好現金內控三要素：職責分工、複核機制、獨立編表",
    isCalc: false,
    explain:
      "內部控制（Internal Control）的現金管理原則：\n\n(A) 職責分離（Segregation of Duties）：\n• 管錢（保管）與管帳（記錄）分開 → 相互制衡，防止舞弊\n\n(B) 相互核對（Cross-checking）：\n• 重大交易需多人/多部門參與 → 避免單人操縱，形成勾稽\n\n(C) 獨立編表與覆核：\n• 銀行調節表由「出納以外」人員編製或覆核 → 防止出納偽造或挪用現金後自行掩飾\n\n三者都是良好現金內控的重要原則，缺一不可。\n\n→ 答案 (D)：以上皆是",
  },
    {
    id: "acc-49",
    q: "資本支出記錄為：",
    opts: [
      "(A) 費用",
      "(B) 資產",
      "(C) 負債",
      "(D) 所得",
    ],
    ans: 1,
    hint: "🔑 資本支出（Capital Expenditure）= 資本化 = 記為資產再逐期折舊",
    isCalc: false,
    explain:
      "支出的分類：\n\n• 資本支出（Capital Expenditure）：\n  增加資產的未來經濟效益、延長使用年限或提升生產能力的支出\n  → 記錄為「資產（B）」，再逐期提列折舊費用\n  例：購買新機器、廠房增建、主要改良\n\n• 收益支出（Revenue Expenditure）：\n  維持資產正常運作、效益在當期用完的支出\n  → 直接記錄為「費用（A）」\n  例：日常保養、修理、清潔\n\n正確資本化 vs 費用化，影響報表的準確性：\n• 不當費用化 → 低估資產、高估費用、低估當期利益\n• 不當資本化 → 高估資產、低估費用、高估當期利益\n\n→ 答案 (B)：資產",
  },
    {
    id: "acc-50",
    q: "零用金制度的主要目的為：",
    opts: [
      "(A) 正確性",
      "(B) 方便性",
      "(C) 內部控制",
      "(D) 穩健原則",
    ],
    ans: 1,
    hint: "🔑 零用金：處理日常少額雜項支出，避免為小額支出開立支票的麻煩",
    isCalc: false,
    explain:
      "零用金制度（Petty Cash System）的目的：\n\n主要目的：「方便性（B）」\n• 企業日常有許多小額零星支出（車費、郵資、文具等）\n• 若每筆都要開立支票，效率極低且不實際\n• 零用金制度提供少額現金，方便直接支付\n\n雖然零用金也有一些內部控制功能（如保管人制度），但設立零用金的「主要目的」是方便性，而非內部控制。事實上，大量使用現金往往是內控的弱點，零用金通常有嚴格的金額上限。\n\n→ 答案 (B)：方便性",
  },
    {
    id: "acc-51",
    q: "企業必須重視內部控制制度，下列何者是有關現金交易必需要求事項？",
    opts: [
      "(A) 所有的零星支出均一律以支票支付",
      "(B) 核准付款與開立支票的工作，勿由同一人處理",
      "(C) 指派誠實的員工負責現金交易作業",
      "(D) 為節省人力，管錢管帳可由同一個人負責",
    ],
    ans: 1,
    hint: "🔑 內控核心：職責分離（授權、執行、記錄三者分開）",
    isCalc: false,
    explain:
      "現金內部控制的核心要求：\n\n✓ (B) 核准（授權）與開票（執行）分開：\n  這是「職責分離原則（Segregation of Duties）」的體現\n  • 若同一人可以核准又開票，可能自行核准非法支出\n  • 分由不同人執行，形成相互制衡\n\n各選項的錯誤：\n✗ (A) 零星小額費用不必全用支票（設零用金更方便）\n✗ (C) 光靠員工「誠實」不夠，需要制度約束（管理上依賴個人品德，非良好內控）\n✗ (D) 管錢管帳由同一人 → 完全違反職責分離原則，極易發生舞弊\n\n→ 答案 (B)",
  },
    {
    id: "acc-52",
    q: "若發行普通股的金額超出股票面額，則超出的部分應貸記：",
    opts: [
      "(A) 現金",
      "(B) 保留盈餘",
      "(C) 資本公積",
      "(D) 股本",
    ],
    ans: 2,
    hint: "🔑 超過面額部分 = 發行溢價 → 貸記「資本公積－普通股發行溢價」",
    isCalc: false,
    explain:
      "股票發行溢價的處理：\n\n面額 $10，以 $15 發行：\n借：現金 $15\n貸：普通股股本 $10（面額部分）\n貸：資本公積─普通股發行溢價 $5（超出面額部分）(C) ✓\n\n為何不貸記：\n(A) 現金：現金是借方（收到的資金）\n(B) 保留盈餘：保留盈餘是來自營業盈餘的累積，非股東投入\n(D) 股本：只記面額，超過面額部分不算入股本\n\n資本公積（Additional Paid-in Capital / Share Premium）的特點：\n• 股東對公司的額外投入\n• 非來自盈餘，不可用於彌補虧損或發放股利（部分法規例外）\n\n→ 答案 (C)：資本公積",
  },
    {
    id: "acc-53",
    q: "下列那一種分錄為企業可自由選擇是否要編製的：",
    opts: [
      "(A) 調整分錄",
      "(B) 結帳分錄",
      "(C) 轉回分錄",
      "(D) 更正分錄",
    ],
    ans: 2,
    hint: "🔑 轉回分錄（Reversing Entry）：是可選擇性的便利工具，非強制",
    isCalc: false,
    explain:
      "四種分錄的強制性比較：\n\n✗ (A) 調整分錄（Adjusting Entry）：強制，必須進行，確保符合應計基礎\n✗ (B) 結帳分錄（Closing Entry）：強制，必須進行，清零虛帳戶\n✗ (D) 更正分錄（Correcting Entry）：發現錯誤就必須更正，非自由選擇\n\n✓ (C) 轉回分錄（Reversing Entry）：\n  • 在新期初（下期1月1日）將某些調整分錄反向沖回\n  • 目的：方便後續的日常記帳（避免分割計算）\n  • 非強制，企業可自由選擇是否使用\n  • 常用於：預計費用/收入的調整分錄（如應計費用、應計收入）\n\n→ 答案 (C)：轉回分錄",
  },
    {
    id: "acc-54",
    q: "分類帳之功用為：",
    opts: [
      "(A) 表達各項交易的情形",
      "(B) 顯示各會計科目的增減變化",
      "(C) 作為過帳之依據",
      "(D) 偵查分錄記錄之錯誤",
    ],
    ans: 1,
    hint: "🔑 分類帳（Ledger）= T帳戶：以科目為單位，顯示每個科目的增減變化與餘額",
    isCalc: false,
    explain:
      "會計帳簿的功用：\n\n• 日記帳（Journal）= 序時帳：\n  按交易「發生時間」順序記錄每筆交易 → (A) 表達各項交易情形\n  也是過帳的「依據（C）」，但分類帳是「結果」而非「依據」\n\n• 分類帳（Ledger）：\n  按「會計科目」分類整理，顯示每個科目的借貸增減與期末餘額\n  → (B) 顯示各會計科目的增減變化 ✓\n\n• 試算表（Trial Balance）：\n  用於(D) 偵查分錄是否借貸平衡（但無法偵查科目分類錯誤）\n\n→ 答案 (B)：顯示各會計科目的增減變化",
  },
    {
    id: "acc-55",
    q: "預收收入之調整會：",
    opts: [
      "(A) 減少負債，增加收入",
      "(B) 增加資產，減少負債",
      "(C) 增加資產，增加收入",
      "(D) 減少收入，減少資產",
    ],
    ans: 0,
    hint: "🔑 預收收入期末調整：服務已完成部分從「負債」轉為「收入」",
    isCalc: false,
    explain:
      "預收收入（Unearned Revenue）的期末調整：\n\n情境：期初收到預收款，年底服務已完成一部分\n\n調整分錄：\n借：預收收入（負債減少）\n貸：服務收入（收入增加）\n\n分析：\n✓ (A) 減少負債（預收收入↓），增加收入 ✓\n\n驗證其他選項：\n✗ (B) 增加資產 → 調整不影響資產（現金已在期初收到，不再變動）\n✗ (C) 增加資產 → 同上，不影響資產\n✗ (D) 減少收入 → 調整是認列（增加）收入，而非減少\n\n→ 答案 (A)：減少負債，增加收入",
  },
    {
    id: "acc-56",
    q: "期末盤點存貨時，若重複計算某批商品，致期末存貨高估，則會導致該公司當年度的：",
    opts: [
      "(A) 銷貨毛利高估",
      "(B) 淨利低估",
      "(C) 保留盈餘不變",
      "(D) 營運資金低估",
    ],
    ans: 0,
    hint: "🔑 期末存貨高估 → 銷貨成本低估 → 毛利高估 → 淨利高估",
    isCalc: false,
    explain:
      "【連鎖影響—期末存貨高估（重複計算）】\n\n銷貨成本 = 期初存貨 + 進貨 − 期末存貨(高估)\n→ 銷貨成本「低估」（扣除的期末存貨虛增）\n\n銷貨毛利 = 銷貨收入 − 銷貨成本(低估)\n→ 銷貨毛利「高估」(A) ✓\n\n淨利 = 毛利(高估) − 費用\n→ 淨利「高估」（B 說低估，錯誤）\n\n保留盈餘 = 期初保留盈餘 + 淨利(高估)\n→ 保留盈餘「高估」（C 說不變，錯誤）\n\n營運資金 = 流動資產(含存貨高估) − 流動負債\n→ 流動資產高估 → 營運資金「高估」（D 說低估，錯誤）\n\n→ 答案 (A)：銷貨毛利高估",
  },
    {
    id: "acc-57",
    q: "下列何項資產為無法明確辨認之無形資產：",
    opts: [
      "(A) 特許權",
      "(B) 著作權",
      "(C) 商譽",
      "(D) 專利權",
    ],
    ans: 2,
    hint: "🔑 商譽是唯一無法單獨辨認的無形資產，依附於整個企業而存在",
    isCalc: false,
    explain:
      "無形資產的可辨認性：\n\n可明確辨認的無形資產（可單獨交易或分離）：\n✓ 特許權（A）：政府授予的特定業務經營權\n✓ 著作權（B）：可單獨授權或出售\n✓ 專利權（D）：可單獨申請、授權、出售\n其他：商標權、客戶名單、電腦軟體\n\n無法明確辨認的無形資產：\n✗ 商譽（C）：\n• 代表企業超額獲利能力、品牌聲譽、客戶忠誠度等的綜合體現\n• 只能在企業「整體」購買（企業合併）時才能確認\n• 無法從企業中單獨分離或出售\n• 企業存在，商譽才存在；企業消失，商譽也消失\n\n→ 答案 (C)：商譽",
  },
    {
    id: "acc-58",
    q: "調整後試算表上，業主資本帳戶的餘額為：",
    opts: [
      "(A) 零",
      "(B) 期末資本帳戶的餘額",
      "(C) 結帳分錄前資本帳戶的餘額",
      "(D) 結帳分錄後資本帳戶的餘額",
    ],
    ans: 2,
    hint: "🔑 調整後試算表：在調整分錄後、結帳分錄前的試算；資本帳戶尚未納入本期損益",
    isCalc: false,
    explain:
      "會計流程時序（按順序）：\n\n1. 日記帳記錄交易\n2. 過帳到分類帳\n3. 調整前試算表\n4. 調整分錄（應計、遞延等）\n5. ★「調整後試算表」← 本題的時點\n6. 編製財務報表\n7. 結帳分錄（清零收入、費用，轉入資本）\n8. 結帳後試算表\n\n調整後試算表（步驟 5）的時間點：\n• 已完成調整分錄\n• 但「尚未」做結帳分錄\n\n因此：\n• 業主資本帳戶餘額 = 結帳「前」的餘額（含期初資本 + 本期業主增資，但尚未含本期損益）\n• 收入、費用帳戶仍有餘額（尚未結清）\n\n→ 答案 (C)：結帳分錄前的資本帳戶餘額",
  },
    {
    id: "acc-59",
    q: "配合原則與下列那一張財務報表之關係最大？",
    opts: [
      "(A) 綜合損益表",
      "(B) 資產負債表",
      "(C) 保留盈餘表",
      "(D) 現金流量表",
    ],
    ans: 0,
    hint: "🔑 配合原則決定收入和費用的認列時點 → 直接影響損益計算 → 損益表",
    isCalc: false,
    explain:
      "配合原則（Matching Principle）的直接影響：\n\n配合原則規範「費用與收入的認列時點」：\n→ 確保費用在帶來相關收入的同一期間認列\n→ 使當期「淨利」的計算正確\n\n與損益表（綜合損益表）的關係最直接：\n• 損益表 = 收入 − 費用 = 淨利\n• 配合原則正是決定哪些收入和費用應列入「本期」損益表的核心原則\n\n其他報表的關係：\n• 資產負債表（B）：反映配合原則的結果（如預付費用、應計費用），但主要關係在損益表\n• 現金流量表（D）：以現金為基礎，與配合原則（應計基礎）方向不同\n\n→ 答案 (A)：綜合損益表",
  },
    {
    id: "acc-60",
    q: "閒置廠房設備在資產負債表上應列為：",
    opts: [
      "(A) 流動資產",
      "(B) 不動產、廠房與設備",
      "(C) 無形資產",
      "(D) 其他資產",
    ],
    ans: 3,
    hint: "🔑 不再供正常營業使用的廠房 → 不符合 PPE 定義 → 重新分類為「其他資產」",
    isCalc: false,
    explain:
      "不動產、廠房及設備（PPE）的認列條件之一：「供營業使用」\n\n若廠房設備停止使用（閒置）：\n• 不再符合「供營業使用」的條件\n• 不得繼續列示為 PPE\n• 應重新分類為「其他資產（D）」\n\n同時還需要考慮：\n• 評估是否有減損（Impairment），若閒置代表可能無法回收帳面價值\n• 若計畫出售，且符合 IFRS 5 條件，應改列「持有供出售資產」\n\n→ 答案 (D)：其他資產",
  },
    {
    id: "acc-61",
    q: "庫藏股為：",
    opts: [
      "(A) 資產加項",
      "(B) 資產減項",
      "(C) 權益加項",
      "(D) 權益減項",
    ],
    ans: 3,
    hint: "🔑 庫藏股：公司買回自己發行的股票 → 股東權益（投入資本）減少",
    isCalc: false,
    explain:
      "庫藏股（Treasury Stock）的性質：\n\n公司買回自己的股票時（以成本法）：\n借：庫藏股（股東權益的「抵減」帳戶）\n貸：現金\n\n→ 庫藏股不是資產（公司不能「擁有」自己），而是股東權益的「減項」\n→ 在資產負債表股東權益區列示為：\n  普通股股本 $XXX\n  保留盈餘   $XXX\n  「減：庫藏股 ($XXX)」\n\n庫藏股的用途：\n• 提高每股盈餘（EPS）\n• 員工股票選擇權計畫的股票來源\n• 未來重新出售獲利\n\n→ 答案 (D)：權益減項",
  },
    {
    id: "acc-62",
    q: "股票股利之宣布與發放不影響：",
    opts: [
      "(A) 權益變動表",
      "(B) 保留盈餘表",
      "(C) 資產負債表",
      "(D) 損益表",
    ],
    ans: 3,
    hint: "🔑 股票股利：保留盈餘轉入股本，均在股東權益內；不影響收入費用 → 不影響損益表",
    isCalc: false,
    explain:
      "股票股利對各報表的影響：\n\n宣告股票股利分錄：\n借：保留盈餘（OE↓）\n貸：應發放股票股利（OE↑）\n\n發放股票股利分錄：\n借：應發放股票股利（OE↓）\n貸：普通股股本（OE↑）\n\n影響的報表：\n✓ (A) 權益變動表：股東權益內部組成改變，有影響\n✓ (B) 保留盈餘表：保留盈餘因宣告而減少，有影響\n✓ (C) 資產負債表：股東權益各科目金額改變，有影響\n\n不影響的報表：\n✗ (D) 損益表：股票股利不是費用、不是收入，不影響損益的計算\n\n→ 答案 (D)：損益表",
  },
    {
    id: "acc-63",
    q: "客戶在銀行之存款帳戶，為銀行之：",
    opts: [
      "(A) 資產",
      "(B) 負債",
      "(C) 資本",
      "(D) 收益",
    ],
    ans: 1,
    hint: "🔑 銀行觀點：存款 = 銀行向客戶「借」的錢 → 銀行的負債",
    isCalc: false,
    explain:
      "同一存款帳戶，不同視角的科目分類：\n\n客戶（存款人）視角：\n• 在銀行的存款 → 資產（我的錢在銀行，隨時可提）\n\n銀行視角：\n• 客戶的存款 → 銀行欠客戶的錢，客戶隨時可來提款\n  → 銀行的「負債（B）」✓\n\n這個概念很重要，說明同一筆錢對不同主體，會計科目完全不同。\n\n銀行資產負債表結構：\n資產：放款、投資、準備金\n負債：各類存款（客戶的存款）、發行的債券\n股東權益：股本、保留盈餘\n\n→ 答案 (B)：負債",
  },
    {
    id: "acc-64",
    q: "結帳前，以下各帳戶都有餘額，則下列何者最可能有誤？",
    opts: [
      "(A) 存貨帳戶有借方餘額",
      "(B) 應付公司債帳戶有貸方餘額",
      "(C) 普通股股本帳戶有借方餘額",
      "(D) 銷貨收入帳戶有貸方餘額",
    ],
    ans: 2,
    hint: "🔑 普通股股本（增加記貸方）→ 正常餘額為「貸方」→ 有借方餘額表示記錄有誤",
    isCalc: false,
    explain:
      "各科目的正常餘額：\n\n• 資產類：借方餘額（增加記借方）\n  → 存貨（A）：借方餘額 ✓ 正常\n\n• 負債類：貸方餘額（增加記貸方）\n  → 應付公司債（B）：貸方餘額 ✓ 正常\n\n• 股東權益類：貸方餘額（增加記貸方）\n  → 普通股股本（C）：應為「貸方餘額」\n    有「借方餘額」→ 異常，最可能有誤 ✓\n    （除非是企業虧損極嚴重導致股本呈負，但一般情況下不應有借餘）\n\n• 收入類：貸方餘額（增加記貸方）\n  → 銷貨收入（D）：貸方餘額 ✓ 正常\n\n→ 答案 (C)：普通股股本有借方餘額最可能有誤",
  },
    {
    id: "acc-65",
    q: "配合原則用於下列何種帳戶之認列基礎？",
    opts: [
      "(A) 費用",
      "(B) 資產",
      "(C) 負債",
      "(D) 業主權益",
    ],
    ans: 0,
    hint: "🔑 配合原則直接決定費用的認列時點：費用應在相關收入的同一期間認列",
    isCalc: false,
    explain:
      "配合原則（Matching Principle）的適用對象：\n\n配合原則是「費用認列」的基礎：\n• 費用（A）✓：應在帶來相關收入的同一期間認列，而非在現金支出時\n  例：折舊費用（資產使用期間認列）、銷貨成本（銷售時認列）\n\n其他帳戶的認列基礎：\n• 資產（B）：成本原則（取得成本入帳），非配合原則\n• 負債（C）：在義務發生時認列，非配合原則\n• 業主權益（D）：由資產負債差額決定，非配合原則\n\n收入的認列依「收入認列原則」，費用的認列則依「配合原則」，二者相輔相成。\n\n→ 答案 (A)：費用",
  },
    {
    id: "acc-66",
    q: "甲公司在編製完成銀行往來調節表後，下列何者需於公司帳上作調整分錄？（同題型第二次考察）",
    opts: [
      "(A) 銀行手續費",
      "(B) 銀行誤將兌付他公司支票誤記為該公司帳戶",
      "(C) 在途存款",
      "(D) 未兌現支票",
    ],
    ans: 0,
    hint: "🔑 銀行已知悉、但公司帳尚未記錄的項目 → 需在公司帳作調整",
    isCalc: false,
    explain:
      "銀行往來調節表（Bank Reconciliation）的調整原則：\n\n需「公司帳」調整的項目（銀行知道但公司不知道）：\n✓ 銀行手續費（A）：銀行已扣款，公司尚未入帳 → 補作費用分錄\n✓ 銀行代收款：銀行已收到，公司未知\n✓ 銀行利息：銀行已計入，公司未記\n✓ 存款不足退票：銀行已退回，公司未知\n\n需「銀行帳」調整（通知銀行更正，公司不用動）：\n✗ 銀行誤記（B）：是銀行錯誤，請銀行更正即可\n✗ 在途存款（C）：公司已記，等銀行到帳\n✗ 未兌現支票（D）：公司已記，等銀行兌付\n\n→ 答案 (A)：銀行手續費",
  },
    {
    id: "acc-67",
    q: "下列何項不屬營業資產？（同題型第二次考察）",
    opts: [
      "(A) 商譽",
      "(B) 償債基金",
      "(C) 專利權",
      "(D) 土地",
    ],
    ans: 1,
    hint: "🔑 償債基金是為未來還債設立的專款投資，與日常營業無關",
    isCalc: false,
    explain:
      "資產分類複習：\n\n營業資產（用於日常業務產生收益）：\n✓ 商譽（A）：企業獲利能力的超額體現 → 營業\n✓ 專利權（C）：用於生產或提供服務 → 營業\n✓ 土地（D）：廠房所在地 → 營業\n\n非營業資產（特定目的或財務性投資）：\n✗ 償債基金（B）：\n   • 企業將資金提撥至專戶，用於到期時償還特定長期債務\n   • 資金受限於特定用途，不可用於日常業務\n   • 分類為「長期投資」或「其他資產」，非營業資產\n\n→ 答案 (B)：償債基金",
  },
    {
    id: "acc-68",
    q: "下列科目何者的正常餘額為借方餘額：",
    opts: [
      "(A) 應付租金",
      "(B) 預收租金",
      "(C) 租金收入",
      "(D) 預付租金",
    ],
    ans: 3,
    hint: "🔑 借方正常餘額：資產類（含預付費用）、費用類科目",
    isCalc: false,
    explain:
      "各科目的正常餘額判斷（依科目性質）：\n\n✗ (A) 應付租金：負債類（應付而未付），增加記貸方 → 貸方正常餘額\n✗ (B) 預收租金：負債類（收到但尚未服務），增加記貸方 → 貸方正常餘額\n✗ (C) 租金收入：收益類，增加記貸方 → 貸方正常餘額\n\n✓ (D) 預付租金：\n• 已支付但尚未到期的租金 → 「資產類」（預付費用）\n• 資產增加記借方 → 「借方正常餘額」 ✓\n• 期末到期時，調整借：租金費用 / 貸：預付租金\n\n→ 答案 (D)：預付租金（借方正常餘額）",
  },
    {
    id: "acc-69",
    q: "銷貨淨額為：",
    opts: [
      "(A) 銷貨收入加銷貨折扣",
      "(B) 銷貨收入減銷貨成本",
      "(C) 銷貨收入減銷貨折扣以及銷貨退回與折讓",
      "(D) 銷貨收入減應收帳款",
    ],
    ans: 2,
    hint: "🔑 銷貨淨額 = 銷貨總額 − 銷貨折扣 − 銷貨退回與折讓",
    isCalc: false,
    explain:
      "銷貨淨額（Net Sales）的計算公式：\n\n銷貨總額\n− 銷貨退回（Sales Returns）：客戶退貨\n− 銷貨折讓（Sales Allowances）：因品質瑕疵給予的價格減讓\n− 銷貨折扣（Sales Discounts）：鼓勵提早付款的現金折扣\n= 銷貨淨額（C）✓\n\n注意各項目的意義：\n• 銷貨折扣 vs 商業折扣：商業折扣直接從定價扣除，不另行記錄；現金折扣才另記\n• 銷貨淨額不含扣除「銷貨成本（B）」—扣了成本才是毛利，不是淨額\n\n→ 答案 (C)",
  },
    {
    id: "acc-70",
    q: "每一企業的活動，必須與其業主及其他企業的活動分開，並作區隔。此假設稱之為：",
    opts: [
      "(A) 貨幣單位假設",
      "(B) 經濟個體假設",
      "(C) 繼續經營假設",
      "(D) 會計期間假設",
    ],
    ans: 1,
    hint: "🔑 企業與業主財務分開 → 經濟個體假設（獨立主體原則）",
    isCalc: false,
    explain:
      "四大基本會計假設：\n\n(A) 貨幣單位假設（Monetary Unit Assumption）：\n  以貨幣（如新台幣）作為統一計量單位\n\n(B) 經濟個體假設（Economic Entity Assumption）✓：\n  每個企業是獨立的經濟個體\n  企業財務必須與業主個人財務完全分開記帳\n  例：老闆個人的房貸不可記入公司帳\n\n(C) 繼續經營假設（Going Concern Assumption）：\n  假設企業在可預見未來將持續營運，不會清算\n\n(D) 會計期間假設（Periodicity Assumption）：\n  將企業的持續活動分割為等距的會計期間（如季、年）\n\n→ 答案 (B)：經濟個體假設",
  },
    {
    id: "acc-71",
    q: "買回庫藏股票對資產負債表的影響為：",
    opts: [
      "(A) 資產增加",
      "(B) 股東權益減少",
      "(C) 負債增加",
      "(D) 負債減少",
    ],
    ans: 1,
    hint: "🔑 用現金買回股票：現金（資產↓）換庫藏股（股東權益↓），等式維持平衡",
    isCalc: false,
    explain:
      "買回庫藏股的會計分錄（成本法）：\n\n借：庫藏股（股東權益的「抵減帳戶」↑ = 股東權益↓）\n貸：現金（資產↓）\n\n對資產負債表的影響：\n✓ (B) 股東權益減少：庫藏股增加 → 抵減股東權益 ✓\n✓ 資產同時減少（現金流出）\n\n恆等式驗證：\n資產↓ = 負債（不變）+ 股東權益↓ ✓ 等式仍平衡\n\n錯誤選項：\n✗ (A) 資產「增加」：錯誤，資產（現金）是「減少」\n✗ (C)(D) 負債變動：買回庫藏股不影響負債\n\n→ 答案 (B)：股東權益減少",
  },
    {
    id: "acc-72",
    q: "存貨在資產負債表係以何種方式報導：",
    opts: [
      "(A) 成本法",
      "(B) 市價法",
      "(C) 成本與淨變現價值孰高法",
      "(D) 成本與淨變現價值孰低法",
    ],
    ans: 3,
    hint: "🔑 IFRS（IAS 2）：存貨以成本與淨變現價值（NRV）「孰低」計量",
    isCalc: false,
    explain:
      "存貨計量原則（依 IFRS IAS 2）：\n\n存貨以「成本與淨變現價值（Net Realizable Value, NRV）孰低」計量（D）✓\n\n定義：\n• 成本：購進存貨的取得成本\n• 淨變現價值（NRV）= 估計售價 − 估計完工成本 − 估計銷售費用\n\n應用規則：\n• 若 成本 ≤ NRV → 以成本入帳（正常情況）\n• 若 成本 > NRV → 以 NRV 入帳，認列「存貨跌價損失」\n\n體現的原則：穩健原則（損失提前認列，利益不提前認列）\n\n注意：是「孰低（Lower of Cost or NRV）」，不是孰高（C 錯）\n\n→ 答案 (D)",
  },
    {
    id: "acc-73",
    q: "下列那一帳戶在調整後試算表上之餘額與結帳後試算表上之餘額相同？",
    opts: [
      "(A) 保留盈餘",
      "(B) 銷貨收入",
      "(C) 累計折舊",
      "(D) 折舊費用",
    ],
    ans: 2,
    hint: "🔑 結帳後不清零的帳戶（實帳戶）= 資產、負債、股東權益；累計折舊是資產抵減（實帳戶）",
    isCalc: false,
    explain:
      "結帳的影響：\n\n結帳後會「清零」的帳戶（虛帳戶）：\n✗ 銷貨收入（B）：結帳後清零（轉入保留盈餘）\n✗ 折舊費用（D）：結帳後清零（轉入保留盈餘）\n\n結帳後會「改變」的帳戶：\n✗ 保留盈餘（A）：結帳後加入本期淨損益，餘額「改變」\n\n結帳前後「不變」的帳戶（實帳戶）：\n✓ 累計折舊（C）：\n  • 是固定資產的抵減帳戶（實帳戶）\n  • 不受結帳影響（不是收入費用帳戶）\n  • 調整後試算表的餘額 = 結帳後試算表的餘額 ✓\n\n→ 答案 (C)：累計折舊",
  },
    {
    id: "acc-74",
    q: "應收帳款、應付帳款、存貨金額變動，在現金流量表中屬於何種項目：",
    opts: [
      "(A) 營業活動",
      "(B) 融資活動",
      "(C) 投資活動",
      "(D) 視情況為營業或投資活動",
    ],
    ans: 0,
    hint: "🔑 應收帳款、應付帳款、存貨 = 日常營業相關的流動資產/負債 → 營業活動",
    isCalc: false,
    explain:
      "現金流量表（間接法）中，「營業活動」的組成：\n\n從淨利出發，加減「流動資產和流動負債」的變動：\n\n✓ 應收帳款增加（A）→ 代表有收入但現金未收到 → 減少（-）\n✓ 應收帳款減少 → 現金已收到 → 增加（+）\n✓ 存貨增加 → 購買但未賣出，耗用現金 → 減少（-）\n✓ 應付帳款增加 → 有費用但未付現，少流出現金 → 增加（+）\n\n這些都是日常業務（應收帳款和應付帳款是核心業務收付款，存貨是商品買賣）→ 全屬「營業活動」。\n\n→ 答案 (A)：營業活動",
  },
    {
    id: "acc-75",
    q: "下列何者最可能認列為資本支出？",
    opts: [
      "(A) 每年例行的機器檢修",
      "(B) 修理電話機費用",
      "(C) 粉刷展覽室之支出",
      "(D) 換修工廠屋頂之支出",
    ],
    ans: 3,
    hint: "🔑 資本支出：明顯延長資產使用年限或提升其價值的重大修繕",
    isCalc: false,
    explain:
      "資本支出（Capital Expenditure）vs 收益支出（Revenue Expenditure）：\n\n收益支出（直接費用化）：\n✗ 每年例行機器檢修（A）：維持現有功能，不延長年限 → 修繕費用\n✗ 修理電話機（B）：小額、維修 → 維修費用\n✗ 粉刷展覽室（C）：美觀翻新，非延長資產壽命 → 費用\n\n資本支出（認列為資產）：\n✓ 換修工廠屋頂（D）：\n  • 屋頂是建築物的主要構成部分\n  • 換修屋頂屬「大型修繕」，能延長建築物的使用年限\n  • 金額通常重大\n  → 最可能認列為資本支出（增加建築物資產，再攤提折舊）\n\n→ 答案 (D)：換修工廠屋頂",
  }
  ],
  經濟學: [
    {
    id: "eco-01",
    q: "廠商的機會成本通常是：",
    opts: [
      "(A) 只有外顯成本",
      "(B) 只有隱藏成本",
      "(C) 外顯成本加隱藏成本",
      "(D) 只有會計成本",
    ],
    ans: 2,
    hint: "🔑 機會成本（經濟學）= 外顯成本 + 隱藏成本，比會計成本更廣泛",
    isCalc: false,
    explain:
      "成本概念的比較：\n\n• 會計成本（Accounting Cost）= 外顯成本（Explicit Cost）\n  → 實際支出的貨幣成本（如：薪資、租金、原料費）\n\n• 經濟學機會成本（Opportunity Cost）= 外顯成本 + 隱藏成本\n  → 外顯成本：有形的現金支出\n  → 隱藏成本（Implicit Cost）：放棄的最佳替代用途的價值（如：業主自己的時間、自有廠房的租金市價）\n\n例子：老闆自己經營商店\n• 會計利潤 = 收入 − 外顯成本\n• 經濟利潤 = 收入 − 外顯成本 − 隱藏成本（放棄上班的薪資等）\n\n→ 答案 (C)：外顯成本加隱藏成本",
  },
    {
    id: "eco-02",
    q: "下列關於正常財與劣等財的敘述，何者正確？",
    opts: [
      "(A) 某甲認為的正常財，對某乙來說也一定是正常財",
      "(B) 某甲認為的正常財，對他來說不可能一朝變成劣等財，除非某甲不理性",
      "(C) 消費者願付代價取得正常財，但只有在獲得補貼時，才願意消費劣等財",
      "(D) 需求隨所得提高而增加的必然是正常財，而隨之減少的即是劣等財",
    ],
    ans: 3,
    hint: "🔑 正常財與劣等財的分類標準：所得需求彈性（Income Elasticity）的正負",
    isCalc: false,
    explain:
      "正常財 vs 劣等財的定義：\n\n• 正常財（Normal Goods）：所得增加 → 需求增加（所得彈性 > 0）\n• 劣等財（Inferior Goods）：所得增加 → 需求減少（所得彈性 < 0）\n\n(D) 正確：這是標準的經濟學定義。\n\n錯誤選項分析：\n(A) 錯：正常/劣等的分類因人而異，如泡麵對高收入者可能是劣等財（有錢後改吃餐廳），對低收入者是正常財\n(B) 錯：同一人所得變化到不同階段，同一商品的分類可能改變\n(C) 錯：劣等財也有需求，低所得者會主動購買，不需補貼\n\n→ 答案 (D)",
  },
    {
    id: "eco-03",
    q: "對創造外部效益的廠商進行補貼，此即：",
    opts: [
      "(A) 消費者付費原則",
      "(B) 污染者付費原則",
      "(C) 效用極大原則",
      "(D) 外部效果內部化原則",
    ],
    ans: 3,
    hint: "🔑 正外部性補貼 + 負外部性課稅 = 讓廠商將社會成本/效益納入決策 = 外部效果內部化",
    isCalc: false,
    explain:
      "外部效果內部化（Internalization of Externalities）：\n\n核心概念：透過政府干預，使廠商決策時將外部成本或效益納入考量。\n\n正外部性（如：教育、研發、蜜蜂採蜜）：\n• 社會效益 > 私人效益 → 廠商生產不足\n• 解決方案：政府補貼廠商 → 使廠商增加產量至社會最適水準\n\n負外部性（如：工廠污染）：\n• 社會成本 > 私人成本 → 廠商生產過多\n• 解決方案：課徵庇古稅（Pigouvian Tax）→ 使廠商減少至社會最適產量\n\n兩種做法都是「外部效果內部化」的手段。\n\n→ 答案 (D)：外部效果內部化原則",
  },
    {
    id: "eco-04",
    q: "失業率是景氣循環的：",
    opts: [
      "(A) 領先指標",
      "(B) 落後指標",
      "(C) 同時指標",
      "(D) 擴張指標",
    ],
    ans: 1,
    hint: "🔑 失業率滯後於景氣：企業在景氣確認回升後才增聘，在景氣確認衰退後才裁員",
    isCalc: false,
    explain:
      "景氣循環指標分類：\n\n• 領先指標（Leading Indicators）：景氣轉折前先行變動（預測用）\n  例：股價指數、M1B、建築許可數\n\n• 同時指標（Coincident Indicators）：與景氣同步變動（確認景氣）\n  例：工業生產指數、製造業銷售量\n\n• 落後指標（Lagging Indicators）：景氣轉折後才跟著變動（確認趨勢）\n  ✓ 失業率：企業在景氣確認衰退「之後」才大規模裁員；確認復甦「之後」才增聘\n  → 失業率的變化比景氣轉折晚 3~6 個月\n  其他落後指標：貸款利率、勞工成本\n\n→ 答案 (B)：落後指標",
  },
    {
    id: "eco-05",
    q: "在通貨膨脹時，比較理想的投資工具為：",
    opts: [
      "(A) 定期存款",
      "(B) 現金",
      "(C) 房屋",
      "(D) 債券",
    ],
    ans: 2,
    hint: "🔑 通膨時：實物資產（房地產）隨物價上漲而增值；現金和固定收益受侵蝕",
    isCalc: false,
    explain:
      "通貨膨脹下各資產的表現：\n\n✗ 現金（B）：最差！名目金額不變，但購買力持續下降\n✗ 定期存款（A）：固定名目利率，若利率 < 通膨率，實質報酬為負\n✗ 債券（D）：固定票面利率，通膨侵蝕實質購買力；且通膨→利率↑→債券價格↓\n\n✓ 房屋（C）：最理想！\n• 實物資產，隨整體物價上漲而升值\n• 房租收入也隨通膨調升\n• 歷史上，房地產是最有效的通膨對沖工具之一\n其他好的通膨避險工具：黃金、原物料商品、抗通膨債券（TIPS）\n\n→ 答案 (C)：房屋",
  },
    {
    id: "eco-06",
    q: "2018 年 8 月製造業採購經理人指數（PMI）微跌 0.1 個百分點至 56.0%，表示：",
    opts: [
      "(A) 製造業景氣正處於擴張期",
      "(B) 製造業景氣正處於緊縮期",
      "(C) 製造業景氣呈現擴張",
      "(D) 製造業景氣持平",
    ],
    ans: 0,
    hint: "🔑 PMI > 50% → 景氣擴張；< 50% → 景氣收縮；56% 遠高於 50%，處於擴張期",
    isCalc: false,
    explain:
      "採購經理人指數（Purchasing Managers' Index, PMI）解讀：\n\n• PMI > 50%：製造業景氣「擴張」（廠商訂單、產出、僱用增加）\n• PMI = 50%：景氣持平\n• PMI < 50%：製造業景氣「收縮」\n\n本題：PMI = 56.0%（雖微跌 0.1 個百分點，但仍遠高於 50%）\n→ 製造業景氣「仍處於擴張期」\n\n選項辨析：\n(A) 「正處於擴張期」✓：描述「當前所處的狀態」，正確\n(C) 「呈現擴張」：描述「正在進行擴張動作」，但 PMI 微跌代表擴張「動能略有減緩」，選項表達不完整\n\n→ 答案 (A)：製造業景氣正處於擴張期",
  },
    {
    id: "eco-07",
    q: "有關消費者剩餘的描述，下列何者描最完整？",
    opts: [
      "(A) 消費者願意購買，一定是「物超所值」",
      "(B) 「邊際效益」超過市價，那個差額就是消費者剩餘",
      "(C) 消費者願意支付的代價，大於實際所支付的成本，超過的差額稱之為消費者剩餘",
      "(D) 以上皆是",
    ],
    ans: 3,
    hint: "🔑 消費者剩餘 = 願付最高價格（保留價格）− 實際市場價格",
    isCalc: false,
    explain:
      "消費者剩餘（Consumer Surplus）的完整描述：\n\n(A) 「物超所值」：消費者只在自認物超所值時才會購買 ✓（部分正確）\n\n(B) 邊際效益超過市價：\n   每單位消費的邊際效益 > 市場價格 → 產生剩餘\n   ✓（部分正確，針對邊際量的說明）\n\n(C) 最完整定義：\n   消費者剩餘 = 願意支付的最高金額（保留價格）− 實際支付的市場價格\n   ✓（這是標準定義）\n\n三個描述從不同角度說明同一概念，都部分正確：\n(A) 直觀說法，(B) 邊際分析，(C) 標準定義\n\n→ 答案 (D)：以上皆是（最完整）",
  },
    {
    id: "eco-08",
    q: "下列情況，何者有外部成本？",
    opts: [
      "(A) 自家庭園遍植花木，引來隔壁養蜂戶蜜蜂採蜜",
      "(B) 以聖誕燈飾裝飾圍籬，夜間閃閃發亮，引來過往行人，駐足觀賞",
      "(C) 亞當．斯密出版《國富論》",
      "(D) 染整工廠排放污水，污染河川",
    ],
    ans: 3,
    hint: "🔑 外部成本（負外部性）：己身行為對未參與交易的第三方造成傷害且不補償",
    isCalc: false,
    explain:
      "外部性（Externality）的分類：\n\n外部效益（正外部性）：\n✗ (A) 鄰居種花 → 蜜蜂採蜜，養蜂戶受益 → 正外部性\n✗ (B) 聖誕燈飾 → 路人賞心悅目 → 正外部性\n✗ (C) 《國富論》 → 全人類受益 → 正外部性\n\n外部成本（負外部性）：\n✓ (D) 染整工廠排放污水汙染河川：\n   • 工廠生產獲益，但附近居民、漁民、下游用水者受害\n   • 工廠不需支付補償 → 這正是「外部成本（Negative Externality）」\n   • 工廠的私人成本 < 社會成本，導致生產過多，資源配置失當\n\n→ 答案 (D)：染整工廠排放污水",
  },
    {
    id: "eco-09",
    q: "關於環境污染，下列敘述何者正確？",
    opts: [
      "(A) 陽光、空氣和水，取之不盡，用之不竭，可以盡情使用",
      "(B) 面對污染問題，政府應直接管制，以達成零污染",
      "(C) 由於環境污染問題源自市場失靈，因此干預方法無法再利用市場機能",
      "(D) 面對污染問題，可以利用課稅與補貼的方式將外部效果內部化",
    ],
    ans: 3,
    hint: "🔑 庇古稅（Pigouvian Tax）：透過市場機制（課稅/補貼）解決外部性，不必管制",
    isCalc: false,
    explain:
      "環境污染政策的分析：\n\n✗ (A) 錯：空氣、水是「共有資源」，使用過度會耗竭（公有地悲劇，Tragedy of Commons）\n\n✗ (B) 錯：零污染不是最佳目標。最適污染量是邊際社會成本 = 邊際社會效益的交點，不一定為零\n\n✗ (C) 錯：即使源自市場失靈，仍可利用市場機制解決：\n   • 課徵污染稅（庇古稅）→ 讓廠商承擔外部成本\n   • 可交易污染排放許可（碳排放交易）\n\n✓ (D) 正確：\n   • 課污染稅 → 提高廠商成本 → 減少污染\n   • 補貼清潔技術 → 廠商主動採用\n   → 利用市場誘因，達成外部效果內部化\n\n→ 答案 (D)",
  },
    {
    id: "eco-10",
    q: "公共財有搭便車（free-riding）問題，主要是因為公共財具有：",
    opts: [
      "(A) 共享性（non-rival，或稱非敵對性）",
      "(B) 獨享性（rival，或稱敵對性）",
      "(C) 可排他性（excludable）",
      "(D) 不可排他性（non-excludable）",
    ],
    ans: 3,
    hint: "🔑 搭便車問題的根源：無法排除不付費者使用 → 不可排他性",
    isCalc: false,
    explain:
      "公共財（Public Goods）的兩大特性：\n① 非競爭性（Non-rival）：一人使用不妨礙他人同時使用（如：國防、路燈）\n② 不可排他性（Non-excludable）：無法阻止未付費者享用\n\n搭便車問題（Free-rider Problem）的根源：\n→ 「不可排他性（D）」\n• 無法排除不付費的人使用 → 每個人都等別人付費，自己免費享用\n• 結果：沒有人願意付費 → 公共財供給嚴重不足（市場失靈）\n→ 必須由政府提供（如：國防、基礎建設）\n\n注意：非競爭性（A）是公共財「不稀少」的特性，但搭便車問題的「根本原因」是不可排他性。\n\n→ 答案 (D)：不可排他性",
  },
    {
    id: "eco-11",
    q: "下列何者屬於失業人口？",
    opts: [
      "(A) 家裡開便利超商，在家看店之美華",
      "(B) 美華當兵的哥哥",
      "(C) 美華讀國中的弟弟",
      "(D) 美華大學剛畢業正在找工作的姊姊",
    ],
    ans: 3,
    hint: "🔑 失業人口定義：有工作意願、有工作能力、正積極尋職，但尚未找到工作",
    isCalc: false,
    explain:
      "勞動力（Labour Force）= 就業人口 + 失業人口\n\n各選項分析：\n✗ (A) 在家看店之美華：在便利超商工作（無薪家屬工也算就業）→ 就業人口\n✗ (B) 當兵的哥哥：服役中，非勞動力（制度性原因）\n✗ (C) 讀國中的弟弟：在學，未達勞動年齡，非勞動力\n✓ (D) 大學剛畢業正在找工作的姊姊：\n   • 有工作意願 ✓\n   • 有工作能力 ✓（大學畢業）\n   • 正在積極求職 ✓\n   • 尚未找到工作 ✓\n   → 符合失業定義，屬於「失業人口」\n\n→ 答案 (D)",
  },
    {
    id: "eco-12",
    q: "實質貨幣總計數 M1B 及股價指數是景氣循環的：",
    opts: [
      "(A) 領先指標",
      "(B) 落後指標",
      "(C) 同時指標",
      "(D) 擴張指標",
    ],
    ans: 0,
    hint: "🔑 M1B 和股價指數在景氣轉折前先行變動，是最重要的領先指標",
    isCalc: false,
    explain:
      "領先指標（Leading Indicators）：在景氣轉折「之前」先行變動，用於預測未來景氣。\n\n台灣常見領先指標：\n✓ 實質 M1B（貨幣供給）：\n   貨幣充裕 → 企業投資意願高 → 景氣回升先兆\n   通常領先景氣 3~6 個月\n\n✓ 股價指數（TAIEX）：\n   股市反映投資人對未來的預期，通常比實體經濟早 6~12 個月反應\n\n其他台灣領先指標：外銷訂單指數、工業生產指數、貨幣供給年增率等\n\n→ 答案 (A)：領先指標",
  },
    {
    id: "eco-13",
    q: "世界貿易組織（WTO）成立的基本精神，是希望參加的各國透過貿易：",
    opts: [
      "(A) 生產上可以自給自足，追求各國的絕對利益",
      "(B) 發揮資源配置效率以及專業生產的比較利益",
      "(C) 建立關稅壁壘，保護國內產業",
      "(D) 建立關稅同盟，抵擋經濟帝國主義的侵略",
    ],
    ans: 1,
    hint: "🔑 WTO 精神：自由貿易 + 比較利益原則，降低關稅和貿易壁壘",
    isCalc: false,
    explain:
      "世界貿易組織（WTO）的基本精神：\n\n✓ (B) 比較利益（Comparative Advantage）+ 自由貿易：\n   • 各國依比較利益專業化生產（生產相對效率最高的商品）\n   • 透過自由貿易交換，使每個國家的資源配置效率最大化\n   • 全球整體福祉提升\n\n✗ (A) 自給自足：WTO 反對自給自足，鼓勵開放貿易\n✗ (C) 建立關稅壁壘：WTO 的核心目標是「降低」關稅壁壘，非建立\n✗ (D) 關稅同盟對外排他：與 WTO 的多邊自由化精神相違背\n\nWTO 的三大原則：\n• 最惠國待遇（MFN）\n• 國民待遇（National Treatment）\n• 透明化（Transparency）\n\n→ 答案 (B)",
  },
    {
    id: "eco-14",
    q: "甲國開放紡織品進口之後，將會造成甲國國內紡織品的：",
    opts: [
      "(A) 供給線往右移動",
      "(B) 競爭增加，價格上漲",
      "(C) 生產數量增加",
      "(D) 消費數量減少",
    ],
    ans: 0,
    hint: "🔑 開放進口 = 外國供給加入市場 = 市場總供給增加 = 供給曲線右移",
    isCalc: false,
    explain:
      "開放進口的市場效果分析：\n\n開放進口等效於「新增供給來源」：\n✓ (A) 供給線往右移動：\n   外國廠商的供給加入 → 市場總供給增加 → 供給曲線右移 ✓\n\n連鎖效應：\n供給↑（右移） → 均衡價格「下降」（非上漲）→\n• 本國消費數量「增加」（非減少）—因為價格下降\n• 本國廠商生產數量「減少」（非增加）—被低價進口品擠壓\n\n各錯誤選項：\n✗ (B) 競爭增加，但價格「下跌」，非上漲\n✗ (C) 市場消費量增加，但本國廠商「生產量減少」\n✗ (D) 消費數量「增加」（因進口品更便宜），非減少\n\n→ 答案 (A)：供給線往右移動",
  },
    {
    id: "eco-15",
    q: "以下何者是貨幣最主要的功能？",
    opts: [
      "(A) 計價的單位",
      "(B) 價值的儲存",
      "(C) 遞延支付的工具",
      "(D) 交易的媒介",
    ],
    ans: 3,
    hint: "🔑 貨幣最原始也最核心的功能：解決以物易物的「雙重需求巧合」問題",
    isCalc: false,
    explain:
      "貨幣的四大功能：\n① 交易媒介（Medium of Exchange）(D) ★最主要★\n   → 解決以物易物（Barter）需要「雙重需求巧合」的問題\n   → 貨幣誕生的根本原因，是其他功能的基礎\n\n② 計價單位（Unit of Account）(A)：\n   → 提供統一的價格衡量標準\n\n③ 價值儲存（Store of Value）(B)：\n   → 可跨期間保存購買力（但通膨時此功能弱化）\n\n④ 遞延支付（Standard of Deferred Payment）(C)：\n   → 可用於借貸、分期付款等跨期交易\n\n交易媒介是貨幣最根本的功能，若無此功能，其他三項功能也沒有意義。\n\n→ 答案 (D)：交易的媒介",
  },
    {
    id: "eco-16",
    q: "所謂的「強力貨幣」（high-powered money）是指：",
    opts: [
      "(A) 活期存款",
      "(B) 定期存款",
      "(C) 定期儲蓄存款",
      "(D) 通貨",
    ],
    ans: 3,
    hint: "🔑 強力貨幣 = 貨幣基數（MB）= 通貨 + 銀行在央行的準備金存款",
    isCalc: false,
    explain:
      "強力貨幣（High-powered Money）= 貨幣基數（Monetary Base, MB）：\n\nMB = 通貨（流通在外的現金）+ 銀行存放在央行的準備金\n\n選項中最接近的是：\n✓ 通貨（D）：MB 的主要組成部分（另一部分是準備金）\n\n為何稱「強力」：\n一元的通貨（或準備金），透過銀行體系的信用創造，可以產生數倍（乘數 = 1÷準備率）的存款貨幣，故稱具有「強力」的乘數擴張效果。\n\n比較：\n• 活期/定期存款已是信用創造的「結果」，不是強力貨幣\n\n→ 答案 (D)：通貨",
  },
    {
    id: "eco-17",
    q: "有關國民生產毛額 GNP 與國內生產毛額 GDP 的敘述，下列何者比較正確？",
    opts: [
      "(A) GNP 是一地區全體人民，在某期間所生產最終商品與勞務的市場價值",
      "(B) GDP 只要在本地生產，不論籍設何處，均屬其生產毛額的計算範疇",
      "(C) GNP = GDP + 本國人在國外的產出價值 − 外國人在國內的產出價值",
      "(D) 以上皆是",
    ],
    ans: 3,
    hint: "🔑 GDP = 地理原則（國境內）；GNP = 國籍原則（本國人）",
    isCalc: false,
    explain:
      "GDP vs GNP 的精確定義：\n\n(A) GNP（國民生產毛額）✓：\n   一國「全體國民」（不論在哪裡）在某期間生產的最終商品和勞務的市場價值\n   → 以「國籍」為界定標準\n\n(B) GDP（國內生產毛額）✓：\n   在「本國境內」（不論是誰）生產的最終商品和勞務的市場價值\n   → 以「地理位置」為界定標準\n\n(C) 兩者關係 ✓：\n   GNP = GDP\n        + 本國人在國外的生產貢獻（要素所得流入）\n        − 外國人在本國的生產貢獻（要素所得流出）\n\n三項敘述皆正確。\n\n→ 答案 (D)：以上皆是",
  },
    {
    id: "eco-18",
    q: "這幾年我國人才外流嚴重，對我國 GDP 和 GNP 造成何種影響？",
    opts: [
      "(A) 對 GDP 和 GNP 均無影響",
      "(B) 使 GDP 和 GNP 均減少",
      "(C) 使 GDP 減少，GNP 無影響",
      "(D) 使 GNP 減少，GDP 無影響",
    ],
    ans: 2,
    hint: "🔑 人才出國工作：在國外生產 → GDP（地理）不計入；GNP（國籍）仍計入",
    isCalc: false,
    explain:
      "人才外流的 GDP / GNP 影響分析：\n\n假設台灣工程師跑去美國矽谷工作：\n\n對台灣 GDP：\n• GDP = 在「台灣境內」生產的產值\n• 該工程師不在台灣境內工作\n→ 台灣 GDP「減少」（減少該工程師在台灣的生產貢獻）\n\n對台灣 GNP：\n• GNP = 「台灣國民」的生產貢獻（不論在哪裡）\n• 該工程師仍是台灣國籍（台灣國民）\n• 在美國的生產仍計入台灣 GNP\n→ 台灣 GNP「無影響」（只是生產地點換了）\n\n→ 答案 (C)：GDP 減少，GNP 無影響",
  },
    {
    id: "eco-19",
    q: "台商將台灣的生產線移至大陸或東南亞生產，在其他條件不變之下，將導致我國的國內生產毛額：",
    opts: [
      "(A) 提高",
      "(B) 降低",
      "(C) 不變",
      "(D) 不一定",
    ],
    ans: 1,
    hint: "🔑 GDP = 國境內生產；生產線移到海外 → 在台灣境內生產↓ → GDP↓",
    isCalc: false,
    explain:
      "台商外移對台灣 GDP 的影響：\n\nGDP（國內生產毛額）以「地理原則」計算：\n凡在台灣境內生產的產值，不論是誰生產，都計入台灣 GDP。\n\n台商將生產線移至海外：\n• 在台灣境內的生產活動「減少」\n• 即使產品回銷台灣，生產發生地不在台灣\n→ 台灣 GDP「降低（B）」✓\n\n對台灣 GNP 的影響：\n台商仍是台灣法人/自然人，海外的生產仍計入台灣 GNP\n→ 台灣 GNP 不一定減少（海外獲利若匯回）\n\n→ 答案 (B)：降低",
  },
    {
    id: "eco-20",
    q: "當經濟有通貨膨脹壓力時，政府可採何種政策以減輕之：",
    opts: [
      "(A) 擴張性財政政策",
      "(B) 增加公共投資",
      "(C) 減少政府支出或增稅",
      "(D) 擴張性貨幣政策",
    ],
    ans: 2,
    hint: "🔑 通膨 = 總需求過熱 → 緊縮政策降溫：減少政府支出或增稅（緊縮財政）",
    isCalc: false,
    explain:
      "抑制通貨膨脹的政策組合：\n\n通膨原因：總需求（AD）過剩，超過潛在產出\n解決方向：降低 AD → 緊縮性政策\n\n緊縮性財政政策（C）✓：\n• 減少政府支出（G↓）→ AD 直接減少\n• 增加稅收（T↑）→ 民間可支配所得↓ → 消費↓ → AD 減少\n\n錯誤選項（都是擴張政策，會加重通膨）：\n✗ (A) 擴張性財政政策：增加政府支出/減稅 → AD 增加 → 通膨惡化\n✗ (B) 增加公共投資：政府支出↑ → 同上\n✗ (D) 擴張性貨幣政策：增加貨幣供給 → 利率↓ → 投資↑ → AD 增加 → 通膨惡化\n\n→ 答案 (C)：減少政府支出或增稅",
  },
    {
    id: "eco-21",
    q: "在長期總體均衡時：",
    opts: [
      "(A) 實質所得等於潛在所得，而且總合供給決定價格水準",
      "(B) 實質所得等於潛在所得，且處於充分就業水準",
      "(C) 價格水準是固定的，而且總合需求決定實質所得",
      "(D) 實質所得小於潛在所得",
    ],
    ans: 1,
    hint: "🔑 長期均衡：實質 GDP = 潛在 GDP，充分就業，長期總供給為垂直線",
    isCalc: false,
    explain:
      "長期總體均衡（Long-run Macroeconomic Equilibrium）：\n\n長期總供給曲線（LRAS）是一條垂直線，代表：\n• 垂直線對應的產量 = 潛在產出（Potential GDP）\n• 此時：實質 GDP = 潛在 GDP ✓\n• 失業率 = 自然失業率（充分就業）✓\n• 物價完全彈性調整\n\n✓ (B)「實質所得等於潛在所得，且處於充分就業水準」完整正確\n\n錯誤選項：\n✗ (A) 長期下，AD 和 AS 共同決定物價水準，非只有 AS\n✗ (C) 長期下物價並非固定，也不是 AD 決定產出（產出由 LRAS 決定）\n✗ (D) 長期均衡下實質所得等於潛在所得，非「小於」\n\n→ 答案 (B)",
  },
    {
    id: "eco-22",
    q: "全體勞動生產力的提高，將造成：",
    opts: [
      "(A) 總合供給線右移",
      "(B) 總合需求線左移",
      "(C) 物價上升",
      "(D) 潛在產出減少",
    ],
    ans: 0,
    hint: "🔑 勞動生產力↑ → 相同投入可生產更多 → 供給能力↑ → AS 曲線右移",
    isCalc: false,
    explain:
      "全體勞動生產力提高的影響：\n\n勞動生產力（Output per Labor Hour）提升：\n• 每位勞工可生產更多商品\n• 單位生產成本下降\n• 廠商願意在每個物價水準提供更多供給\n\n→ 短期和長期總合供給曲線（AS / LRAS）均「右移（A）」✓\n\n連鎖效應（供給右移在需求不變下）：\n• 均衡物價水準「下降」（非上升，C 錯）\n• 均衡實質產出「增加」\n• 潛在產出「增加」（非減少，D 錯）\n\n✗ (B) 勞動生產力不影響需求面（AD），需求曲線不移動\n\n→ 答案 (A)：總合供給線右移",
  },
    {
    id: "eco-23",
    q: "衡量物品批發價格之物價指數稱為：",
    opts: [
      "(A) 消費者物價指數",
      "(B) 國內生產毛額平減指數",
      "(C) 躉售物價指數",
      "(D) 工業生產指數",
    ],
    ans: 2,
    hint: "🔑 三大物價指數：CPI（消費零售端）、WPI（批發端）、GDP 平減指數（全面）",
    isCalc: false,
    explain:
      "三大物價指數比較：\n\n(A) 消費者物價指數（CPI）：\n   衡量消費者購買一籃子商品和服務的「零售價格」水準\n   反映民眾生活成本，最常用於通膨衡量\n\n(C) 躉售物價指數（Wholesale Price Index, WPI）✓：\n   衡量廠商之間「批發階段」的商品價格水準\n   早於 CPI 反映通膨壓力（是 CPI 的領先指標）\n\n(B) GDP 平減指數（GDP Deflator）：\n   衡量「所有」最終商品和服務的整體物價變動（最廣義）\n   = 名目 GDP ÷ 實質 GDP × 100\n\n(D) 工業生產指數：衡量工業產出量，非物價指數\n\n→ 答案 (C)：躉售物價指數（WPI）",
  },
    {
    id: "eco-24",
    q: "下列那一種失業屬於自然失業？",
    opts: [
      "(A) 循環性失業",
      "(B) 結構性失業",
      "(C) 長期性失業",
      "(D) 膨脹性失業",
    ],
    ans: 1,
    hint: "🔑 自然失業率 = 摩擦性失業 + 結構性失業（景氣中性時的「自然」水準）",
    isCalc: false,
    explain:
      "失業類型與自然失業率的關係：\n\n自然失業率（Natural Rate of Unemployment）：\n= 摩擦性失業（Frictional）+ 結構性失業（Structural）\n\n✓ 結構性失業（B）：\n   產業結構或技術變化，導致勞工技能不符市場需求\n   例：工廠自動化，工人技能過時需要再訓練\n   → 屬自然失業（即使景氣好也存在）\n\n✓ 摩擦性失業：轉換工作期間的暫時性失業（非選項，但也是自然失業）\n\n✗ (A) 循環性失業（Cyclical / Demand-deficient）：\n   景氣衰退，總需求不足導致 → 非自然失業，是可透過政策改善的\n\n✗ (C)(D) 長期性/膨脹性失業：非標準分類\n\n→ 答案 (B)：結構性失業",
  },
    {
    id: "eco-25",
    q: "下列那項指標適合用來衡量家戶所得分配不均度？",
    opts: [
      "(A) 最高最低所得組之所得倍數",
      "(B) 消費者物價指數",
      "(C) 國內生產毛額（GDP）平減指數",
      "(D) 痛苦指數",
    ],
    ans: 0,
    hint: "🔑 所得分配不均：五等分位所得倍數（最高 20% ÷ 最低 20%）或吉尼係數",
    isCalc: false,
    explain:
      "衡量所得分配不均的指標：\n\n✓ (A) 最高最低所得組之所得倍數：\n   = 最高 20% 家庭平均所得 ÷ 最低 20% 家庭平均所得\n   數值越大 → 所得分配越不均\n   例：台灣五等分位差距約 6.3 倍（近年數據）\n\n其他所得分配指標：\n• 吉尼係數（Gini Coefficient）：0（完全均等）至 1（完全不均）\n• 洛倫茲曲線（Lorenz Curve）\n\n其他選項衡量的不是所得分配：\n✗ (B) CPI：衡量消費物價（通膨）\n✗ (C) GDP 平減指數：衡量整體物價水準\n✗ (D) 痛苦指數（Misery Index）= 通膨率 + 失業率：衡量民眾痛苦程度\n\n→ 答案 (A)",
  },
    {
    id: "eco-26",
    q: "下列何者不會使速食餐飲的需求線移動？",
    opts: [
      "(A) 失業人數增加",
      "(B) 平均國民所得增加",
      "(C) 國民便當的問市",
      "(D) 速食連鎖店的擴張",
    ],
    ans: 3,
    hint: "🔑 需求曲線移動：非價格因素（所得、替代品、互補品、偏好）；供給面變化不移動需求曲線",
    isCalc: false,
    explain:
      "需求曲線的移動因素（Determinants of Demand，非價格）：\n\n✓ 使需求線移動的因素：\n(A) 失業人數增加 → 民眾所得↓ → 速食需求可能減少（需求線左移）\n(B) 國民所得增加 → 消費能力↑ → 速食需求增加（需求線右移）\n(C) 國民便當問市 → 替代品出現 → 速食需求減少（需求線左移）\n\n✗ 不移動需求曲線的因素：\n(D) 速食連鎖店擴張：\n   → 這是「供給面」的變化（供給者增加）\n   → 影響的是供給曲線（右移），不是需求曲線\n   → 需求曲線不移動\n\n→ 答案 (D)：速食連鎖店的擴張",
  },
    {
    id: "eco-27",
    q: "丁強修理 1 部腳踏車要花 2 小時，修理 1 台電腦要花 4 小時。下列敘述何者正確？",
    opts: [
      "(A) 丁強的比較利益是修腳踏車",
      "(B) 修電腦是丁強的絕對利益",
      "(C) 丁強的比較利益是修電腦",
      "(D) 丁強修 1 台電腦的機會成本是修 2 台腳踏車",
    ],
    ans: 3,
    hint: "🔑【計算題】機會成本：修 1 台電腦（4 小時）= 放棄幾台腳踏車（2 小時/台）",
    isCalc: true,
    explain:
      "【機會成本計算—比較利益分析】\n\n丁強的生產時間：\n• 修理 1 台腳踏車 = 2 小時\n• 修理 1 台電腦 = 4 小時\n\n計算機會成本：\n修 1 台電腦（需 4 小時）的機會成本：\n= 這 4 小時可修多少台腳踏車\n= 4 小時 ÷ 2 小時/台 = 2 台腳踏車\n\n→ (D) 丁強修 1 台電腦的機會成本是修 2 台腳踏車 ✓\n\n關於比較利益（A、C）：\n• 比較利益需要與「他人」比較機會成本，題目只給丁強一人資料，無法單獨判斷丁強的比較利益在哪\n→ (A)(C) 均無法由題目資訊得出\n\n關於絕對利益（B）：\n• 絕對利益也需要比較，題目未給第二人資料，無法判斷\n→ (B) 同樣無法由題目得出\n\n→ 答案 (D)",
  },
    {
    id: "eco-28",
    q: "永續發展強調三要素協調發展，促進社會的總體進步：",
    opts: [
      "(A) 環境要素（Environmental aspect）：指儘量減少對環境的損害",
      "(B) 社會要素（Social aspect）：要能滿足人類自身的需要",
      "(C) 經濟要素（Economic aspect）：必須在經濟上有利可圖",
      "(D) 以上皆是",
    ],
    ans: 3,
    hint: "🔑 永續發展三大支柱：環境（Planet）、社會（People）、經濟（Profit）——3P 模型",
    isCalc: false,
    explain:
      "永續發展（Sustainable Development）三大支柱：\n\n1991 年 Brundtland 報告定義：「滿足當代人的需求，而不損害後代滿足其需求的能力」\n\n三大要素（3P 模型）：\n(A) 環境可持續性（Planet）✓：保護生態環境，減少污染與資源耗竭\n(B) 社會可持續性（People）✓：滿足人類基本需求，確保社會公平\n(C) 經濟可持續性（Profit）✓：創造持續的經濟價值與利潤\n\n三者必須「同時兼顧、協調並進」，犧牲任何一個都不是真正的永續發展。\n\n→ 答案 (D)：以上皆是",
  },
    {
    id: "eco-29",
    q: "除了通貨膨脹率之外，下列那個經濟指標也適合衡量景氣波動？",
    opts: [
      "(A) 最高最低所得倍數",
      "(B) 失業率",
      "(C) 外匯存底",
      "(D) 綠色國民所得",
    ],
    ans: 1,
    hint: "🔑 景氣好→失業率低；景氣差→失業率高，是衡量景氣循環的核心指標之一",
    isCalc: false,
    explain:
      "景氣波動的衡量指標：\n\n✓ 失業率（B）：\n   景氣擴張期 → 企業增聘 → 失業率下降\n   景氣衰退期 → 企業裁員 → 失業率上升\n   → 與景氣呈反向關係，是衡量景氣的核心指標\n\n「痛苦指數」= 通膨率 + 失業率，即結合通膨與失業衡量整體景氣痛苦。\n\n其他選項的衡量對象：\n✗ (A) 最高最低所得倍數：衡量「所得分配不均度」，非景氣\n✗ (C) 外匯存底：衡量國際準備資產，非直接反映景氣\n✗ (D) 綠色國民所得：衡量扣除環境成本後的真實所得，非景氣波動\n\n→ 答案 (B)：失業率",
  },
    {
    id: "eco-30",
    q: "有關交易性貨幣需求的敘述，以下何者錯誤？",
    opts: [
      "(A) 所得增加將提高交易性貨幣需求",
      "(B) 利率上揚將提高交易性貨幣需求",
      "(C) 提款成本增加將提高交易性貨幣需求",
      "(D) 因所得與交易發生的非同步性，使得大眾有必要持有貨幣",
    ],
    ans: 1,
    hint: "🔑 利率↑ = 持有現金的機會成本↑ → 人們會減少現金持有，非增加",
    isCalc: false,
    explain:
      "Baumol-Tobin 交易性貨幣需求模型：\n\n最適貨幣持有量 = √(2bY / i)\n• b = 每次提款的固定成本\n• Y = 所得\n• i = 利率（機會成本）\n\n各因素影響分析：\n✓ (A) 所得（Y）增加 → 需要更多貨幣進行交易 → 需求增加 ✓\n✗ (B) 利率（i）上揚：\n   持有現金的機會成本（放棄的利息）增加\n   → 人們會更頻繁提款（每次少提一點）→ 平均現金持有量「減少」\n   → (B) 說「提高」是錯誤的 ✗\n✓ (C) 提款成本（b）增加 → 每次多提一點以減少提款次數 → 需求增加 ✓\n✓ (D) 所得收到和支出不同步 → 需要現金緩衝 → 此為持有貨幣的根本原因 ✓\n\n→ 答案 (B)：利率上揚「降低」交易性貨幣需求，非提高",
  },
    {
    id: "eco-31",
    q: "根據貨幣學派的看法，貨幣數量與物價間關係的說明何者正確？",
    opts: [
      "(A) 具有反向變化關係",
      "(B) 具有同向變化關係",
      "(C) 無一定關係",
      "(D) 兩者的關係由政府當局所制訂",
    ],
    ans: 1,
    hint: "🔑 貨幣數量學說 MV = PQ：V、Q 固定時，M↑ → P 等比例↑，正向關係",
    isCalc: false,
    explain:
      "貨幣學派（Monetarism）的費雪方程式：\n\nM × V = P × Q\n• M：貨幣供給量\n• V：貨幣流通速度（貨幣學派假設 V 穩定）\n• P：物價水準\n• Q：實質產出（長期假設等於潛在產出，充分就業）\n\n貨幣學派推論（在 V 和 Q 固定的假設下）：\n→ M 增加 → P 等比例增加（同向關係）✓\n→ 「通貨膨脹無論何時何地都是一種貨幣現象」（傅利曼名言）\n\n政策意涵：控制貨幣供給成長率（貨幣法則）是穩定物價最重要的手段。\n\n→ 答案 (B)：同向變化關係",
  },
    {
    id: "eco-32",
    q: "邊際產量遞減與下列何種現象相關連？",
    opts: [
      "(A) 總成本遞減",
      "(B) 邊際成本遞減",
      "(C) 邊際成本遞增",
      "(D) 總成本不變",
    ],
    ans: 2,
    hint: "🔑【計算題】邊際成本 = 要素價格 ÷ 邊際產量；邊際產量↓ → 邊際成本↑",
    isCalc: true,
    explain:
      "【邊際產量遞減定律與邊際成本的關係】\n\n邊際成本（MC）公式：\nMC = ΔTC ÷ ΔQ = w ÷ MP（工資 ÷ 邊際產量）\n\n當邊際產量遞減（Law of Diminishing Marginal Returns）：\n固定其他要素，不斷增加勞動 → 邊際產量（MP）最終遞減\n→ MC = w ÷ MP，MP 下降 → MC 上升\n\n計算示範：\n假設工資 w = $100/小時\n• 第 1 個勞工 MP = 10 → MC = 100/10 = $10\n• 第 2 個勞工 MP = 8  → MC = 100/8 = $12.5\n• 第 3 個勞工 MP = 5  → MC = 100/5 = $20\n→ 邊際成本遞增（C）✓\n\n→ 總成本仍在增加（A、D 均錯）\n\n→ 答案 (C)：邊際成本遞增",
  },
    {
    id: "eco-33",
    q: "下列何者不屬於「非價格競爭」的種類？",
    opts: [
      "(A) 廣告",
      "(B) 贈品與摸彩",
      "(C) 產品保證與售後服務",
      "(D) 差別訂價",
    ],
    ans: 3,
    hint: "🔑 非價格競爭 = 不以改變價格為手段；差別訂價本身就是「價格」策略",
    isCalc: false,
    explain:
      "非價格競爭（Non-price Competition）：\n廠商不透過降價，而以其他方式吸引消費者的競爭策略。\n\n✓ 非價格競爭的例子：\n(A) 廣告（品牌形象塑造）✓\n(B) 贈品、摸彩（促銷活動）✓\n(C) 產品保證、售後服務（提升附加價值）✓\n其他：產品差異化、包裝設計、顧客忠誠計畫\n\n✗ 差別訂價（D）：\n   對不同消費者（或市場）收取不同價格\n   → 這是一種「價格」策略，屬於「價格歧視（Price Discrimination）」\n   → 不屬於非價格競爭\n\n非價格競爭常見於獨占性競爭市場（廠商眾多、產品差異化）。\n\n→ 答案 (D)：差別訂價",
  },
    {
    id: "eco-34",
    q: "廠商數目多且生產異質產品的市場屬於：",
    opts: [
      "(A) 獨占",
      "(B) 寡占",
      "(C) 完全競爭",
      "(D) 獨占性競爭",
    ],
    ans: 3,
    hint: "🔑 四種市場結構：廠商數 × 產品同質性；多廠商 + 差異化產品 = 獨占性競爭",
    isCalc: false,
    explain:
      "四種市場結構比較：\n\n| 市場類型 | 廠商數目 | 產品性質 | 例子 |\n|---------|---------|---------|------|\n| 完全競爭 | 非常多  | 同質    | 農產品市場 |\n| 獨占性競爭| 多     | 異質（差異化）| 餐廳、美容院 |\n| 寡占    | 少數幾家 | 同質或異質| 鋼鐵、汽車 |\n| 獨占    | 一家   | 唯一    | 台灣水電公司 |\n\n✓ 獨占性競爭（D）：\n• 廠商數目多（進入門檻低）\n• 產品差異化（異質）—每家有些不同（口味、裝潢、服務）\n• 短期可能有超額利潤，長期利潤趨近於零\n• 例：餐廳、美容院、書店、服飾店\n\n→ 答案 (D)：獨占性競爭",
  },
    {
    id: "eco-35",
    q: "土地的供給彈性通常假設為：",
    opts: [
      "(A) 零",
      "(B) 無窮大",
      "(C) 小於一",
      "(D) 大於一",
    ],
    ans: 0,
    hint: "🔑 土地面積固定，無論地價多高，供給量完全無法增加 → 供給彈性 = 0（完全無彈性）",
    isCalc: false,
    explain:
      "土地供給的特殊性：\n\n土地（狹義指自然賦予的土地面積）的特性：\n• 總量固定，無法生產更多（不像勞動可增加人口，資本可投資增加）\n• 無論地價漲多高，土地的「總供給量」都不會增加\n\n供給價格彈性：\nEs = (ΔQ/Q) ÷ (ΔP/P)\n土地：ΔQ = 0（地價再高，供給量不變）\n→ Es = 0 ÷ (ΔP/P) = 0（零彈性）✓\n\n圖形表示：土地供給曲線是一條「垂直線」（完全無彈性）\n\n政策意涵：\n課徵土地稅的全部稅負由地主承擔（因為無彈性，無法轉嫁），不影響效率。\n\n→ 答案 (A)：供給彈性 = 零",
  },
    {
    id: "eco-36",
    q: "甲國之總人口為 2 千萬人，勞動人口為 1,750 萬人，失業人口為 875,000 人，則甲國之失業率為：",
    opts: [
      "(A) 4.38%",
      "(B) 5%",
      "(C) 7.5%",
      "(D) 25%",
    ],
    ans: 1,
    hint: "🔑【計算題】失業率 = 失業人口 ÷ 勞動人口（非總人口）× 100%",
    isCalc: true,
    explain:
      "【計算步驟—失業率公式】\n\n失業率 = 失業人口 ÷ 勞動人口 × 100%\n\n代入數值：\n= 875,000 ÷ 17,500,000 × 100%\n= 875,000 ÷ 17,500,000 × 100%\n= 0.05 × 100%\n= 5%\n\n⚠ 常見錯誤：\n✗ 用總人口（2,000 萬）作分母\n   = 875,000 ÷ 20,000,000 = 4.375% ≈ 4.38%（A 的錯誤計算）\n\n✓ 正確分母是「勞動人口」（1,750 萬）\n   勞動人口 = 就業人口 + 失業人口（不含在學、退休、服役等非勞動力）\n\n→ 答案 (B)：失業率 5%",
  },
    {
    id: "eco-37",
    q: "如果世界上只有美國和台灣兩國。美國能生產 500 噸小麥或 1,000 噸玉米，台灣則能生產 100 噸小麥或 400 噸玉米，根據比較利益法則，美國應該：",
    opts: [
      "(A) 出口小麥，進口玉米",
      "(B) 出口玉米，進口小麥",
      "(C) 同時出口小麥和玉米，完全不進口",
      "(D) 同時進口小麥和玉米，完全不出口",
    ],
    ans: 0,
    hint: "🔑【計算題】比較機會成本：各國生產一單位 X 需放棄多少 Y，哪國較低就有比較利益",
    isCalc: true,
    explain:
      "【機會成本計算—比較利益法則（Ricardo）】\n\n各國生產能力：\n• 美國：500 噸小麥 OR 1,000 噸玉米\n• 台灣：100 噸小麥 OR 400 噸玉米\n\n計算機會成本：\n\n生產 1 噸小麥的機會成本（放棄多少玉米）：\n• 美國：1,000/500 = 2 噸玉米\n• 台灣：400/100 = 4 噸玉米\n→ 美國生產小麥的機會成本（2）< 台灣（4）\n→ 「美國生產小麥有比較利益」✓\n\n生產 1 噸玉米的機會成本（放棄多少小麥）：\n• 美國：500/1,000 = 0.5 噸小麥\n• 台灣：100/400 = 0.25 噸小麥\n→ 台灣生產玉米的機會成本（0.25）< 美國（0.5）\n→ 「台灣生產玉米有比較利益」✓\n\n結論：\n• 美國應「出口小麥，進口玉米」(A)\n• 台灣應「出口玉米，進口小麥」\n\n→ 答案 (A)",
  },
    {
    id: "eco-38",
    q: "若中央銀行要實施擴張型的貨幣政策來刺激景氣，可採用下列那一種政策？",
    opts: [
      "(A) 擴大公開市場買入",
      "(B) 提高存款準備率",
      "(C) 提高重貼現率",
      "(D) 選擇性信用管制",
    ],
    ans: 0,
    hint: "🔑 擴張貨幣政策工具：公開市場買入、降準備率、降重貼現率（三選一）",
    isCalc: false,
    explain:
      "擴張性貨幣政策（刺激景氣）的工具：\n\n✓ (A) 公開市場「買入」公債：\n   央行買入公債 → 向市場注入資金 → 銀行準備金增加 → 貨幣供給增加 → 利率↓ → 投資↑ → GDP↑\n   最常用、最靈活的工具 ✓\n\n緊縮性工具（會抑制景氣，非答案）：\n✗ (B) 提高存款準備率 → 銀行可放款↓ → 緊縮\n✗ (C) 提高重貼現率 → 銀行向央行借錢成本↑ → 緊縮\n✗ (D) 選擇性信用管制 → 針對特定市場信用限制 → 非一般擴張工具\n\n→ 答案 (A)：擴大公開市場買入",
  },
    {
    id: "eco-39",
    q: "下列敘述何者錯誤？",
    opts: [
      "(A) 利率下跌對股市較為不利",
      "(B) 當物價明顯上漲時，政府通常會採用緊縮性的貨幣政策",
      "(C) 當新台幣升值時，對我國之出口商較為不利",
      "(D) 當景氣對策信號為藍燈時，代表景氣衰退",
    ],
    ans: 0,
    hint: "🔑 利率↓ → 借貸成本低、資金轉入股市 → 股市通常「上漲」，非不利",
    isCalc: false,
    explain:
      "各選項分析：\n\n✗ (A) ★錯誤★：「利率下跌對股市較為不利」\n   利率下跌的效果：\n   • 企業借貸成本降低 → 投資和獲利能力提升\n   • 債券收益率降低 → 資金轉入股市\n   → 股市通常「上漲」，對股市是「有利」而非不利\n\n✓ (B) 物價上漲（通膨）→ 政府採緊縮貨幣政策（升息、減少 M）→ 正確\n\n✓ (C) 新台幣升值 → 出口商以外幣計價的收入換回台幣變少 → 不利出口 → 正確\n\n✓ (D) 台灣景氣對策信號藍燈（9~16 分）→ 景氣衰退 → 正確\n\n→ 答案 (A)：利率下跌對股市「有利」，非不利",
  },
    {
    id: "eco-40",
    q: "假設其他條件不變，當平均在學年數增加時：",
    opts: [
      "(A) 勞動參與率會下降",
      "(B) 勞動參與率會上升",
      "(C) 勞動參與率不受影響",
      "(D) 資料不足，無法判斷",
    ],
    ans: 0,
    hint: "🔑 在學年數↑ → 更多人繼續就學 → 暫不進入勞動市場 → 勞動參與率↓",
    isCalc: false,
    explain:
      "勞動參與率（Labor Force Participation Rate）：\n= 勞動力（就業 + 失業）÷ 工作年齡人口 × 100%\n\n平均在學年數增加時：\n• 更多人選擇繼續升學（高中、大學、研究所）\n• 在學期間通常不算入勞動力（非就業、非求職）\n• 勞動力（分子）相對減少\n→ 勞動參與率「下降（A）」✓\n\n實際案例：\n• 大學入學率提高 → 18~22 歲族群勞動參與率下降\n• 女性教育程度提升 → 延後進入職場\n\n→ 答案 (A)：勞動參與率會下降",
  },
    {
    id: "eco-41",
    q: "國人出國觀光住旅館使用了外匯，對本國而言是：",
    opts: [
      "(A) 勞務的進口",
      "(B) 勞務的出口",
      "(C) 物品的進口",
      "(D) 物品的出口",
    ],
    ans: 0,
    hint: "🔑 國人在國外消費（旅遊、住宿）= 購買外國服務 = 進口勞務（外匯流出）",
    isCalc: false,
    explain:
      "國際貿易帳的記法（從本國角度）：\n\n• 外國人來台灣消費（旅遊、住宿）：\n  台灣提供服務給外國人 → 外匯流入 → 「出口勞務」（算在台灣的經常帳收入）\n\n• 本國人出國消費（旅遊、住宿）(A)：\n  台灣人向外國購買服務（旅館、餐廳等）→ 外匯流出\n  → 「進口勞務（服務進口）」✓\n  → 計入台灣國際收支的「服務帳」借方（逆差）\n\n台灣出國旅遊支出龐大，是台灣服務帳的重要逆差來源之一。\n\n→ 答案 (A)：勞務的進口",
  },
    {
    id: "eco-42",
    q: "所謂「實質匯率」的意義是指：",
    opts: [
      "(A) 兩國間貨幣的兌換比率關係",
      "(B) 兩國間商品與勞務的兌換比率關係",
      "(C) 兩國間股票的兌換比率關係",
      "(D) 兩國間工資的兌換比率關係",
    ],
    ans: 1,
    hint: "🔑 名目匯率 = 貨幣兌換比；實質匯率 = 商品兌換比（考慮兩國物價差異）",
    isCalc: false,
    explain:
      "名目匯率 vs 實質匯率：\n\n• 名目匯率（Nominal Exchange Rate）：\n  兩國貨幣的「直接兌換比率」\n  例：1 美元 = 30 新台幣\n\n• 實質匯率（Real Exchange Rate）：\n  考慮物價因素後，兩國「商品與勞務」的相對兌換比率\n  公式：q = e × (P*/P)\n  其中 e = 名目匯率，P* = 外國物價，P = 本國物價\n  \n  意義：反映「國際競爭力」\n  實質匯率升值 → 本國商品相對更貴 → 出口競爭力下降\n\n(B) 正確：實質匯率反映兩國商品與勞務的相對兌換比率\n\n→ 答案 (B)：兩國間商品與勞務的兌換比率關係",
  },
    {
    id: "eco-43",
    q: "下列何者為「貿易依存度」的定義？",
    opts: [
      "(A) 進口總額 ／ 出口總額",
      "(B) （進口總額 ＋ 出口總額）／ 國民生產毛額",
      "(C) （出口總額 − 進口總額）／（進口總額 ＋ 出口總額）",
      "(D) （出口總額 − 進口總額）／ 國民生產毛額",
    ],
    ans: 1,
    hint: "🔑【計算題】貿易依存度 = 進出口總額 ÷ GNP（或 GDP）",
    isCalc: true,
    explain:
      "【貿易依存度公式】\n\n貿易依存度（Trade Dependence Ratio）：\n= (進口總額 + 出口總額) ÷ GNP（或 GDP）\n\n意義：衡量一國經濟對「國際貿易」的依賴程度\n• 比率越高 → 對外貿易越重要，受國際景氣影響越大\n• 台灣是出口導向型小型開放經濟，貿易依存度常超過 100%\n\n其他選項的計算：\n(A) 進口/出口 → 貿易逆差/順差比，非依存度\n(C) (出口-進口)/(進口+出口) → 類似貿易平衡指數\n(D) (出口-進口)/GNP → 淨出口占 GNP 比重\n\n→ 答案 (B)：（進口+出口）÷ GNP",
  },
    {
    id: "eco-44",
    q: "生產力定義為：",
    opts: [
      "(A) 經濟體的總產出",
      "(B) 經濟體內某一部門的總產出",
      "(C) 每單位投入要素所能產出的數量",
      "(D) 生產創新",
    ],
    ans: 2,
    hint: "🔑 生產力（Productivity）= 產出 ÷ 投入；衡量投入效率，非總量",
    isCalc: false,
    explain:
      "生產力（Productivity）的定義：\n\n生產力 = 產出量 ÷ 投入量\n= 每單位投入要素所能生產的產出量（C）✓\n\n常見類型：\n• 勞動生產力：GDP ÷ 就業人數（或工時）\n  每位勞工（或每工時）平均產出多少\n\n• 全要素生產力（TFP）：衡量技術進步等無法歸因於勞動或資本的生產力\n\n與總產出（A、B）的區別：\n• 總產出是「絕對量」，生產力是「效率比率」\n• 一國規模大，總產出高，但若投入也多，生產力不一定高\n\n生產力提升是長期經濟成長的最根本驅動力。\n\n→ 答案 (C)",
  },
    {
    id: "eco-45",
    q: "當經濟體內有所得稅時，乘數將：",
    opts: [
      "(A) 變大",
      "(B) 變小",
      "(C) 不變",
      "(D) 當稅增加時變小，而當稅減少時變大",
    ],
    ans: 1,
    hint: "🔑【計算題】有所得稅 → 可支配所得縮小 → 消費乘數效果縮小 → 乘數變小",
    isCalc: true,
    explain:
      "【凱因斯乘數與所得稅—計算說明】\n\n無所得稅時的簡單乘數：\nk = 1 ÷ (1 − MPC)\n\n有所得稅（稅率 t）時的乘數：\nk = 1 ÷ (1 − MPC × (1 − t))\n\n計算比較（MPC = 0.8，t = 0.25）：\n• 無稅：k = 1 ÷ (1 − 0.8) = 1 ÷ 0.2 = 5\n• 有稅：k = 1 ÷ (1 − 0.8 × 0.75) = 1 ÷ (1 − 0.6) = 1 ÷ 0.4 = 2.5\n\n原因：\n所得稅 → 每輪消費的一部分流向政府（稅收）→ 進入消費循環的比例減少\n→ 乘數效果縮小\n所得稅扮演「自動穩定器（Automatic Stabilizer）」的角色：\n景氣好 → 稅收增加 → 抑制過熱\n景氣差 → 稅收減少 → 緩解衰退\n\n→ 答案 (B)：乘數變小",
  },
    {
    id: "eco-46",
    q: "下列何者不是傾銷（dumping）的定義？",
    opts: [
      "(A) 以低於生產成本，將產品外銷到他國的行為",
      "(B) 以低於國內售價價格，將產品外銷到他國的行為",
      "(C) 對出口到國外的商品，課以較高的出口稅",
      "(D) 在政府補貼下，將產品外銷到他國的行為",
    ],
    ans: 2,
    hint: "🔑 傾銷 = 低價出口（低於成本或國內售價）；課出口稅是限制出口，方向相反",
    isCalc: false,
    explain:
      "傾銷（Dumping）的定義：\n廠商將產品以不公平低價出口至他國，損害進口國同業競爭力。\n\n傾銷的三種形態（皆符合定義）：\n✓ (A) 以低於生產成本出口：掠奪性定價，擠垮他國競爭者\n✓ (B) 以低於國內售價出口：對國外比國內更低的價格（價格歧視）\n✓ (D) 政府補貼下低價出口：利用補貼實現不公平低價（反補貼稅的對象）\n\n不是傾銷的：\n✗ (C) 課以較高的出口稅：\n   這是「限制出口」的措施（如：為了保護國內供給，提高出口成本）\n   方向與傾銷完全相反，是讓出口變得更貴（難以出口），非低價傾銷\n\n各國可對傾銷行為課徵「反傾銷稅（Anti-dumping Duty）」。\n\n→ 答案 (C)：課較高出口稅不是傾銷",
  },
    {
    id: "eco-47",
    q: "國發會公布，107 年 7 月景氣對策信號綜合判斷分數為 27 分，燈號為綠燈，這是顯示什麼意義？",
    opts: [
      "(A) 景氣熱絡",
      "(B) 景氣低迷",
      "(C) 當前國內經濟溫和擴張",
      "(D) 景氣不明",
    ],
    ans: 2,
    hint: "🔑 台灣景氣信號燈：紅燈=過熱；黃紅=繁榮；綠燈=穩定擴張；黃藍=趨緩；藍燈=衰退",
    isCalc: false,
    explain:
      "台灣景氣對策信號（Monitoring Indicator）燈號說明：\n\n| 燈號   | 分數範圍 | 意義          |\n|--------|---------|---------------|\n| 紅燈   | 45~38分  | 景氣過熱      |\n| 黃紅燈 | 37~32分  | 景氣趨向繁榮  |\n| 綠燈   | 31~23分  | 景氣穩定（溫和擴張）|\n| 黃藍燈 | 22~17分  | 景氣趨向低迷  |\n| 藍燈   | 16~9分   | 景氣衰退      |\n\n本題：27 分，綠燈\n→ 景氣「穩定、溫和擴張」狀態（C）✓\n\n→ 答案 (C)：當前國內經濟溫和擴張",
  },
    {
    id: "eco-48",
    q: "當消費越多的商品時：",
    opts: [
      "(A) 消費該商品所得到的邊際效用與總效用同時增加",
      "(B) 消費該商品所得到的邊際效用上升，但總效用下降",
      "(C) 消費該商品所得到的邊際效用下降，但總效用上升",
      "(D) 消費該商品所得到的邊際效用與總效用同時下降",
    ],
    ans: 2,
    hint: "🔑 邊際效用遞減定律：MU 遞減，但只要 MU > 0，TU 仍在增加（只是增加越來越慢）",
    isCalc: false,
    explain:
      "邊際效用遞減定律（Law of Diminishing Marginal Utility）：\n\n• 邊際效用（MU）：多消費「一單位」所增加的滿足感\n  → 隨消費量增加而「遞減」（第 1 碗拉麵最好吃，第 2 碗次之…）\n\n• 總效用（TU）：所有消費累積的總滿足感\n  → 只要 MU > 0，TU 仍在「增加」（雖增加越來越慢）\n  → 直到 MU = 0，TU 達到最大值\n  → MU < 0 時，TU 才開始下降\n\n圖形關係：\n• TU 曲線是向右上方遞增的凹弧（增速漸緩）\n• MU 曲線是向右下方傾斜（遞減）\n\n→ (C) 邊際效用「下降」，但總效用「上升」✓\n\n→ 答案 (C)",
  },
    {
    id: "eco-49",
    q: "對劣等財而言，下列何者為真？",
    opts: [
      "(A) 需求曲線具有正斜率",
      "(B) 價格改變時，所得效果與替代效果的作用相反",
      "(C) 價格改變時，所得效果與替代效果的作用相同",
      "(D) 所得效果為正",
    ],
    ans: 1,
    hint: "🔑 劣等財：替代效果（負）與所得效果（正）方向相反；通常替代效果仍勝出",
    isCalc: false,
    explain:
      "劣等財（Inferior Goods）的價格效果分解：\n\n當劣等財的「價格下跌」時：\n\n① 替代效果（Substitution Effect）：\n   該商品相對便宜 → 以此替代其他商品 → 需求「增加」（負向效果：需求反向於價格）\n\n② 所得效果（Income Effect）：\n   價格下跌 → 實質所得上升（購買力增加）\n   → 但劣等財在實質所得↑時，需求「減少」（正向所得效果的反面）\n\n→ 替代效果（增加需求）與所得效果（減少需求）「方向相反」(B) ✓\n\n通常替代效果 > 所得效果 → 需求曲線仍向右下傾斜（負斜率）\n（例外：季芬財—所得效果 > 替代效果 → 需求曲線正斜率）\n\n→ 答案 (B)：作用方向相反",
  },
    {
    id: "eco-50",
    q: "債券價格與市場利率之間存在：",
    opts: [
      "(A) 正相關",
      "(B) 負相關",
      "(C) 不相關",
      "(D) 與中央銀行政策無關",
    ],
    ans: 1,
    hint: "🔑 債券現值 = 未來現金流折現；折現率（利率）↑ → 現值↓ → 債券價格↓",
    isCalc: false,
    explain:
      "債券定價公式（債券現值）：\n\nP = C/(1+r) + C/(1+r)² + ... + (C+F)/(1+r)^n\n\n• P = 債券價格\n• C = 每期票面利息\n• F = 到期面額\n• r = 市場利率（折現率）\n\n利率上升時（r ↑）：\n• 分母（1+r）增大 → 每筆現金流的現值減小\n• 債券總現值（P）下降\n\n→ 債券價格（P）與市場利率（r）呈「負相關（B）」✓\n\n直觀理解：\n市場利率升至 5%，原來票面利率 3% 的舊債券吸引力下降 → 必須降價才能賣出。\n\n→ 答案 (B)：負相關",
  },
    {
    id: "eco-51",
    q: "緊縮的貨幣政策再加上人們及公司的財富下降，將使得：",
    opts: [
      "(A) 總合需求上升",
      "(B) 總合需求下降",
      "(C) 許多商業銀行倒閉",
      "(D) 短期及長期利率明顯下降",
    ],
    ans: 1,
    hint: "🔑 緊縮貨幣 → 利率↑；財富↓ → 消費↓；雙重因素壓低總需求",
    isCalc: false,
    explain:
      "雙重打擊對總合需求的影響：\n\n① 緊縮貨幣政策：\n   貨幣供給↓ → 利率↑ → 投資成本↑ → 企業投資↓ → AD↓\n\n② 財富下降（如：股市下跌、房市下跌）：\n   家庭財富↓ → 消費信心↓ → 消費支出↓（財富效果）→ AD↓\n\n兩個因素都使總合需求（AD）下降（B）✓\n\n其他選項分析：\n✗ (A) AD 上升：方向完全相反\n✗ (C) 銀行倒閉：這是信用危機的情境，非本題重點\n✗ (D) 利率下降：緊縮貨幣政策使利率「上升」，非下降\n\n→ 答案 (B)：總合需求下降",
  },
    {
    id: "eco-52",
    q: "2018-06-16 美中貿易戰開打，主打的貿易政策工具是那一種？",
    opts: [
      "(A) 出口補貼",
      "(B) 進口配額流量",
      "(C) 相互課徵 25% 的懲罰性關稅",
      "(D) 加強檢驗干預進口",
    ],
    ans: 2,
    hint: "🔑 美中貿易戰主要武器：雙方相互加征懲罰性關稅（Retaliatory Tariff）",
    isCalc: false,
    explain:
      "2018 年美中貿易戰（Trade War）背景：\n\n• 2018 年 3 月：美國宣布對中國 $600 億商品加征關稅\n• 中國隨即宣布對美國商品報復性加征關稅\n• 主要集中在對方的鋼鐵、農產品、科技產品等\n• 稅率普遍為 25%，部分高達 25% 以上\n\n主要工具 (C) 懲罰性關稅（Retaliatory / Punitive Tariff）：\n• 以關稅報復對方的「不公平貿易行為」（如傾銷、補貼）\n• 特點：相互報復（美課中，中必報復美）\n\n其他選項（非此次貿易戰主要工具）：\n(A) 出口補貼：美中均未以此為主要武器\n(B) 進口配額：未大量採用\n(D) 加強檢驗：是非關稅壁壘，非主要工具\n\n→ 答案 (C)",
  },
    {
    id: "eco-53",
    q: "下列那一個不是流量（flow）變數？",
    opts: [
      "(A) 廠商所擁有的產出存貨",
      "(B) 家戶的消費支出",
      "(C) 出口",
      "(D) 進口",
    ],
    ans: 0,
    hint: "🔑 存量（Stock）= 某時點的量（如存貨、資本）；流量（Flow）= 某期間的量",
    isCalc: false,
    explain:
      "存量（Stock）vs 流量（Flow）的辨別：\n\n流量（Flow）：在「某一段時間內」累積的數量\n✓ (B) 家戶消費支出：「每月/每年」消費了多少 → 流量\n✓ (C) 出口：「每季/每年」出口了多少價值 → 流量\n✓ (D) 進口：「每季/每年」進口了多少價值 → 流量\n其他流量：投資、GDP、稅收、折舊\n\n存量（Stock）：在「某一特定時點」衡量的數量\n✗ (A) 廠商所擁有的產出存貨：\n   「某一天（如年底）」廠房裡還剩多少存貨 → 存量\n其他存量：資本存量、貨幣供給、外匯存底、人口\n\n→ 答案 (A)：廠商所擁有的產出存貨（存量，非流量）",
  },
    {
    id: "eco-54",
    q: "如果在一個經濟體內消費超過產出時，代表：",
    opts: [
      "(A) 進口大於出口",
      "(B) 折舊超過消費",
      "(C) 政府支出大於政府稅收",
      "(D) 出口大於進口",
    ],
    ans: 0,
    hint: "🔑 消費 > 產出 → 不足部分從國外進口補充 → 進口 > 出口（貿易逆差）",
    isCalc: false,
    explain:
      "【總體恆等式推導】\n\n國民所得恆等式：\nY（產出）= C + I + G + NX\n其中 NX = X（出口）− M（進口）\n\n若消費超過產出（其他條件不變，簡化為 Y < C）：\nNX = Y − C − I − G < 0\n→ 出口 − 進口 < 0\n→ 進口 > 出口（貿易逆差）(A) ✓\n\n直觀理解：\n國內生產不足以滿足消費需求 → 必須向國外「進口」更多商品來彌補缺口\n→ 進口 > 出口\n\n→ 答案 (A)：進口大於出口",
  },
    {
    id: "eco-55",
    q: "我國外匯市場為了避免匯率波動太大，中央銀行仍會進場干預，請問我國是採取哪一種匯率制度？",
    opts: [
      "(A) 固定匯率",
      "(B) 自由浮動匯率",
      "(C) 管理浮動匯率制度",
      "(D) 管制匯率",
    ],
    ans: 2,
    hint: "🔑 台灣：由市場決定匯率為主，但必要時央行干預 → 管理浮動（Managed Float）",
    isCalc: false,
    explain:
      "匯率制度分類：\n\n(A) 固定匯率（Fixed / Pegged）：\n   匯率釘住某一貨幣（如美元），央行持續干預維持固定\n   例：香港港元釘住美元（聯繫匯率制）\n\n(B) 自由浮動匯率（Free / Clean Float）：\n   完全由外匯市場供需決定，央行完全不干預\n   例：理論上的美元\n\n(C) 管理浮動匯率制度（Managed / Dirty Float）✓：\n   基本上由市場決定，但當波動過大時央行進場干預\n   台灣採行此制度\n\n(D) 管制匯率（Exchange Control）：\n   政府嚴格管制外匯交易，通常設立黑市，如某些發展中國家\n\n→ 答案 (C)：管理浮動匯率制度",
  },
    {
    id: "eco-56",
    q: "豐田汽車台灣廠生產一部汽車，而且回銷日本。試問此一交易應列入那一個國家的 GDP？",
    opts: [
      "(A) 台灣",
      "(B) 日本",
      "(C) 兩國，且每一個國家均將此部汽車價值列入該國 GDP 中",
      "(D) 兩國，而每一個國家可將此部汽車 1/2 的價值列入該國 GDP 中",
    ],
    ans: 0,
    hint: "🔑 GDP = 地理原則：在台灣境內生產 → 計入台灣 GDP，不論廠商國籍",
    isCalc: false,
    explain:
      "GDP（國內生產毛額）的地理原則：\n\n凡「在台灣境內」生產的最終商品和勞務，不論生產者是台灣人還是外資（豐田日本公司），均計入「台灣的 GDP」。\n\n本題分析：\n• 生產地點：台灣 → 計入台灣 GDP ✓\n• 銷售（回銷日本）：這是台灣的出口，對台灣 GDP 是出口（+），對日本是進口（+）\n• 日本 GDP：日本「消費」這部車，但「生產」不在日本 → 不計入日本 GDP\n\n注意：日本消費這台車是日本的最終消費，但 GDP 計算的是「生產地」，不是「消費地」。\n\n→ 答案 (A)：台灣",
  },
    {
    id: "eco-57",
    q: "如果一國家對進口商品課以關稅時，該國人民所負擔稅額的大小取決於：",
    opts: [
      "(A) 供給彈性",
      "(B) 所得彈性",
      "(C) 與本國商品的需求交叉彈性",
      "(D) 需求彈性",
    ],
    ans: 3,
    hint: "🔑 關稅由誰負擔？取決於需求彈性：消費者需求越無彈性，承擔越多",
    isCalc: false,
    explain:
      "關稅的稅負分配（Tax Incidence）：\n\n課徵關稅後的影響：\n進口商品成本增加 → 進口商提高售價 → 消費者是否承擔取決於「需求彈性」\n\n需求彈性（D）的影響（D 正確）✓：\n\n• 需求彈性「小」（無彈性，如必需品）：\n  消費者無論價格多少都必須買 → 承擔大部分關稅\n\n• 需求彈性「大」（有彈性，如奢侈品）：\n  消費者容易找到替代品 → 大幅減少購買 → 進口商被迫自行吸收稅負\n  → 消費者承擔較少關稅\n\n類比：關稅就像一種消費稅，其負擔分配原理與其他稅的轉嫁分析相同。\n\n→ 答案 (D)：需求彈性",
  },
    {
    id: "eco-58",
    q: "如果把工作時間用來休閒，則休閒時間的機會成本（opportunity cost）指的是放棄工作這段時間中，各種可能工作機會中的：",
    opts: [
      "(A) 最高工資",
      "(B) 次高工資",
      "(C) 工資的加總",
      "(D) 最高與最低工資的差異",
    ],
    ans: 0,
    hint: "🔑 機會成本 = 放棄的「最佳替代方案」的價值（次一等最好的選擇）",
    isCalc: false,
    explain:
      "機會成本（Opportunity Cost）的精確定義：\n\n機會成本 = 選擇某方案時，所放棄的「最佳替代方案」的價值\n\n本題應用：\n選擇「休閒」→ 放棄「工作」\n工作有多種可能的機會，需選其中最好的（最高工資）作為機會成本。\n\n理由：\n理性的決策者會選擇最好的工作機會，因此：\n• 放棄的「最高工資（A）」才是真正的機會成本 ✓\n• 次高工資（B）不是機會成本，因為最高工資才是「被迫放棄」的最好替代\n• 加總（C）錯誤：只能選一個最好的，不是全部加總\n\n→ 答案 (A)：最高工資",
  },
    {
    id: "eco-59",
    q: "何謂需求法則？",
    opts: [
      "(A) 價格下降，需求增加",
      "(B) 價格下降，需求減少",
      "(C) 價格下降，需求變動方向不一定",
      "(D) 價格上升，供給增加",
    ],
    ans: 0,
    hint: "🔑 需求法則：價格 ↑ → 需求量 ↓（負向關係）；反之，價格 ↓ → 需求量 ↑",
    isCalc: false,
    explain:
      "需求法則（Law of Demand）：\n\n在其他條件不變（Ceteris Paribus）的情況下，\n商品價格「下降」→ 消費者購買的數量「增加」\n商品價格「上升」→ 消費者購買的數量「減少」\n\n→ 需求量與價格呈「反向（負向）」關係\n→ 需求曲線（Demand Curve）向右下方傾斜（負斜率）\n\n例外（不遵循需求法則）：\n• 季芬財（Giffen Goods）：極少數劣等財，漲價反而買更多（歷史案例：愛爾蘭馬鈴薯）\n• 凡勃倫財（Veblen Goods）：炫耀性商品，越貴越有人買（名牌奢侈品）\n\n→ 答案 (A)：價格下降，需求增加",
  },
    {
    id: "eco-60",
    q: "下列說明何者符合柏瑞圖效率（Pareto efficiency）的定義？",
    opts: [
      "(A) 損人不利己",
      "(B) 不損人無法利己",
      "(C) 不損人也可以利己",
      "(D) 損害所有人但無人得利",
    ],
    ans: 2,
    hint: "🔑 Pareto 改善：在不損害任何人的情況下，至少有一人獲益",
    isCalc: false,
    explain:
      "柏瑞圖效率（Pareto Efficiency / Pareto Optimality）：\n\n定義：達到一種資源配置狀態，在此狀態下，\n「不可能在不損害任何人的情況下，使某人的處境更好」\n\n柏瑞圖改善（Pareto Improvement）：\n「在不損害任何人的前提下，使至少一人的處境更好」\n= 「不損人也可以利己（C）」✓\n\n選項分析：\n(A) 損人不利己：負和賽局，最差\n(B) 不損人無法利己：已達 Pareto 效率（無法再改善），不是定義本身\n(C) 不損人也可以利己：這就是 Pareto 改善，代表資源配置「未達」Pareto 效率，還有改進空間 ✓\n(D) 損害所有人但無人得利：負和，最差\n\n→ 答案 (C)",
  },
    {
    id: "eco-61",
    q: "關於公共財的特性，以下何者為真？",
    opts: [
      "(A) 有獨享（rival）與排他（exclusive）性",
      "(B) 有獨享（rival）與不可排他（non-exclusive）性",
      "(C) 有共享（non-rival）與排他（exclusive）性",
      "(D) 有共享（non-rival）與不可排他（non-exclusive）性",
    ],
    ans: 3,
    hint: "🔑 公共財兩大特性：非競爭性（共享）+ 不可排他性；兩者兼具才是純公共財",
    isCalc: false,
    explain:
      "財貨的四種分類（依競爭性和排他性）：\n\n| 類型     | 競爭性    | 排他性    | 例子       |\n|---------|----------|----------|-----------|\n| 私有財   | 有（獨享）| 有（排他）| 一般商品   |\n| 俱樂部財 | 無（共享）| 有（排他）| 付費電視   |\n| 共有資源 | 有（獨享）| 無（不排他）| 海洋漁業  |\n| 公共財   | 無（共享）| 無（不排他）| 國防、路燈 |\n\n純公共財（D）✓：\n① 非競爭性（Non-rival / 共享）：一人使用不妨礙他人\n② 不可排他性（Non-exclusive / 非排他）：無法阻止未付費者享用\n→ 導致搭便車問題，市場失靈，需政府提供\n\n→ 答案 (D)",
  },
    {
    id: "eco-62",
    q: "不同於古典及新古典的成長理論，內生成長理論最強調的生產因素為以下何者？",
    opts: [
      "(A) 人力資本的累積",
      "(B) 實質資本的累積",
      "(C) 勞動力的累積",
      "(D) 外生的技術進步",
    ],
    ans: 0,
    hint: "🔑 內生成長理論（Lucas、Romer）：技術進步來自人力資本和知識的積累，非外生給定",
    isCalc: false,
    explain:
      "成長理論的演進：\n\n古典/新古典成長理論（Solow Model）：\n• 技術進步是「外生」給定的（D）\n• 儲蓄率和人口成長率決定資本累積\n• 長期成長率由外生技術進步率決定\n\n內生成長理論（Endogenous Growth Theory）（Lucas、Romer）：\n• 強調「人力資本（A）」的累積 ✓\n• 教育、訓練、研發（R&D）→ 人力資本↑ → 技術進步是「內生」的\n• 知識溢出效應（Knowledge Spillover）：新知識可被多人使用（非競爭性）\n• 政府投資教育和研發有長期成長效益\n\n→ 答案 (A)：人力資本的累積",
  },
    {
    id: "eco-63",
    q: "我國現行所得稅是採用何種稅制，以減輕所得分配的不均度：",
    opts: [
      "(A) 定額稅（lump-sum tax）",
      "(B) 比例稅（proportional tax）",
      "(C) 累進稅（progressive tax）",
      "(D) 累退稅（regressive tax）",
    ],
    ans: 2,
    hint: "🔑 累進稅：所得越高，稅率越高 → 縮小稅後所得差距 → 改善所得分配",
    isCalc: false,
    explain:
      "所得稅制度比較：\n\n(A) 定額稅（Lump-sum Tax）：每人繳固定金額，與所得無關\n   → 對高低收入者一視同仁，所得分配不改善\n\n(B) 比例稅（Flat Tax）：固定稅率，如每人繳所得的 20%\n   → 高低收入者稅率相同，所得差距縮小但效果有限\n\n(C) 累進稅（Progressive Tax）✓：\n   所得越高 → 適用稅率越高\n   台灣個人綜合所得稅：5%、12%、20%、30%、40%（最高級距）\n   → 高所得者繳更多稅 → 稅後所得差距縮小 → 有助於改善所得分配\n\n(D) 累退稅（Regressive Tax）：\n   所得越高，有效稅率反而越低（如貨物稅、菸酒稅）\n   → 加重低所得者負擔，惡化所得分配\n\n→ 答案 (C)：累進稅",
  },
    {
    id: "eco-64",
    q: "當中央銀行施行寬鬆性貨幣政策後，將使體系總合需求：",
    opts: [
      "(A) 增加",
      "(B) 減少",
      "(C) 不變",
      "(D) 不一定",
    ],
    ans: 0,
    hint: "🔑 寬鬆貨幣：M↑ → 利率↓ → 投資↑、消費↑ → AD 增加",
    isCalc: false,
    explain:
      "寬鬆性貨幣政策（Expansionary Monetary Policy）的傳導機制：\n\n央行增加貨幣供給（降準備率、公開市場買入、降重貼現率）\n↓\n市場資金充裕，利率下降\n↓\n① 投資增加：企業借貸成本↓ → 投資↑\n② 消費增加：消費貸款利率↓ → 消費↑\n③ 匯率效果：資金外流 → 本幣貶值 → 出口↑（淨出口↑）\n↓\nAD（總合需求）= C + I + G + NX → 全面增加\n↓\n總合需求曲線向右移動 → 均衡產出和物價水準上升\n\n→ 答案 (A)：總合需求增加",
  },
    {
    id: "eco-65",
    q: "管理證券市場交易之「證券暨期貨管理委員會」隸屬於政府單位之：",
    opts: [
      "(A) 中央銀行",
      "(B) 財政部",
      "(C) 經濟部",
      "(D) 行政院經濟建設委員會",
    ],
    ans: 1,
    hint: "🔑 台灣早期：證管會隸屬財政部（現已改制為金管會）",
    isCalc: false,
    explain:
      "台灣金融監理機構沿革（本題考歷史制度）：\n\n「證券暨期貨管理委員會」的隸屬（依題庫當時的制度）：\n→ 隸屬「財政部（B）」✓\n\n注意：2004 年台灣成立「金融監督管理委員會（金管會）」，整合原本分散在：\n• 財政部的銀行局、保險司\n• 財政部證券暨期貨管理委員會\n→ 現在證券和期貨市場的監管機關為「金管會」\n\n本題為歷史制度題，根據題庫內容，答案為財政部（B）。\n\n→ 答案 (B)：財政部",
  },
    {
    id: "eco-66",
    q: "物價水準持續下跌的現象稱為：",
    opts: [
      "(A) 通貨緊縮",
      "(B) 通貨膨脹",
      "(C) 通貨危機",
      "(D) 通貨緊張",
    ],
    ans: 0,
    hint: "🔑 通貨緊縮（Deflation）= 物價持續下跌，CPI 年增率為負",
    isCalc: false,
    explain:
      "通貨緊縮（Deflation）(A) ✓：\n物價水準「持續下跌」（CPI 年增率持續為負）的現象。\n\n通貨緊縮的危害（常被稱為「惡性螺旋」）：\n物價下跌 → 消費者等待更低價格，延後購買\n→ 總需求下降 → 廠商減產、裁員\n→ 失業增加、所得下降\n→ 消費進一步下降 → 物價繼續跌（惡性循環）\n歷史案例：1930 年代美國大蕭條、日本「失落的 20 年」\n\n其他概念區分：\n• 通貨膨脹（Inflation）(B)：物價持續上漲\n• 通縮危機（C、D）：非標準術語\n\n→ 答案 (A)：通貨緊縮",
  },
    {
    id: "eco-67",
    q: "美國商務部調查公布，初步認定中國、義大利和台灣補貼「鍛造鋼配件」，擬對台灣相關產品課徵 116.17% 的什麼稅？",
    opts: [
      "(A) 從量稅",
      "(B) 從價稅",
      "(C) 營業稅",
      "(D) 反傾銷稅",
    ],
    ans: 3,
    hint: "🔑 政府補貼出口商品 → 低價傾銷 → 進口國課徵「反傾銷稅」報復",
    isCalc: false,
    explain:
      "反傾銷稅（Anti-dumping Duty）(D) ✓：\n\n當他國廠商以低於生產成本或國內售價向本國傾銷商品時，進口國政府有權課徵「反傾銷稅」，以抵消不公平低價的競爭優勢。\n\n本題情境：\n• 認定台灣補貼鍛造鋼配件（政府補貼 = 一種補貼傾銷）\n• 美國商務部擬課徵 116.17% 的「反傾銷稅」\n\n各稅種說明：\n(A) 從量稅：按商品數量課稅（如：每公斤課 $X）\n(B) 從價稅：按商品價值百分比課稅（關稅大多如此）\n(C) 營業稅：國內消費稅（與進口傾銷無關）\n(D) 反傾銷稅 ✓：針對傾銷行為課徵的特定關稅\n\n→ 答案 (D)：反傾銷稅",
  },
    {
    id: "eco-68",
    q: "中央銀行制訂與施行貨幣政策，最主要之目的為：",
    opts: [
      "(A) 維持物價穩定",
      "(B) 促進經濟成長",
      "(C) 創造就業機會",
      "(D) 提高出口競爭力",
    ],
    ans: 0,
    hint: "🔑 各國央行最核心使命：物價穩定（Price Stability）— 通膨目標制",
    isCalc: false,
    explain:
      "中央銀行的使命（依重要性排列）：\n\n① 物價穩定（Price Stability）(A) ★最主要★：\n   • 低且穩定的通貨膨脹率（如 2%）\n   • 是其他目標（成長、就業）的基礎\n   • 台灣央行法明訂以「促進金融穩定及物價穩定」為主要任務\n\n② 充分就業（Full Employment）：\n   美聯準（Fed）有雙重使命（物價穩定 + 充分就業）\n   台灣央行以物價穩定為主\n\n③ 促進經濟成長（B）：間接目標\n④ 創造就業（C）：間接效果\n⑤ 提高出口競爭力（D）：非央行主要使命（可能涉及匯率政策，但非最主要）\n\n→ 答案 (A)：維持物價穩定",
  },
    {
    id: "eco-69",
    q: "當外匯買賣在目前簽訂，並議定日後交易的價格，此種外匯交易稱為：",
    opts: [
      "(A) 即期外匯交易",
      "(B) 遠期外匯交易",
      "(C) 定期外匯交易",
      "(D) 議定外匯交易",
    ],
    ans: 1,
    hint: "🔑 現在簽約、約定未來交割的外匯交易 = 遠期外匯（Forward Exchange）",
    isCalc: false,
    explain:
      "外匯交易類型：\n\n(A) 即期外匯交易（Spot Exchange）：\n   立即（或 2 個工作日內）完成的外匯交割\n   → 今天簽約，今天（或 T+2）交割，以當前即期匯率成交\n\n(B) 遠期外匯交易（Forward Exchange）✓：\n   「現在」簽訂合約，約定「未來某一日期」以「議定的遠期匯率」交割\n   → 目的：鎖定未來的匯率，規避匯率風險\n   → 常用於進出口企業的外匯避險\n   例：出口商 3 個月後會收到美元，現在先賣出 3 個月遠期美元，鎖定兌換率\n\n(C)(D) 非標準金融術語\n\n→ 答案 (B)：遠期外匯交易",
  },
    {
    id: "eco-70",
    q: "下列那一項是貿易政策？",
    opts: [
      "(A) 引進外籍勞工",
      "(B) 出口自動設限",
      "(C) 投資抵減",
      "(D) 減徵土地增值稅",
    ],
    ans: 1,
    hint: "🔑 貿易政策 = 直接影響進出口貿易的政策工具（關稅、配額、出口設限等）",
    isCalc: false,
    explain:
      "各政策的分類：\n\n貿易政策（Trade Policy）：直接管制商品或服務的國際流通\n✓ (B) 出口自動設限（Voluntary Export Restraint, VER）：\n   出口國主動同意限制出口量（通常為了避免進口國採取更嚴格措施）\n   例：1980 年代日本限制對美汽車出口\n   → 直接影響貿易數量，是貿易政策 ✓\n\n非貿易政策（其他政策）：\n✗ (A) 引進外籍勞工：勞動力政策（移民/勞動市場政策）\n✗ (C) 投資抵減：租稅優惠政策（財稅政策），鼓勵國內投資\n✗ (D) 減徵土地增值稅：財稅政策（不動產稅務政策）\n\n→ 答案 (B)：出口自動設限",
  },
    {
    id: "eco-71",
    q: "家電業者為了促銷，經常舉辦贈品與摸彩活動，這是何種競爭的型態？",
    opts: [
      "(A) 價格競爭",
      "(B) 非價格競爭",
      "(C) 數量競爭",
      "(D) 非數量競爭",
    ],
    ans: 1,
    hint: "🔑 不改變商品售價，以其他方式（贈品、摸彩）吸引消費者 = 非價格競爭",
    isCalc: false,
    explain:
      "競爭類型分類：\n\n• 價格競爭（Price Competition）：\n  以降低商品售價吸引消費者\n  例：打折、促銷價\n\n• 非價格競爭（Non-price Competition）✓：\n  不改變價格，透過其他手段吸引消費者\n  例：廣告、改善品質、贈品（B）、摸彩（B）、售後服務、品牌形象、包裝設計\n\n贈品和摸彩活動：\n• 商品售價不變\n• 以「附加吸引力」吸引消費者\n→ 屬非價格競爭（B）✓\n\n非價格競爭在「獨占性競爭」和「寡占」市場中特別常見（廠商不願意引發價格戰）。\n\n→ 答案 (B)：非價格競爭",
  },
    {
    id: "eco-72",
    q: "因為經濟租是指高過機會成本的報酬，所以也就是：",
    opts: [
      "(A) 超額利潤",
      "(B) 社會剩餘",
      "(C) 消費者剩餘",
      "(D) 生產者剩餘",
    ],
    ans: 3,
    hint: "🔑 經濟租 = 超過機會成本的報酬 = 生產者獲得的超額報酬 = 生產者剩餘",
    isCalc: false,
    explain:
      "經濟租（Economic Rent）的概念：\n\n定義：要素供給者獲得的報酬，超過其機會成本（保留價格）的部分\n\n類比關係：\n• 消費者剩餘（Consumer Surplus）：消費者的保留價格 − 市場價格（買者的超額）\n• 生產者剩餘（Producer Surplus）(D) ✓：市場價格 − 供給者的機會成本（賣者的超額）\n→ 生產者剩餘就是「廠商/要素供給者超過機會成本的報酬」\n→ 與「經濟租」概念一致\n\n區別超額利潤（A）：\n• 超額利潤是長期下超過正常利潤的部分（獨占市場才有）\n• 生產者剩餘是短期的概念\n\n→ 答案 (D)：生產者剩餘",
  },
    {
    id: "eco-73",
    q: "廠商的機會成本通常是：（複習同 eco-01）",
    opts: [
      "(A) 只有外顯成本",
      "(B) 只有隱藏成本",
      "(C) 外顯成本加隱藏成本",
      "(D) 只有會計成本",
    ],
    ans: 2,
    hint: "🔑 機會成本（經濟觀點）= 外顯成本 + 隱藏成本（被放棄的最佳替代方案的價值）",
    isCalc: false,
    explain:
      "機會成本（Opportunity Cost）完整定義（重要複習）：\n\n• 外顯成本（Explicit Cost）：\n  有實際貨幣支出的成本（租金、薪資、原料費等）\n  → 也就是「會計成本」\n\n• 隱藏成本（Implicit Cost）：\n  放棄其他用途的機會代價，沒有實際貨幣支出\n  例：業主自己的時間（若去上班的薪資）、自用廠房的市場租金價值\n\n• 機會成本 = 外顯成本 + 隱藏成本 (C) ✓\n  （比會計成本更廣泛）\n\n• 經濟利潤 = 會計利潤 − 隱藏成本\n  若經濟利潤 > 0：值得繼續從事此業務\n  若經濟利潤 ≤ 0：應考慮轉行（即使有正的會計利潤）\n\n→ 答案 (C)：外顯成本加隱藏成本",
  },
    {
    id: "eco-74",
    q: "一國貨幣當局用以挹注國際收支逆差時，可動用之外幣資產稱為：",
    opts: [
      "(A) 外匯存款",
      "(B) 外匯存底",
      "(C) 外幣準備金",
      "(D) 外幣預備金",
    ],
    ans: 1,
    hint: "🔑 外匯存底（Foreign Exchange Reserves）= 央行持有的外幣資產，用於干預匯市或彌補國際收支",
    isCalc: false,
    explain:
      "外匯存底（Foreign Exchange Reserves / International Reserves）(B) ✓：\n\n定義：\n中央銀行和貨幣當局持有的外幣資產（包括：美元、歐元等外國貨幣、黃金、特別提款權 SDR 等），可隨時動用。\n\n主要用途：\n① 穩定匯率：當本幣貶值壓力過大，賣出外幣買進本幣（或反之）\n② 支應國際收支逆差：當進口大於出口造成外匯短缺，動用外匯存底彌補\n③ 維持市場信心：龐大的外匯存底是金融穩定的「保障」\n\n台灣外匯存底排名全球前幾名（約 5,000 億美元以上）。\n\n→ 答案 (B)：外匯存底",
  },
    {
    id: "eco-75",
    q: "在下列何種情況下，廠商雖有虧損，短期仍會繼續經營而不關門？",
    opts: [
      "(A) 價格足以支付平均變動成本",
      "(B) 總收益超過總固定成本",
      "(C) 平均變動成本大於邊際成本",
      "(D) 平均固定成本大於平均變動成本",
    ],
    ans: 0,
    hint: "🔑 短期關門決策：P < AVC（虧損連變動成本都補不回）→ 關門；P ≥ AVC → 繼續",
    isCalc: false,
    explain:
      "廠商短期關門決策（Shutdown Decision）：\n\n固定成本（Fixed Cost）：短期無法避免，無論開不開門都要支付（如租金）\n變動成本（Variable Cost）：只有在生產時才發生\n\n短期繼續經營的條件：\n只要「收入」能彌補「變動成本」，就比關門強：\n• 若 P ≥ AVC（A）✓：繼續生產，至少可部分回收固定成本\n• 若 P < AVC：不如關門，損失僅限固定成本（比繼續生產虧更多要好）\n\n關門點（Shutdown Point）= AVC 的最低點\n\n圖形說明：\n• P = AVC 時：收入 = 變動成本，固定成本全部虧損（但比關門一樣）\n• P 介於 AVC 和 ATC 之間：虧損但仍繼續（可彌補部分固定成本）\n• P < AVC：關門（損失更少）\n\n→ 答案 (A)：價格足以支付平均變動成本",
  }
  ],
};
// ─── 色彩 & 動畫 樣式常數 ────────────────────────────────────────────────────
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@300;400;500;600&family=Noto+Sans+TC:wght@300;400;500;700&display=swap');

  :root {
    --navy:    #050d1a;
    --navy2:   #0a1628;
    --navy3:   #0e1f38;
    --navy4:   #162847;
    --blue:    #1a3a6b;
    --accent:  #2a7fff;
    --accent2: #4fa8ff;
    --gold:    #d4a843;
    --gold2:   #f0c96a;
    --silver:  #8fa8c8;
    --white:   #e8f0fc;
    --dimwhite:#b0c8e8;
    --success: #22d3a0;
    --error:   #f05a5a;
    --warn:    #f0b429;
    --border:  rgba(42,127,255,0.18);
    --border2: rgba(42,127,255,0.35);
    --glow:    rgba(42,127,255,0.12);
  }

  * { box-sizing: border-box; margin: 0; padding: 0; }

  body, #root {
    background: var(--navy);
    min-height: 100vh;
    font-family: 'Noto Sans TC', sans-serif;
    color: var(--white);
  }

  .mono { font-family: 'IBM Plex Mono', monospace; }

  /* Scanline overlay */
  .scanlines::before {
    content: '';
    position: fixed;
    inset: 0;
    background: repeating-linear-gradient(
      0deg,
      transparent,
      transparent 2px,
      rgba(0,0,0,0.03) 2px,
      rgba(0,0,0,0.03) 4px
    );
    pointer-events: none;
    z-index: 9999;
  }

  /* Grid background */
  .grid-bg {
    background-image:
      linear-gradient(rgba(42,127,255,0.04) 1px, transparent 1px),
      linear-gradient(90deg, rgba(42,127,255,0.04) 1px, transparent 1px);
    background-size: 40px 40px;
  }

  /* Fade-in animation */
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(16px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes fadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  @keyframes pulseGlow {
    0%, 100% { box-shadow: 0 0 0 0 rgba(42,127,255,0); }
    50%       { box-shadow: 0 0 20px 4px rgba(42,127,255,0.25); }
  }
  @keyframes shimmer {
    0%   { background-position: -200% center; }
    100% { background-position: 200% center; }
  }
  @keyframes barGrow {
    from { width: 0; }
  }
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  @keyframes blink {
    0%, 100% { opacity: 1; }
    50%       { opacity: 0; }
  }

  .anim-fadeup  { animation: fadeUp 0.4s ease both; }
  .anim-fadein  { animation: fadeIn 0.3s ease both; }
  .anim-glow    { animation: pulseGlow 2.5s ease-in-out infinite; }

  /* Card styles */
  .card {
    background: linear-gradient(135deg, var(--navy2) 0%, var(--navy3) 100%);
    border: 1px solid var(--border);
    border-radius: 12px;
    backdrop-filter: blur(8px);
  }
  .card-hover {
    transition: all 0.25s ease;
  }
  .card-hover:hover {
    border-color: var(--border2);
    background: linear-gradient(135deg, var(--navy3) 0%, var(--navy4) 100%);
    transform: translateY(-2px);
    box-shadow: 0 8px 32px rgba(42,127,255,0.12);
  }

  /* Button styles */
  .btn-primary {
    background: linear-gradient(135deg, var(--accent) 0%, #1a5fcc 100%);
    color: var(--white);
    border: none;
    border-radius: 8px;
    padding: 10px 24px;
    font-family: 'Noto Sans TC', sans-serif;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    letter-spacing: 0.5px;
  }
  .btn-primary:hover {
    background: linear-gradient(135deg, var(--accent2) 0%, var(--accent) 100%);
    box-shadow: 0 4px 20px rgba(42,127,255,0.4);
    transform: translateY(-1px);
  }
  .btn-primary:active { transform: translateY(0); }

  .btn-ghost {
    background: transparent;
    color: var(--silver);
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 9px 20px;
    font-family: 'Noto Sans TC', sans-serif;
    font-size: 13px;
    cursor: pointer;
    transition: all 0.2s ease;
    letter-spacing: 0.3px;
  }
  .btn-ghost:hover {
    border-color: var(--accent);
    color: var(--accent2);
    background: var(--glow);
  }

  .btn-gold {
    background: linear-gradient(135deg, var(--gold) 0%, #b8860b 100%);
    color: #1a0e00;
    border: none;
    border-radius: 8px;
    padding: 10px 24px;
    font-family: 'Noto Sans TC', sans-serif;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.2s ease;
    letter-spacing: 0.5px;
  }
  .btn-gold:hover {
    background: linear-gradient(135deg, var(--gold2) 0%, var(--gold) 100%);
    box-shadow: 0 4px 20px rgba(212,168,67,0.4);
    transform: translateY(-1px);
  }

  /* Option buttons */
  .opt-btn {
    width: 100%;
    text-align: left;
    background: rgba(10,22,40,0.8);
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 14px 18px;
    color: var(--dimwhite);
    font-family: 'Noto Sans TC', sans-serif;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s ease;
    line-height: 1.6;
  }
  .opt-btn:hover:not(:disabled) {
    border-color: var(--accent);
    background: rgba(42,127,255,0.08);
    color: var(--white);
    transform: translateX(4px);
  }
  .opt-btn:disabled { cursor: default; transform: none !important; }
  .opt-correct {
    border-color: var(--success) !important;
    background: rgba(34,211,160,0.1) !important;
    color: var(--success) !important;
  }
  .opt-wrong {
    border-color: var(--error) !important;
    background: rgba(240,90,90,0.08) !important;
    color: var(--error) !important;
  }
  .opt-reveal {
    border-color: var(--success) !important;
    background: rgba(34,211,160,0.06) !important;
    color: var(--success) !important;
  }

  /* Hint box */
  .hint-box {
    background: linear-gradient(135deg, rgba(212,168,67,0.08), rgba(212,168,67,0.03));
    border: 1px solid rgba(212,168,67,0.35);
    border-radius: 8px;
    padding: 14px 18px;
    color: var(--gold2);
    font-size: 13.5px;
    line-height: 1.7;
    animation: fadeUp 0.3s ease;
  }

  /* Explain box */
  .explain-box {
    background: linear-gradient(135deg, rgba(42,127,255,0.06), rgba(42,127,255,0.02));
    border-left: 3px solid var(--accent);
    border-radius: 0 8px 8px 0;
    padding: 16px 20px;
    color: var(--dimwhite);
    font-size: 13.5px;
    line-height: 1.8;
    white-space: pre-line;
    animation: fadeUp 0.35s ease;
  }

  /* Progress bar */
  .progress-track {
    background: rgba(255,255,255,0.06);
    border-radius: 99px;
    height: 5px;
    overflow: hidden;
  }
  .progress-fill {
    height: 100%;
    border-radius: 99px;
    background: linear-gradient(90deg, var(--accent) 0%, var(--accent2) 100%);
    transition: width 0.5s cubic-bezier(0.4,0,0.2,1);
    box-shadow: 0 0 8px var(--accent);
  }

  /* Calc badge */
  .calc-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    background: rgba(240,180,41,0.12);
    border: 1px solid rgba(240,180,41,0.4);
    color: var(--warn);
    font-size: 11px;
    font-family: 'IBM Plex Mono', monospace;
    padding: 3px 10px;
    border-radius: 4px;
    letter-spacing: 0.5px;
  }

  /* Chapter badge */
  .chapter-badge {
    display: inline-block;
    font-family: 'IBM Plex Mono', monospace;
    font-size: 10px;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    padding: 3px 10px;
    border-radius: 3px;
    background: rgba(42,127,255,0.12);
    border: 1px solid rgba(42,127,255,0.25);
    color: var(--accent2);
  }

  /* Score ring */
  .score-ring {
    width: 120px;
    height: 120px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
  }

  /* Scrollbar */
  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-track { background: var(--navy2); }
  ::-webkit-scrollbar-thumb { background: var(--blue); border-radius: 2px; }
  ::-webkit-scrollbar-thumb:hover { background: var(--accent); }

  /* Modal overlay */
  .modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(5,13,26,0.85);
    backdrop-filter: blur(6px);
    z-index: 100;
    display: flex;
    align-items: center;
    justify-content: center;
    animation: fadeIn 0.2s ease;
  }

  /* Cursor blink */
  .cursor::after {
    content: '█';
    font-size: 0.8em;
    animation: blink 1s step-end infinite;
    color: var(--accent);
  }

  /* Tag */
  .tag-wrong {
    background: rgba(240,90,90,0.12);
    border: 1px solid rgba(240,90,90,0.3);
    color: var(--error);
    font-size: 11px;
    padding: 2px 8px;
    border-radius: 3px;
  }
`;

// ─── 子元件 ───────────────────────────────────────────────────────────────────

// Header
function Header({ onHome, wrongCount }) {
  return (
    <header style={{
      background: "linear-gradient(180deg, rgba(5,13,26,0.98) 0%, rgba(10,22,40,0.95) 100%)",
      borderBottom: "1px solid var(--border)",
      padding: "0 24px",
      height: 56,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      position: "sticky",
      top: 0,
      zIndex: 50,
      backdropFilter: "blur(12px)",
    }}>
      <button onClick={onHome} style={{
        background: "none", border: "none", cursor: "pointer",
        display: "flex", alignItems: "center", gap: 10,
      }}>
        <div style={{
          width: 32, height: 32, borderRadius: 8,
          background: "linear-gradient(135deg, var(--accent) 0%, #1a3a6b 100%)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 16, boxShadow: "0 0 12px rgba(42,127,255,0.4)",
        }}>📊</div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--white)", letterSpacing: 1 }}>
            EFOA 考證神器
          </div>
          <div className="mono" style={{ fontSize: 9, color: "var(--silver)", letterSpacing: 2 }}>
            ENTERPRISE FINANCE ANALYST
          </div>
        </div>
      </button>

      <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
        <div className="mono" style={{ fontSize: 11, color: "var(--silver)" }}>
          題庫 · 300 題
        </div>
        {wrongCount > 0 && (
          <div style={{
            background: "rgba(240,90,90,0.12)",
            border: "1px solid rgba(240,90,90,0.3)",
            borderRadius: 6,
            padding: "3px 10px",
            fontSize: 12,
            color: "var(--error)",
            display: "flex", alignItems: "center", gap: 5,
          }}>
            ✗ 錯題 {wrongCount}
          </div>
        )}
      </div>
    </header>
  );
}

// Progress Bar
function ProgressBar({ current, total, label }) {
  const pct = total > 0 ? Math.round((current / total) * 100) : 0;
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
        <span style={{ fontSize: 11, color: "var(--silver)", fontFamily: "IBM Plex Mono" }}>
          {label || "進度"}
        </span>
        <span style={{ fontSize: 11, color: "var(--accent2)", fontFamily: "IBM Plex Mono" }}>
          {current} / {total} · {pct}%
        </span>
      </div>
      <div className="progress-track">
        <div className="progress-fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// Chapter Icon Map
const CHAPTER_META = {
  財務報表分析: { icon: "📈", code: "FSA", color: "#2a7fff", desc: "Financial Statement Analysis" },
  貨幣銀行學:   { icon: "🏦", code: "MBA", color: "#22d3a0", desc: "Money & Banking" },
  會計學:       { icon: "📒", code: "ACC", color: "#d4a843", desc: "Accounting" },
  經濟學:       { icon: "⚖️",  code: "ECO", color: "#a78bfa", desc: "Economics" },
};

// ─── 頁面：首頁 ──────────────────────────────────────────────────────────────
function HomePage({ onChapter, onExam, onWrongBook, wrongCount, chapterStats }) {
  return (
    <div className="anim-fadeup" style={{ padding: "32px 24px", maxWidth: 820, margin: "0 auto" }}>

      {/* Hero */}
      <div style={{ textAlign: "center", marginBottom: 40 }}>
        <div className="mono" style={{ fontSize: 11, color: "var(--accent2)", letterSpacing: 4, marginBottom: 10 }}>
          EFOA · ENTERPRISE FINANCE OPERATION ANALYST
        </div>
        <h1 style={{
          fontSize: 30, fontWeight: 700, color: "var(--white)",
          lineHeight: 1.3, marginBottom: 8,
          background: "linear-gradient(135deg, var(--white) 30%, var(--accent2) 100%)",
          WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
        }}>
          企業財務企劃師<br />考證刷題系統
        </h1>
        <p style={{ fontSize: 13, color: "var(--silver)", lineHeight: 1.6 }}>
          300 題完整題庫 · 分章節練習 · 50 題全真模擬考 · 錯題本
        </p>
        <div style={{ display: "flex", justifyContent: "center", gap: 16, marginTop: 14, flexWrap: "wrap" }}>
          {["50 題隨機模擬考", "每題 2 分 / 滿分 100", "70 分及格", "60 分鐘"].map(t => (
            <span key={t} className="mono" style={{
              fontSize: 11, color: "var(--silver)", padding: "3px 10px",
              border: "1px solid var(--border)", borderRadius: 4,
            }}>{t}</span>
          ))}
        </div>
      </div>

      {/* 分章節練習 */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
          <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
          <span className="mono" style={{ fontSize: 11, color: "var(--silver)", letterSpacing: 2 }}>分章節練習</span>
          <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          {CHAPTERS.map((ch, i) => {
            const meta = CHAPTER_META[ch];
            const stat = chapterStats[ch] || { done: 0, total: 75, correct: 0 };
            const pct = stat.total > 0 ? Math.round((stat.done / stat.total) * 100) : 0;
            return (
              <button key={ch} className="card card-hover" onClick={() => onChapter(ch)}
                style={{
                  border: "none", cursor: "pointer", padding: "20px",
                  textAlign: "left", animation: `fadeUp 0.4s ease ${i * 0.07}s both`,
                }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                  <div style={{
                    width: 42, height: 42, borderRadius: 10, flexShrink: 0,
                    background: `${meta.color}18`,
                    border: `1px solid ${meta.color}40`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 20,
                  }}>{meta.icon}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: "var(--white)", marginBottom: 2 }}>
                      {ch}
                    </div>
                    <div className="mono" style={{ fontSize: 10, color: "var(--silver)", letterSpacing: 1 }}>
                      {meta.desc}
                    </div>
                    <div style={{ marginTop: 10 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                        <span style={{ fontSize: 10, color: "var(--silver)" }}>完成 {stat.done} / {stat.total}</span>
                        <span className="mono" style={{ fontSize: 10, color: meta.color }}>{pct}%</span>
                      </div>
                      <div className="progress-track">
                        <div style={{
                          height: "100%", borderRadius: 99,
                          background: `linear-gradient(90deg, ${meta.color}, ${meta.color}bb)`,
                          width: `${pct}%`, transition: "width 0.5s ease",
                          boxShadow: `0 0 6px ${meta.color}`,
                        }} />
                      </div>
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* 快捷功能 */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <button className="card card-hover" onClick={onExam} style={{
          border: "none", cursor: "pointer", padding: "20px",
          textAlign: "left", animation: "fadeUp 0.4s ease 0.28s both",
        }}>
          <div style={{ fontSize: 24, marginBottom: 10 }}>🎯</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "var(--white)", marginBottom: 4 }}>
            全真模擬考
          </div>
          <div style={{ fontSize: 12, color: "var(--silver)", lineHeight: 1.5 }}>
            隨機抽取 50 題 · 計時 60 分鐘<br />70 分及格，完整評分報告
          </div>
          <div style={{ marginTop: 12 }}>
            <span className="btn-primary" style={{ display: "inline-block", fontSize: 12, padding: "6px 16px" }}>
              開始考試 →
            </span>
          </div>
        </button>

        <button className="card card-hover" onClick={onWrongBook} style={{
          border: `1px solid ${wrongCount > 0 ? "rgba(240,90,90,0.3)" : "var(--border)"}`,
          cursor: "pointer", padding: "20px",
          textAlign: "left", animation: "fadeUp 0.4s ease 0.35s both",
          background: wrongCount > 0
            ? "linear-gradient(135deg, rgba(240,90,90,0.08) 0%, var(--navy3) 100%)"
            : undefined,
        }}>
          <div style={{ fontSize: 24, marginBottom: 10 }}>📕</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "var(--white)", marginBottom: 4 }}>
            錯題本
          </div>
          <div style={{ fontSize: 12, color: "var(--silver)", lineHeight: 1.5 }}>
            自動記錄所有答錯題目<br />針對弱點專項複習
          </div>
          <div style={{ marginTop: 12 }}>
            {wrongCount > 0 ? (
              <span style={{
                fontSize: 12, color: "var(--error)",
                background: "rgba(240,90,90,0.1)", border: "1px solid rgba(240,90,90,0.3)",
                padding: "5px 14px", borderRadius: 6, display: "inline-block",
              }}>
                {wrongCount} 題待複習 →
              </span>
            ) : (
              <span style={{ fontSize: 12, color: "var(--silver)" }}>尚無錯題記錄</span>
            )}
          </div>
        </button>
      </div>
    </div>
  );
}

// ─── 頁面：刷題模式 ───────────────────────────────────────────────────────────
function QuizPage({ chapter, questions, wrongSet, onWrongUpdate, onBack }) {
  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState(null);  // index of chosen option
  const [showHint, setShowHint] = useState(false);
  const [showExplain, setShowExplain] = useState(false);
  const [answered, setAnswered] = useState(false);
  const [sessionCorrect, setSessionCorrect] = useState(0);
  const [sessionTotal, setSessionTotal] = useState(0);
  const [finished, setFinished] = useState(false);

  const q = questions[idx];
  const meta = CHAPTER_META[chapter];

  const reset = () => {
    setSelected(null);
    setShowHint(false);
    setShowExplain(false);
    setAnswered(false);
  };

  const handleSelect = (optIdx) => {
    if (answered) return;
    setSelected(optIdx);
    setAnswered(true);
    setShowExplain(true);
    setSessionTotal(t => t + 1);
    const isCorrect = optIdx === q.ans;
    if (isCorrect) {
      setSessionCorrect(c => c + 1);
    }
    // Update wrong set
    const newWrong = new Set(wrongSet);
    if (!isCorrect) {
      newWrong.add(q.id);
    } else {
      newWrong.delete(q.id);
    }
    onWrongUpdate(newWrong);
  };

  const handleNext = () => {
    if (idx + 1 >= questions.length) {
      setFinished(true);
    } else {
      setIdx(i => i + 1);
      reset();
    }
  };

  const handleRestart = () => {
    setIdx(0);
    setSessionCorrect(0);
    setSessionTotal(0);
    setFinished(false);
    reset();
  };

  if (finished) {
    const pct = Math.round((sessionCorrect / sessionTotal) * 100);
    return (
      <div className="anim-fadeup" style={{ padding: "32px 24px", maxWidth: 600, margin: "0 auto" }}>
        <div className="card" style={{ padding: 32, textAlign: "center" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>
            {pct >= 80 ? "🏆" : pct >= 60 ? "📚" : "💪"}
          </div>
          <div className="mono" style={{ fontSize: 11, color: "var(--silver)", letterSpacing: 3, marginBottom: 8 }}>
            CHAPTER COMPLETE
          </div>
          <h2 style={{ fontSize: 22, color: "var(--white)", marginBottom: 6 }}>{chapter}</h2>
          <div style={{
            fontSize: 56, fontWeight: 700, fontFamily: "IBM Plex Mono",
            color: pct >= 70 ? "var(--success)" : "var(--error)",
            marginBottom: 4,
          }}>{pct}<span style={{ fontSize: 24 }}>%</span></div>
          <p style={{ fontSize: 14, color: "var(--silver)", marginBottom: 24 }}>
            答對 {sessionCorrect} / {sessionTotal} 題
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
            <button className="btn-ghost" onClick={handleRestart}>重新練習</button>
            <button className="btn-primary" onClick={onBack}>返回首頁</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: "24px", maxWidth: 720, margin: "0 auto" }}>

      {/* Top bar */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <button className="btn-ghost" onClick={onBack} style={{ padding: "6px 12px", fontSize: 12 }}>
          ← 返回
        </button>
        <div style={{ flex: 1 }}>
          <ProgressBar current={idx} total={questions.length} label={`${chapter} 練習`} />
        </div>
        {sessionTotal > 0 && (
          <div className="mono" style={{
            fontSize: 11, padding: "4px 10px",
            background: "rgba(34,211,160,0.1)", border: "1px solid rgba(34,211,160,0.25)",
            borderRadius: 6, color: "var(--success)", whiteSpace: "nowrap",
          }}>
            ✓ {sessionCorrect}/{sessionTotal}
          </div>
        )}
      </div>

      {/* Question card */}
      <div key={q.id} className="card anim-fadeup" style={{ padding: "28px 28px 24px" }}>

        {/* Meta */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
          <span style={{
            fontFamily: "IBM Plex Mono", fontSize: 11, letterSpacing: 1,
            color: meta.color,
          }}>{meta.icon} {meta.code}</span>
          <span className="mono" style={{ fontSize: 11, color: "var(--silver)" }}>
            · Q{idx + 1}
          </span>
          {q.isCalc && (
            <span className="calc-badge">⚡ 計算題</span>
          )}
          {wrongSet.has(q.id) && (
            <span className="tag-wrong">✗ 曾錯誤</span>
          )}
        </div>

        {/* Question */}
        <p style={{
          fontSize: 16, fontWeight: 500, color: "var(--white)",
          lineHeight: 1.75, marginBottom: 22,
        }}>{q.q}</p>

        {/* Options */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
          {q.opts.map((opt, i) => {
            let cls = "opt-btn";
            if (answered) {
              if (i === q.ans) cls += " opt-correct";
              else if (i === selected && i !== q.ans) cls += " opt-wrong";
            }
            return (
              <button key={i} className={cls} onClick={() => handleSelect(i)} disabled={answered}>
                {opt}
              </button>
            );
          })}
        </div>

        {/* Action row */}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
          {!answered && (
            <button className="btn-ghost" onClick={() => setShowHint(h => !h)}
              style={{ fontSize: 12, padding: "7px 14px" }}>
              {showHint ? "▲ 隱藏提示" : "💡 思維提示"}
            </button>
          )}
          {answered && !showExplain && (
            <button className="btn-ghost" onClick={() => setShowExplain(true)}
              style={{ fontSize: 12, padding: "7px 14px" }}>
              📖 查看詳解
            </button>
          )}
          {answered && (
            <button className="btn-primary" onClick={handleNext}
              style={{ fontSize: 13, marginLeft: "auto" }}>
              {idx + 1 < questions.length ? "下一題 →" : "完成練習 🎉"}
            </button>
          )}
        </div>

        {/* Hint */}
        {showHint && !answered && (
          <div className="hint-box">
            <span style={{ fontFamily: "IBM Plex Mono", fontSize: 10, letterSpacing: 1, color: "var(--gold)", marginRight: 8 }}>
              HINT
            </span>
            {q.hint}
          </div>
        )}

        {/* Explain */}
        {showExplain && answered && (
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, marginTop: 4 }}>
              <span style={{
                padding: "2px 8px", borderRadius: 4, fontSize: 12,
                background: selected === q.ans ? "rgba(34,211,160,0.15)" : "rgba(240,90,90,0.12)",
                color: selected === q.ans ? "var(--success)" : "var(--error)",
                border: `1px solid ${selected === q.ans ? "rgba(34,211,160,0.35)" : "rgba(240,90,90,0.3)"}`,
              }}>
                {selected === q.ans ? "✓ 答對了！" : "✗ 答錯了"}
              </span>
              {q.isCalc && (
                <span style={{ fontSize: 12, color: "var(--warn)" }}>📐 含計算步驟</span>
              )}
            </div>
            <div className="explain-box">{q.explain}</div>
          </div>
        )}
      </div>

      {/* Nav */}
      <div style={{ display: "flex", gap: 8, marginTop: 16, justifyContent: "center" }}>
        {questions.map((_, i) => (
          <div key={i} style={{
            width: 8, height: 8, borderRadius: "50%",
            background: i === idx ? "var(--accent)" : i < idx ? "rgba(42,127,255,0.35)" : "rgba(255,255,255,0.1)",
            cursor: i <= idx ? "pointer" : "default",
            transition: "all 0.2s",
            boxShadow: i === idx ? "0 0 6px var(--accent)" : "none",
          }} onClick={() => { if (i < idx) { setIdx(i); reset(); } }} />
        ))}
      </div>
    </div>
  );
}

// ─── 頁面：模擬考 ─────────────────────────────────────────────────────────────
function ExamPage({ allQuestions, wrongSet, onWrongUpdate, onBack }) {
  const EXAM_COUNT = 50;
  const TIME_LIMIT = 60 * 60; // 60 min in seconds

  const [examQuestions, setExamQuestions] = useState([]);
  const [answers, setAnswers] = useState({});       // { qId: optIdx }
  const [currentIdx, setCurrentIdx] = useState(0);
  const [phase, setPhase] = useState("ready");      // ready | exam | result
  const [timeLeft, setTimeLeft] = useState(TIME_LIMIT);
  const timerRef = useRef(null);

  // Build exam question pool from allQuestions
  const buildExam = useCallback(() => {
    const pool = [];
    Object.values(allQuestions).forEach(qs => pool.push(...qs));
    const shuffled = [...pool].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, Math.min(EXAM_COUNT, shuffled.length));
  }, [allQuestions]);

  const startExam = () => {
    setExamQuestions(buildExam());
    setAnswers({});
    setCurrentIdx(0);
    setTimeLeft(TIME_LIMIT);
    setPhase("exam");
  };

  // Timer
  useEffect(() => {
    if (phase === "exam") {
      timerRef.current = setInterval(() => {
        setTimeLeft(t => {
          if (t <= 1) {
            clearInterval(timerRef.current);
            setPhase("result");
            return 0;
          }
          return t - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [phase]);

  const submitExam = () => {
    clearInterval(timerRef.current);
    // Update wrong set
    const newWrong = new Set(wrongSet);
    examQuestions.forEach(q => {
      if (answers[q.id] === undefined) return;
      if (answers[q.id] !== q.ans) newWrong.add(q.id);
      else newWrong.delete(q.id);
    });
    onWrongUpdate(newWrong);
    setPhase("result");
  };

  const handleAnswer = (qId, optIdx) => {
    setAnswers(a => ({ ...a, [qId]: optIdx }));
  };

  const formatTime = (s) => {
    const m = Math.floor(s / 60).toString().padStart(2, "0");
    const sec = (s % 60).toString().padStart(2, "0");
    return `${m}:${sec}`;
  };

  // ── Ready screen
  if (phase === "ready") {
    const pool = Object.values(allQuestions).flat();
    return (
      <div className="anim-fadeup" style={{ padding: "32px 24px", maxWidth: 600, margin: "0 auto" }}>
        <button className="btn-ghost" onClick={onBack} style={{ marginBottom: 24, fontSize: 12 }}>← 返回</button>
        <div className="card" style={{ padding: "36px 32px", textAlign: "center" }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🎯</div>
          <h2 style={{ fontSize: 22, color: "var(--white)", marginBottom: 8 }}>全真模擬考</h2>
          <p style={{ fontSize: 13, color: "var(--silver)", lineHeight: 1.7, marginBottom: 28 }}>
            從 {pool.length} 題題庫中隨機抽取 50 題<br />
            每題 2 分，滿分 100 分，70 分及格<br />
            考試時間 60 分鐘，作答完畢可立即交卷
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12, marginBottom: 28 }}>
            {[
              { label: "題數", val: "50" },
              { label: "每題", val: "2分" },
              { label: "及格", val: "70分" },
              { label: "時間", val: "60min" },
            ].map(({ label, val }) => (
              <div key={label} style={{
                background: "rgba(42,127,255,0.05)", border: "1px solid var(--border)",
                borderRadius: 8, padding: "12px 8px",
              }}>
                <div className="mono" style={{ fontSize: 18, color: "var(--accent2)", marginBottom: 4 }}>{val}</div>
                <div style={{ fontSize: 11, color: "var(--silver)" }}>{label}</div>
              </div>
            ))}
          </div>
          <button className="btn-gold" onClick={startExam} style={{ fontSize: 15, padding: "12px 36px" }}>
            開始考試
          </button>
        </div>
      </div>
    );
  }

  // ── Result screen
  if (phase === "result") {
    const answered = examQuestions.filter(q => answers[q.id] !== undefined);
    const correct = examQuestions.filter(q => answers[q.id] === q.ans);
    const score = correct.length * 2;
    const pass = score >= 70;
    return (
      <div className="anim-fadeup" style={{ padding: "32px 24px", maxWidth: 680, margin: "0 auto" }}>
        <div className="card" style={{ padding: "32px 28px" }}>
          <div style={{ textAlign: "center", marginBottom: 28 }}>
            <div style={{ fontSize: 48, marginBottom: 10 }}>{pass ? "🏆" : "📚"}</div>
            <div className="mono" style={{ fontSize: 11, color: "var(--silver)", letterSpacing: 3, marginBottom: 6 }}>
              EXAM RESULT
            </div>
            <div style={{
              fontSize: 72, fontWeight: 700, fontFamily: "IBM Plex Mono",
              color: pass ? "var(--success)" : "var(--error)",
              lineHeight: 1, marginBottom: 4,
            }}>{score}</div>
            <div style={{ fontSize: 14, color: "var(--silver)", marginBottom: 4 }}>/ 100 分</div>
            <div style={{
              display: "inline-block", padding: "6px 20px", borderRadius: 99, fontSize: 14,
              background: pass ? "rgba(34,211,160,0.12)" : "rgba(240,90,90,0.1)",
              border: `1px solid ${pass ? "rgba(34,211,160,0.4)" : "rgba(240,90,90,0.35)"}`,
              color: pass ? "var(--success)" : "var(--error)", fontWeight: 700,
            }}>
              {pass ? "✓ 及格" : "✗ 未及格"}（70 分及格）
            </div>
          </div>

          {/* Stats */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 24 }}>
            {[
              { label: "答對", val: correct.length, color: "var(--success)" },
              { label: "答錯", val: answered.length - correct.length, color: "var(--error)" },
              { label: "未作答", val: examQuestions.length - answered.length, color: "var(--warn)" },
            ].map(({ label, val, color }) => (
              <div key={label} style={{
                background: "rgba(255,255,255,0.03)", border: "1px solid var(--border)",
                borderRadius: 8, padding: "14px", textAlign: "center",
              }}>
                <div className="mono" style={{ fontSize: 28, color, marginBottom: 4 }}>{val}</div>
                <div style={{ fontSize: 11, color: "var(--silver)" }}>{label}</div>
              </div>
            ))}
          </div>

          {/* Per-question review */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 13, color: "var(--silver)", marginBottom: 10 }}>答題明細</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {examQuestions.map((q, i) => {
                const userAns = answers[q.id];
                const correct2 = userAns === q.ans;
                const unanswered2 = userAns === undefined;
                return (
                  <div key={q.id} title={`Q${i + 1}: ${q.q.slice(0, 30)}...`} style={{
                    width: 32, height: 32, borderRadius: 6,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontFamily: "IBM Plex Mono", fontSize: 11,
                    background: unanswered2 ? "rgba(255,255,255,0.05)"
                      : correct2 ? "rgba(34,211,160,0.15)"
                        : "rgba(240,90,90,0.1)",
                    border: `1px solid ${unanswered2 ? "var(--border)"
                      : correct2 ? "rgba(34,211,160,0.4)"
                        : "rgba(240,90,90,0.3)"}`,
                    color: unanswered2 ? "var(--silver)" : correct2 ? "var(--success)" : "var(--error)",
                  }}>{i + 1}</div>
                );
              })}
            </div>
          </div>

          <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
            <button className="btn-ghost" onClick={startExam}>再考一次</button>
            <button className="btn-primary" onClick={onBack}>返回首頁</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Exam screen
  const q = examQuestions[currentIdx];
  const answeredCount = Object.keys(answers).length;

  return (
    <div style={{ maxWidth: 760, margin: "0 auto", padding: "0 0 40px" }}>

      {/* Exam top bar */}
      <div style={{
        position: "sticky", top: 56, zIndex: 40,
        background: "linear-gradient(180deg, var(--navy2) 0%, rgba(10,22,40,0.95) 100%)",
        borderBottom: "1px solid var(--border)",
        padding: "12px 24px",
        backdropFilter: "blur(12px)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div className="mono" style={{
            fontSize: 20, fontWeight: 600,
            color: timeLeft < 300 ? "var(--error)" : timeLeft < 600 ? "var(--warn)" : "var(--accent2)",
            minWidth: 70,
          }}>⏱ {formatTime(timeLeft)}</div>
          <div style={{ flex: 1 }}>
            <ProgressBar current={answeredCount} total={examQuestions.length} label="已作答" />
          </div>
          <button className="btn-gold" onClick={submitExam} style={{ fontSize: 12, padding: "7px 16px" }}>
            交卷
          </button>
        </div>
      </div>

      <div style={{ padding: "20px 24px" }}>
        {/* Question nav */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 20 }}>
          {examQuestions.map((eq, i) => (
            <button key={eq.id} onClick={() => setCurrentIdx(i)} style={{
              width: 30, height: 30, borderRadius: 6, border: "none", cursor: "pointer",
              fontFamily: "IBM Plex Mono", fontSize: 11,
              background: i === currentIdx ? "var(--accent)"
                : answers[eq.id] !== undefined ? "rgba(42,127,255,0.2)"
                  : "rgba(255,255,255,0.05)",
              color: i === currentIdx ? "#fff"
                : answers[eq.id] !== undefined ? "var(--accent2)"
                  : "var(--silver)",
              transition: "all 0.15s",
              boxShadow: i === currentIdx ? "0 0 8px var(--accent)" : "none",
            }}>{i + 1}</button>
          ))}
        </div>

        {/* Question */}
        {q && (
          <div key={q.id} className="card anim-fadein" style={{ padding: "28px 28px 24px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
              <span className="mono" style={{ fontSize: 13, color: "var(--accent2)", fontWeight: 600 }}>
                Q{currentIdx + 1}
              </span>
              <span className="chapter-badge">{CHAPTER_META[CHAPTERS.find(c => allQuestions[c]?.some(x => x.id === q.id)) || ""]?.code || "—"}</span>
              {q.isCalc && <span className="calc-badge">⚡ 計算題</span>}
            </div>
            <p style={{ fontSize: 15, fontWeight: 500, color: "var(--white)", lineHeight: 1.8, marginBottom: 22 }}>
              {q.q}
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {q.opts.map((opt, i) => (
                <button key={i}
                  className={`opt-btn ${answers[q.id] === i ? "opt-reveal" : ""}`}
                  onClick={() => handleAnswer(q.id, i)}>
                  {opt}
                </button>
              ))}
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 20, justifyContent: "space-between" }}>
              <button className="btn-ghost" onClick={() => setCurrentIdx(i => Math.max(0, i - 1))}
                style={{ fontSize: 12 }} disabled={currentIdx === 0}>← 上一題</button>
              <button className="btn-primary" onClick={() => setCurrentIdx(i => Math.min(examQuestions.length - 1, i + 1))}
                style={{ fontSize: 12 }} disabled={currentIdx === examQuestions.length - 1}>下一題 →</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── 頁面：錯題本 ─────────────────────────────────────────────────────────────
function WrongBookPage({ allQuestions, wrongSet, onWrongUpdate, onBack }) {
  const [reviewIdx, setReviewIdx] = useState(null);
  const [selected, setSelected] = useState(null);
  const [answered, setAnswered] = useState(false);
  const [showExplain, setShowExplain] = useState(false);

  // Collect all wrong questions
  const wrongQs = [];
  Object.entries(allQuestions).forEach(([ch, qs]) => {
    qs.forEach(q => {
      if (wrongSet.has(q.id)) wrongQs.push({ ...q, chapter: ch });
    });
  });

  if (wrongQs.length === 0) {
    return (
      <div className="anim-fadeup" style={{ padding: "32px 24px", maxWidth: 600, margin: "0 auto" }}>
        <button className="btn-ghost" onClick={onBack} style={{ marginBottom: 24, fontSize: 12 }}>← 返回</button>
        <div className="card" style={{ padding: 40, textAlign: "center" }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🎉</div>
          <h2 style={{ fontSize: 20, color: "var(--white)", marginBottom: 8 }}>錯題本是空的！</h2>
          <p style={{ fontSize: 13, color: "var(--silver)" }}>目前沒有答錯的題目，繼續保持！</p>
        </div>
      </div>
    );
  }

  // List view
  if (reviewIdx === null) {
    return (
      <div className="anim-fadeup" style={{ padding: "24px", maxWidth: 720, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
          <button className="btn-ghost" onClick={onBack} style={{ fontSize: 12 }}>← 返回</button>
          <h2 style={{ fontSize: 17, color: "var(--white)" }}>📕 錯題本</h2>
          <span style={{
            fontSize: 12, color: "var(--error)",
            background: "rgba(240,90,90,0.1)", border: "1px solid rgba(240,90,90,0.25)",
            padding: "3px 10px", borderRadius: 99,
          }}>{wrongQs.length} 題</span>
          <button className="btn-primary" onClick={() => { setReviewIdx(0); setSelected(null); setAnswered(false); setShowExplain(false); }}
            style={{ marginLeft: "auto", fontSize: 12 }}>
            開始複習 →
          </button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {wrongQs.map((q, i) => {
            const meta = CHAPTER_META[q.chapter];
            return (
              <button key={q.id} className="card card-hover" onClick={() => { setReviewIdx(i); setSelected(null); setAnswered(false); setShowExplain(false); }}
                style={{ border: "none", cursor: "pointer", padding: "16px 20px", textAlign: "left",
                  animation: `fadeUp 0.3s ease ${i * 0.04}s both` }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                  <div className="mono" style={{ fontSize: 12, color: "var(--error)", minWidth: 28, paddingTop: 2 }}>
                    #{i + 1}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
                      <span style={{ fontSize: 10, color: meta.color, fontFamily: "IBM Plex Mono" }}>{meta.icon} {meta.code}</span>
                      {q.isCalc && <span className="calc-badge" style={{ fontSize: 10 }}>計算題</span>}
                    </div>
                    <p style={{ fontSize: 13, color: "var(--dimwhite)", lineHeight: 1.6 }}>
                      {q.q.length > 60 ? q.q.slice(0, 60) + "…" : q.q}
                    </p>
                  </div>
                  <span style={{ fontSize: 18, color: "var(--error)", opacity: 0.6 }}>→</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // Review mode
  const q = wrongQs[reviewIdx];
  const meta = CHAPTER_META[q.chapter];

  const handleSelect = (optIdx) => {
    if (answered) return;
    setSelected(optIdx);
    setAnswered(true);
    setShowExplain(true);
    if (optIdx === q.ans) {
      const newWrong = new Set(wrongSet);
      newWrong.delete(q.id);
      onWrongUpdate(newWrong);
    }
  };

  return (
    <div style={{ padding: "24px", maxWidth: 720, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <button className="btn-ghost" onClick={() => setReviewIdx(null)} style={{ fontSize: 12 }}>← 錯題列表</button>
        <div style={{ flex: 1 }}>
          <ProgressBar current={reviewIdx} total={wrongQs.length} label="錯題複習" />
        </div>
      </div>

      <div key={q.id} className="card anim-fadeup" style={{ padding: "28px 28px 24px" }}>
        <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
          <span style={{ fontFamily: "IBM Plex Mono", fontSize: 11, color: meta.color }}>{meta.icon} {meta.code}</span>
          <span className="mono" style={{ fontSize: 11, color: "var(--silver)" }}>· {q.chapter}</span>
          {q.isCalc && <span className="calc-badge">⚡ 計算題</span>}
          <span className="tag-wrong">✗ 曾答錯</span>
        </div>
        <p style={{ fontSize: 16, fontWeight: 500, color: "var(--white)", lineHeight: 1.75, marginBottom: 22 }}>{q.q}</p>

        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
          {q.opts.map((opt, i) => {
            let cls = "opt-btn";
            if (answered) {
              if (i === q.ans) cls += " opt-correct";
              else if (i === selected && i !== q.ans) cls += " opt-wrong";
            }
            return (
              <button key={i} className={cls} onClick={() => handleSelect(i)} disabled={answered}>{opt}</button>
            );
          })}
        </div>

        {answered && (
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <span style={{
                padding: "2px 10px", borderRadius: 4, fontSize: 12,
                background: selected === q.ans ? "rgba(34,211,160,0.15)" : "rgba(240,90,90,0.12)",
                color: selected === q.ans ? "var(--success)" : "var(--error)",
                border: `1px solid ${selected === q.ans ? "rgba(34,211,160,0.35)" : "rgba(240,90,90,0.3)"}`,
              }}>
                {selected === q.ans ? "✓ 答對了！已從錯題本移除" : "✗ 再次答錯"}
              </span>
            </div>
            <div className="explain-box">{q.explain}</div>
          </div>
        )}

        <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
          <button className="btn-ghost" onClick={() => { setReviewIdx(r => Math.max(0, r - 1)); setSelected(null); setAnswered(false); setShowExplain(false); }}
            disabled={reviewIdx === 0} style={{ fontSize: 12 }}>← 上一題</button>
          <button className="btn-primary" onClick={() => { setReviewIdx(r => Math.min(wrongQs.length - 1, r + 1)); setSelected(null); setAnswered(false); setShowExplain(false); }}
            disabled={reviewIdx === wrongQs.length - 1} style={{ fontSize: 12, marginLeft: "auto" }}>
            {reviewIdx < wrongQs.length - 1 ? "下一題 →" : "完成複習"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── 主 App ───────────────────────────────────────────────────────────────────
export default function App() {
  const [page, setPage] = useState("home"); // home | chapter | exam | wrong
  const [activeChapter, setActiveChapter] = useState(null);
  const [wrongSet, setWrongSet] = useState(new Set());

  // chapterStats: { ch: { done, total, correct } }
  const [chapterStats, setChapterStats] = useState(() => {
    const s = {};
    CHAPTERS.forEach(ch => {
      s[ch] = { done: 0, total: 75, correct: 0 };
    });
    return s;
  });

  const handleWrongUpdate = useCallback((newSet) => {
    setWrongSet(new Set(newSet));
  }, []);

  const goChapter = (ch) => {
    setActiveChapter(ch);
    setPage("chapter");
  };

  const goHome = () => { setPage("home"); setActiveChapter(null); };

  return (
    <>
      <style>{CSS}</style>
      <div className="scanlines grid-bg" style={{ minHeight: "100vh" }}>
        <Header onHome={goHome} wrongCount={wrongSet.size} />

        <main>
          {page === "home" && (
            <HomePage
              onChapter={goChapter}
              onExam={() => setPage("exam")}
              onWrongBook={() => setPage("wrong")}
              wrongCount={wrongSet.size}
              chapterStats={chapterStats}
            />
          )}
          {page === "chapter" && activeChapter && (
            <QuizPage
              chapter={activeChapter}
              questions={ALL_QUESTIONS[activeChapter] || []}
              wrongSet={wrongSet}
              onWrongUpdate={handleWrongUpdate}
              onBack={goHome}
            />
          )}
          {page === "exam" && (
            <ExamPage
              allQuestions={ALL_QUESTIONS}
              wrongSet={wrongSet}
              onWrongUpdate={handleWrongUpdate}
              onBack={goHome}
            />
          )}
          {page === "wrong" && (
            <WrongBookPage
              allQuestions={ALL_QUESTIONS}
              wrongSet={wrongSet}
              onWrongUpdate={handleWrongUpdate}
              onBack={goHome}
            />
          )}
        </main>
      </div>
    </>
  );
}